import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../config/supabase';
import html2pdf from 'html2pdf.js';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import MyGAPResidueSection, {
  RESIDUE_HEADING, residueSlotDef, residueSeasonId, listResidueDocs,
  residueSignedUrl, buildResidueHeadingPdf, pdfSafeText,
} from './MyGAPResidueSection';
import { useAuth } from '../contexts/AuthContext';
import {
  NOTES_CACHE_LS_PREFIX, NOTES_PENDING_LS_PREFIX, NOTES_LEGACY_LS,
  NOTES_MIGRATED_LS_PREFIX, NOTES_OLD_UNSCOPED_LS,
} from '../utils/mygapNotesStorage';

// Append supplementary documents (images + PDFs) to the generated myGAP 1-1 PDF
// so everything downloads as ONE file. Images get a labelled A4 page; uploaded
// PDFs are merged page-for-page. A single bad attachment is skipped, not fatal.
// Each attachment is { url | getUrl() | bytes, name, mime, label }. getUrl lets
// a private file's signed URL be created right before it is fetched. Pass a
// `problems` array to collect what was skipped (kind 'skip') or may render
// badly (kind 'warn'), so the caller can show it instead of failing silently.
// Pass an `included` array to get back the attachments that made it in.
async function mergeAttachmentsIntoPdf(baseBytes, attachments, problems = null, included = null) {
  const merged = await PDFDocument.load(baseBytes);
  const font = await merged.embedFont(StandardFonts.Helvetica);
  const A4W = 595.28, A4H = 841.89;
  const report = (a, kind, reason) => {
    if (problems) problems.push({ kind, label: a?.label || '', name: a?.name || '', reason });
  };
  for (const a of attachments) {
    try {
      let bytes = a.bytes || null;
      if (!bytes) {
        const url = a.url || (a.getUrl ? await a.getUrl() : '');
        const res = await fetch(url);
        if (!res.ok) {
          console.warn('attachment fetch failed', a?.name, res.status);
          report(a, 'skip', `muat turun gagal (HTTP ${res.status})`);
          continue;
        }
        bytes = await res.arrayBuffer();
      }
      const isPdf = (a.mime === 'application/pdf') || /\.pdf$/i.test(a.name || '');
      if (isPdf) {
        const src = await PDFDocument.load(bytes, { ignoreEncryption: true });
        if (src.isEncrypted) report(a, 'warn', 'PDF ini dilindungi (encrypted); halamannya mungkin kosong dalam fail gabungan');
        const pages = await merged.copyPages(src, src.getPageIndices());
        pages.forEach(p => merged.addPage(p));
      } else {
        const isPng = (a.mime === 'image/png') || /\.png$/i.test(a.name || '');
        let img;
        try { img = isPng ? await merged.embedPng(bytes) : await merged.embedJpg(bytes); }
        catch { try { img = await merged.embedPng(bytes); } catch { img = await merged.embedJpg(bytes); } }
        const page = merged.addPage([A4W, A4H]);
        // Standard fonts are WinAnsi-only: a name with other characters used to
        // throw here and drop the whole attachment.
        page.drawText(pdfSafeText(a.label || 'Lampiran').slice(0, 110), { x: 40, y: A4H - 40, size: 11, font, color: rgb(0.15, 0.15, 0.15) });
        if (a.name) page.drawText(pdfSafeText(a.name).slice(0, 90), { x: 40, y: A4H - 56, size: 8, font, color: rgb(0.45, 0.45, 0.45) });
        const maxW = A4W - 80, maxH = A4H - 110;
        const scale = Math.min(maxW / img.width, maxH / img.height, 1);
        const w = img.width * scale, h = img.height * scale;
        page.drawImage(img, { x: (A4W - w) / 2, y: (A4H - 80 - h), width: w, height: h });
      }
      if (included) included.push(a);
    } catch (e) {
      console.warn('attachment merge skipped', a?.name, e);
      report(a, 'skip', e?.message || String(e));
    }
  }
  return await merged.save();
}

// "Dilangkau" / "Amaran" lines for an alert after an export.
function formatMergeProblems(problems) {
  return (problems || []).map(p => {
    const what = [p.label, p.name].filter(Boolean).join(': ') || 'Lampiran';
    return `${p.kind === 'warn' ? 'Amaran' : 'Tidak disertakan'} - ${what} (${p.reason})`;
  }).join('\n');
}

function downloadPdfBytes(bytes, filename) {
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

// myGAP Logo SVG (official green agricultural logo)
const MyGAPLogo = ({ size = 60 }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="50" cy="50" r="48" fill="#f0fdf4" stroke="#16a34a" strokeWidth="2"/>
    <path d="M50 15 C40 25, 30 35, 35 50 C38 58, 45 62, 50 65 C55 62, 62 58, 65 50 C70 35, 60 25, 50 15Z" fill="#16a34a" opacity="0.8"/>
    <path d="M35 30 C30 40, 25 50, 30 60 C33 65, 38 68, 42 70" stroke="#15803d" strokeWidth="2" fill="none"/>
    <path d="M65 30 C70 40, 75 50, 70 60 C67 65, 62 68, 58 70" stroke="#15803d" strokeWidth="2" fill="none"/>
    <path d="M50 55 L50 80" stroke="#15803d" strokeWidth="2"/>
    <path d="M42 75 C45 72, 48 70, 50 70 C52 70, 55 72, 58 75" stroke="#15803d" strokeWidth="1.5" fill="none"/>
    <text x="50" y="95" textAnchor="middle" fontSize="8" fill="#15803d" fontWeight="bold">myGAP</text>
  </svg>
);

// Section Header component
const SectionHeader = ({ number, titleMalay }) => (
  <div style={{ 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'space-between',
    padding: '12px 16px',
    background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
    borderBottom: '2px solid #16a34a',
    borderRadius: '8px 8px 0 0'
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <MyGAPLogo size={40} />
      <div>
        <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#15803d' }}>myGAP {number}</h4>
        <h6 style={{ margin: 0, fontSize: '11px', fontWeight: 600, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.02em' }}>{titleMalay}</h6>
      </div>
    </div>
    <MyGAPLogo size={36} />
  </div>
);

// ── Farm Record notes (4-1 … 4-8 typed cells) ────────────────────────────────
// One flat map { `${farmId}_${seasonId}_${section}_${field}`: value }. Source of
// truth is public.mygap_farm_record_notes (one row per farm × key, WI-22a);
// localStorage is an offline cache, scoped to the signed-in user and cleared on
// sign-out (utils/mygapNotesStorage.js). A key that is present wins over the
// cell's computed default — including '' (the user cleared the cell on purpose).
// The default is used only when the key is absent.
const NOTES_TABLE = 'mygap_farm_record_notes';
const NOTES_SAVE_DEBOUNCE_MS = 600;
// A pending edit older than this was not sent right away (offline, failed save,
// earlier visit): it is checked against the DB before it may overwrite anything.
const NOTES_STALE_MS = 15000;
// Same limit as the table's value_len CHECK; a longer value could never be saved.
const NOTE_MAX_LEN = 20000;

const readJsonLS = (key) => {
  try {
    const v = JSON.parse(localStorage.getItem(key) || '{}');
    return v && typeof v === 'object' && !Array.isArray(v) ? v : {};
  } catch { return {}; }
};
const writeJsonLS = (key, obj) => {
  try { localStorage.setItem(key, JSON.stringify(obj)); } catch { /* quota / private mode */ }
};
const noteKeyFarmId = (key) => {
  const m = /^(\d+)_/.exec(String(key || ''));
  return m ? Number(m[1]) : null;
};
const isFarmNotesMigrated = (fid) => {
  try { return !!localStorage.getItem(NOTES_MIGRATED_LS_PREFIX + fid); } catch { return false; }
};
// Legacy notes were read as `notes[k] || default`, so a stored '' meant "show the
// default". Drop those so the default still shows; keep only real text. Only for
// farms whose one-time upload has not run yet: after it, the DB is the truth and
// a legacy value the DB no longer has (deleted / reset) must not come back.
const readLegacyNotes = () => {
  const out = {};
  const migrated = {};
  Object.entries(readJsonLS(NOTES_LEGACY_LS)).forEach(([k, v]) => {
    if (v == null || String(v) === '') return;
    const fid = noteKeyFarmId(k);
    if (fid == null) return;
    if (migrated[fid] === undefined) migrated[fid] = isFarmNotesMigrated(fid);
    if (!migrated[fid]) out[k] = String(v);
  });
  return out;
};
// Pending edits: { key: { v: value, t: edit time (ms), b: DB updated_at the edit was based on, or null } }.
const readPendingNotes = (uid) => {
  const out = {};
  Object.entries(readJsonLS(NOTES_PENDING_LS_PREFIX + uid)).forEach(([k, e]) => {
    if (e && typeof e === 'object' && e.v != null && noteKeyFarmId(k) != null) {
      out[k] = { v: String(e.v), t: Number(e.t) || 0, b: e.b == null ? null : String(e.b) };
    }
  });
  return out;
};
const readLocalNotes = (uid) => {
  // The first WI-22a build (never deployed) kept these unscoped; drop them.
  try { NOTES_OLD_UNSCOPED_LS.forEach(k => localStorage.removeItem(k)); } catch { /* ignore */ }
  const pendingVals = {};
  Object.entries(readPendingNotes(uid)).forEach(([k, e]) => { pendingVals[k] = e.v; });
  return { ...readLegacyNotes(), ...readJsonLS(NOTES_CACHE_LS_PREFIX + uid), ...pendingVals };
};
const hasNote = (notes, key) => !!notes && Object.prototype.hasOwnProperty.call(notes, key) && notes[key] != null;
const noteOrDefault = (notes, key, def) => (hasNote(notes, key) ? String(notes[key]) : (def == null ? '' : String(def)));
// Errors the server will give again on a retry (RLS denial, CHECK / length /
// FK violations): the row is dropped and reported instead of blocking the queue.
// Network failures come back from supabase-js with an empty code and are retried.
const isPermanentNoteError = (error) => {
  const code = String(error?.code || '');
  return code === '42501' || /^2[23][0-9A-Z]{3}$/.test(code);
};

// True when the signed-in user may only look at this farm's notes (no edits).
const NotesReadOnlyCtx = React.createContext(false);

// Editable cell component. onNoteChange(key, value) stores the note (state +
// localStorage + debounced DB upsert — see setNoteValues in MyGAPReport).
const EditableCell = ({ value, placeholder, farmId, seasonId, section, field, notes, onNoteChange }) => {
  const readOnly = React.useContext(NotesReadOnlyCtx);
  const noteKey = `${farmId}_${seasonId}_${section}_${field}`;
  const effective = noteOrDefault(notes, noteKey, value);
  const [val, setVal] = useState(effective);

  useEffect(() => {
    setVal(effective);
  }, [effective]);

  const handleChange = (e) => {
    if (readOnly) return;
    const newVal = e.target.value;
    setVal(newVal);
    if (onNoteChange) onNoteChange(noteKey, newVal);
  };

  return (
    <input
      type="text"
      value={val}
      onChange={handleChange}
      placeholder={readOnly ? '' : placeholder}
      readOnly={readOnly}
      maxLength={NOTE_MAX_LEN}
      title={readOnly ? 'Baca sahaja: anda tiada akses untuk mengubah rekod ladang ini' : undefined}
      style={{
        width: '100%',
        border: readOnly ? '1px solid #e5e7eb' : '1px dashed #d1d5db',
        borderRadius: '4px',
        padding: '4px 6px',
        fontSize: '11px',
        color: '#374151',
        background: readOnly ? '#f9fafb' : (val ? '#fff' : '#fefce8'),
        outline: 'none',
        minWidth: '80px'
      }}
    />
  );
};

// Table styles
const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '11px',
};

const thStyle = {
  background: '#f9fafb',
  padding: '8px 6px',
  textAlign: 'left',
  fontWeight: 600,
  color: '#374151',
  borderBottom: '2px solid #e5e7eb',
  fontSize: '10px',
  textTransform: 'uppercase',
  letterSpacing: '0.02em',
};

const tdStyle = {
  padding: '6px',
  borderBottom: '1px solid #f3f4f6',
  color: '#4b5563',
  verticalAlign: 'top',
  fontSize: '11px',
};

const sectionCardStyle = {
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  marginBottom: '20px',
  overflow: 'hidden',
  background: 'white',
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  pageBreakInside: 'avoid',
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatDateShort = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return `${d.getDate()}-${d.getMonth() + 1}-${d.getFullYear()}`;
};

// Escape free text (SOP descriptions, material names, typed notes) before it is
// injected into the PDF HTML string, so "&", "<" etc. can't break the markup.
const escHtml = (v) => String(v ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

// ── Supplementary documents (Lampiran 1) ─────────────────────────────────────
// Five parts per the myGAP 1-1 checklist: (1) IC/SSM, (2) land status (≤10),
// (3) harvest-within-6-months checkbox, (4) farm layout drawing, (5) EIA letter.
// Files go to Supabase Storage (images bucket, mygap-attachments/ prefix); only
// the metadata { path, name, mime, url } is kept in the draft (ATT section). On
// PDF export these tick the Lampiran 1 checklist and are appended to the PDF.
const SUPP_PARTS = [
  { key: 'ic_ssm',      no: 1, title: 'Kad Pengenalan / Sijil Pendaftaran Syarikat (SSM)', subtitle: 'IC / SSM certificate' },
  { key: 'land_status', no: 2, title: 'Bukti status tanah yang diusahakan',                subtitle: 'Proof of land status (boleh sehingga 10 dokumen)' },
  { key: 'farm_layout', no: 4, title: 'Pelan Blok Tanaman / Lukisan susun atur ladang',     subtitle: 'Farm layout drawing' },
  { key: 'eia',         no: 5, title: 'Surat Kelulusan EIA (Jabatan Alam Sekitar)',          subtitle: 'EIA approval letter from Department of Environment (jika berkenaan)' },
];
const SUPP_MAX = 10;                       // up to 10 documents per part
const SUPP_MAX_BYTES = 45 * 1024 * 1024;   // 45 MB per file (Supabase bucket ceiling is ~50 MB)

const SupplementaryDocs = ({ farm, att, setF11, onUploadingChange }) => {
  const [busy, setBusy] = React.useState('');
  const [err, setErr] = React.useState('');
  const list = (key) => (Array.isArray(att?.[key]) ? att[key] : []);

  const handleUpload = async (key, fileList) => {
    setErr('');
    if (!farm?.id) { setErr('Sila pilih ladang dahulu.'); return; }
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    const existing = list(key);
    if (existing.length + incoming.length > SUPP_MAX) {
      setErr(`Maksimum ${SUPP_MAX} dokumen bagi bahagian ini.`);
      return;
    }
    setBusy(key);
    onUploadingChange?.(true);   // pause the focus-refetch so it can't clobber this upload
    const added = [];
    try {
      for (const file of incoming) {
        const okType = /^(image\/(jpeg|jpg|png)|application\/pdf)$/i.test(file.type || '') || /\.(jpe?g|png|pdf)$/i.test(file.name || '');
        if (!okType) { setErr(`"${file.name}": jpeg, png atau pdf sahaja.`); continue; }
        if (file.size > SUPP_MAX_BYTES) { setErr(`"${file.name}" terlalu besar (maks 45 MB).`); continue; }
        try {
          const safe = (file.name || 'file').replace(/[^a-zA-Z0-9._-]/g, '_');
          const path = `mygap-attachments/${farm.id}/${key}/${Date.now()}-${Math.round(Math.random() * 1e6)}-${safe}`;
          const { error: upErr } = await supabase.storage.from('images').upload(path, file, { cacheControl: '3600', upsert: false });
          if (upErr) { setErr(`Muat naik gagal: ${upErr.message || upErr}`); continue; }
          const { data: urlData } = supabase.storage.from('images').getPublicUrl(path);
          added.push({ path, name: file.name, mime: file.type || '', url: urlData?.publicUrl || '' });
        } catch (e) { setErr(`Muat naik gagal: ${e.message || e}`); }
      }
      if (added.length) setF11('ATT', key, [...list(key), ...added]);
    } finally {
      setBusy('');
      onUploadingChange?.(false);
    }
  };

  const handleRemove = async (key, idx) => {
    const arr = list(key);
    const item = arr[idx];
    setF11('ATT', key, arr.filter((_, i) => i !== idx));
    if (item?.path) { try { await supabase.storage.from('images').remove([item.path]); } catch {} }
  };

  const partBox = { marginBottom: 10, padding: 10, border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff' };
  const fileChip = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontSize: 12, padding: '3px 8px', background: '#f3f4f6', borderRadius: 4, marginTop: 4 };

  const renderUploadPart = (part) => {
    const files = list(part.key);
    return (
      <div key={part.key} style={partBox}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{part.no}. {part.title}</div>
        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6 }}>{part.subtitle} — {files.length}/{SUPP_MAX} fail (jpeg/png/pdf, maks 45 MB)</div>
        <input
          type="file"
          accept="image/jpeg,image/png,application/pdf"
          multiple
          disabled={busy === part.key || files.length >= SUPP_MAX}
          onChange={(e) => { handleUpload(part.key, e.target.files); e.target.value = ''; }}
          style={{ fontSize: 12 }}
        />
        {busy === part.key && <span style={{ marginLeft: 8, fontSize: 12, color: '#6b7280' }}>Memuat naik…</span>}
        {files.map((f, i) => (
          <div key={f.path || i} style={fileChip}>
            <a href={f.url} target="_blank" rel="noreferrer" style={{ color: '#2563eb', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {/\.pdf$/i.test(f.name) || f.mime === 'application/pdf' ? '📄' : '🖼️'} {f.name}
            </a>
            <button type="button" onClick={() => handleRemove(part.key, i)}
              style={{ padding: '1px 6px', fontSize: 11, background: '#fee2e2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 4, cursor: 'pointer', flexShrink: 0 }}>
              Buang
            </button>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontSize: 14, fontWeight: 'bold', marginBottom: 4, color: '#111827' }}>
        Dokumen Sokongan (Lampiran 1)
      </div>
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
        Lampirkan dokumen sokongan. Yang dimuat naik akan ditandakan [✓] pada senarai semak Lampiran 1 dan disertakan selepas borang myGAP 1-1 dalam satu penjanaan PDF.
      </div>
      {err && <div style={{ fontSize: 12, color: '#b91c1c', background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 4, padding: '6px 8px', marginBottom: 8 }}>{err}</div>}
      {renderUploadPart(SUPP_PARTS[0])}
      {renderUploadPart(SUPP_PARTS[1])}
      <div style={partBox}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: '#111827', cursor: 'pointer' }}>
          <input type="checkbox" checked={!!att?.harvest_checked} onChange={(e) => setF11('ATT', 'harvest_checked', e.target.checked)} />
          3. Ladang telah / akan mengeluarkan hasil dalam masa 6 bulan.
        </label>
        <div style={{ fontSize: 11, color: '#6b7280', marginLeft: 24 }}>Farm has harvested / will harvest within 6 months — tandakan jika benar.</div>
      </div>
      {renderUploadPart(SUPP_PARTS[2])}
      {renderUploadPart(SUPP_PARTS[3])}
    </div>
  );
};

// ── Form 1-1 (Borang Permohonan) editable preview ────────────────────────────
// Renders Sections A, B, C, D, E as fillable inputs. The PDF Export reads
// the same merged form_draft + form11Edits state, so anything typed here
// flows straight into the generated PDF. Sections F (officer) and G
// (certification office) remain "to be filled by hand" per the spec.
// Field components live at MODULE scope (NOT inside Form11Editor) so their
// component identity is stable across re-renders. Defining them inside the
// parent meant every keystroke created a brand-new component type, so React
// unmounted/remounted the <input> and the field lost focus after each character.
// They read the current values + setter from context, so call sites stay terse.
const Form11FieldCtx = React.createContext({ forms: {}, setF11: () => {}, inputStyle: {} });

const F11TextField = ({ section, field, placeholder = '' }) => {
  const { forms, setF11, inputStyle } = React.useContext(Form11FieldCtx);
  return (
    <input
      type="text"
      value={(forms[section] && forms[section][field]) || ''}
      onChange={(e) => setF11(section, field, e.target.value)}
      placeholder={placeholder}
      style={inputStyle}
    />
  );
};
const F11DateField = ({ section, field }) => {
  const { forms, setF11, inputStyle } = React.useContext(Form11FieldCtx);
  return (
    <input
      type="date"
      value={(forms[section] && forms[section][field]) || ''}
      onChange={(e) => setF11(section, field, e.target.value)}
      style={inputStyle}
    />
  );
};
const F11SelectField = ({ section, field, options }) => {
  const { forms, setF11, inputStyle } = React.useContext(Form11FieldCtx);
  return (
    <select
      value={(forms[section] && forms[section][field]) || ''}
      onChange={(e) => setF11(section, field, e.target.value)}
      style={{ ...inputStyle, cursor: 'pointer' }}
    >
      <option value="">— pilih / select —</option>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
};
const F11CheckField = ({ section, field, label }) => {
  const { forms, setF11 } = React.useContext(Form11FieldCtx);
  const v = forms[section] && forms[section][field];
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
      <input
        type="checkbox"
        checked={v === 'true' || v === true || v === '1'}
        onChange={(e) => setF11(section, field, e.target.checked ? 'true' : 'false')}
      />
      {label}
    </label>
  );
};

const Form11Editor = ({
  farm, selectedCycle, cycles, employees, onCycleChange,
  form11A, form11B, form11C, form11D, form11E, form11ATT,
  setF11, saveStatus, onRefresh, onSuppUploadingChange,
  tableStyle, thStyle, tdStyle,
}) => {
  // "Quick Fill" picker state — which employee + which cycle the user has
  // chosen to copy into the form. Persisted to the same `mygap_form11_draft`
  // row (column: picks) so the chips restore across devices.
  const [pickedApplicantId, setPickedApplicantId] = React.useState('');
  const [pickedCropCycleId, setPickedCropCycleId] = React.useState('');

  React.useEffect(() => {
    if (!farm?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase
          .from('mygap_form11_draft')
          .select('picks')
          .eq('farm_id', farm.id)
          .maybeSingle();
        if (!cancelled && data?.picks) {
          setPickedApplicantId(data.picks.applicantId || '');
          setPickedCropCycleId(data.picks.cropCycleId || '');
          return;
        }
      } catch {}
      // Fall back to localStorage (legacy drafts from before DB sync).
      try {
        const saved = JSON.parse(localStorage.getItem('mygap_form11_picks_' + farm.id) || '{}');
        if (cancelled) return;
        setPickedApplicantId(saved.applicantId || '');
        setPickedCropCycleId(saved.cropCycleId || '');
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [farm?.id]);

  const persistPicks = (next) => {
    try {
      if (farm?.id) localStorage.setItem('mygap_form11_picks_' + farm.id, JSON.stringify(next));
    } catch {}
    if (!farm?.id) return;
    supabase
      .from('mygap_form11_draft')
      .upsert({ farm_id: farm.id, picks: next }, { onConflict: 'farm_id' })
      .then(() => {}, () => {});
  };

  // Pre-fill defaults the first time the user opens Form 1-1 for this farm.
  // We only fill in fields the user hasn't already overridden.
  React.useEffect(() => {
    if (!farm?.id) return;
    if (!form11B.Farm_name && farm?.name) setF11('B', 'Farm_name', String(farm.name).trim());
    if (!form11B.Area && (farm?.size != null)) {
      // farm.size is formatted as "3 hectares" upstream — strip the unit for the
      // numeric form field so users can edit the number cleanly.
      const m = String(farm.size).match(/[\d.]+/);
      if (m) setF11('B', 'Area', m[0]);
    }
    if (!form11A.Address_line_1 && farm?.address) setF11('A', 'Address_line_1', farm.address);
    if (!form11A.Address_line_2 && farm?.address2) setF11('A', 'Address_line_2', farm.address2);
    if (!form11A.Postcode && farm?.postcode) setF11('A', 'Postcode', farm.postcode);
    if (!form11A.City && farm?.city) setF11('A', 'City', farm.city);
    if (!form11A.State && farm?.state) setF11('A', 'State', farm.state);
    if (!form11C.Crop && selectedCycle?.produceName) setF11('C', 'Crop', selectedCycle.produceName);
    if (!form11E.Date) setF11('E', 'Date', new Date().toISOString().split('T')[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [farm?.id]);

  // Quick-fill: copy a selected employee's details into Section A.
  const applyApplicant = (userId) => {
    setPickedApplicantId(userId);
    persistPicks({ applicantId: userId, cropCycleId: pickedCropCycleId });
    if (!userId) return;
    const emp = (employees || []).find(e => String(e.user_id) === String(userId));
    if (!emp) return;
    const name = emp.full_name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
    setF11('A', 'Name', name);
    setF11('A', 'Contact_person', name);
    const ic = emp.identification_no || emp.ic_number || '';
    if (ic) setF11('A', 'IC_number', ic);
    if (emp.email) setF11('A', 'Email', emp.email);
    if (emp.phone || emp.phone_number || emp.contact_no) {
      setF11('A', 'Phone', emp.phone || emp.phone_number || emp.contact_no);
    }
  };

  // Quick-fill: copy a selected cycle's crop into Section C and (optionally)
  // switch the main page's cycle selector to match, so the rest of the report
  // stays consistent.
  const applyCropCycle = (cycleId) => {
    setPickedCropCycleId(cycleId);
    persistPicks({ applicantId: pickedApplicantId, cropCycleId: cycleId });
    if (!cycleId) return;
    const cyc = (cycles || []).find(c => String(c.id) === String(cycleId));
    if (!cyc) return;
    if (cyc.produceName) setF11('C', 'Crop', cyc.produceName);
    const lower = (cyc.produceName || '').toLowerCase();
    let commodity = '';
    if (/lime|fruit|buah|durian|mango|mangga|rambutan|pisang|banana|nanas|pineapple|jambu|tembikai|melon|betik|papaya/.test(lower)) commodity = 'Buah-buahan';
    else if (/sayur|vegetable|tomato|chilli|cili|cabbage|kangkung|bayam|sawi|terung|brinjal|timun|cucumber|lada/.test(lower)) commodity = 'Sayur-sayuran';
    else if (/padi|paddy|rice/.test(lower)) commodity = 'Padi';
    else commodity = 'Lain-lain';
    setF11('C', 'Commodity', commodity);
    if (typeof onCycleChange === 'function') onCycleChange(cycleId);
  };

  const inputStyle = {
    width: '100%',
    border: 'none',
    background: 'transparent',
    fontSize: '13px',
    padding: '4px 2px',
    outline: 'none',
    fontFamily: 'inherit',
  };
  const inputCellStyle = { ...tdStyle, padding: '4px 6px' };
  const labelCellStyle = { ...tdStyle, fontWeight: 'bold', background: '#fafafa' };

  // Field inputs are stable module-scope components (defined at top of file),
  // consumed via Form11FieldCtx — aliased here so the existing call sites stay
  // unchanged. Aliasing to a stable function keeps a stable component identity,
  // so inputs update in place instead of remounting (and keep focus while typing).
  const TextField = F11TextField;
  const DateField = F11DateField;
  const SelectField = F11SelectField;
  const CheckField = F11CheckField;

  const handleSignatureUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setF11('E', 'Signature_of_applicant', reader.result);
    reader.readAsDataURL(file);
  };

  const handleSealUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setF11('E', 'Company_seal', reader.result);
    reader.readAsDataURL(file);
  };

  const quickSelectStyle = {
    width: '100%',
    padding: '6px 8px',
    border: '1px solid #d1d5db',
    borderRadius: 4,
    fontSize: 13,
    background: '#fff',
    cursor: 'pointer',
  };
  const quickLabelStyle = { display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.3 };
  const employeeOptions = (employees || []).map(e => ({
    id: e.user_id,
    label: `${e.full_name || `${e.first_name || ''} ${e.last_name || ''}`.trim() || e.email || 'Unknown'}${e.role_name ? ' • ' + e.role_name : ''}`,
  }));

  return (
    <Form11FieldCtx.Provider value={{ forms: { A: form11A, B: form11B, C: form11C, D: form11D, E: form11E }, setF11, inputStyle }}>
    <div style={{ padding: '12px', overflowX: 'auto' }}>
      {/* Quick Fill panel — pull data from existing records to autofill the form */}
      <div style={{ padding: 12, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 'bold', marginBottom: 8, color: '#1e3a8a' }}>
          Quick Fill — Pull from existing records
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
          <div>
            <label style={quickLabelStyle}>Pemohon (Applicant) — Section A</label>
            <select
              value={pickedApplicantId}
              onChange={(e) => applyApplicant(e.target.value)}
              style={quickSelectStyle}
              disabled={!employeeOptions.length}
            >
              <option value="">{employeeOptions.length ? '— pilih employee/owner —' : '— no employees registered —'}</option>
              {employeeOptions.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
            <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>
              Switches name, IC, email, phone in Section A.
            </div>
          </div>
          <div>
            <label style={quickLabelStyle}>Maklumat Ladang (Farm) — Section B</label>
            <div style={{ ...quickSelectStyle, cursor: 'default', background: '#f3f4f6', color: '#111827' }}>
              {farm?.name || 'No farm selected'}
            </div>
            <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>
              Auto from currently selected farm.
            </div>
          </div>
          <div>
            <label style={quickLabelStyle}>Jenis Tanaman (Crop) — Section C</label>
            <select
              value={pickedCropCycleId || selectedCycle?.id || ''}
              onChange={(e) => applyCropCycle(e.target.value)}
              style={quickSelectStyle}
              disabled={!(cycles && cycles.length)}
            >
              <option value="">{(cycles && cycles.length) ? '— pilih cycle —' : '— no cycles —'}</option>
              {(cycles || []).map(c => (
                <option key={c.id} value={c.id}>
                  {c.produceName}{c.startDate ? ' • ' + new Date(c.startDate).toLocaleDateString() : ''}
                </option>
              ))}
            </select>
            <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>
              Switches crop & commodity in Section C.
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12, color: '#6b7280' }}>
          Fill Sections A–E. Saved automatically to this browser. Click <b>Export PDF</b> to generate the application form.
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            type="button"
            onClick={() => onRefresh && onRefresh()}
            style={{ padding: '4px 10px', fontSize: 12, background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer', whiteSpace: 'nowrap' }}
            title="Re-pull the latest saved data (e.g. a signature signed on the mobile app) without reloading the page"
          >↻ Refresh</button>
          <div style={{ fontSize: 12, minWidth: 140, textAlign: 'right',
            color: saveStatus === 'error' ? '#dc2626' : saveStatus === 'saving' ? '#6b7280' : '#16a34a' }}>
            {saveStatus === 'saving' ? 'Saving…'
              : saveStatus === 'saved' ? '✓ Saved to cloud'
              : saveStatus === 'error' ? '⚠ Save failed (kept locally)'
              : ''}
          </div>
        </div>
      </div>

      {/* A) Applicant Information */}
      <table style={tableStyle}>
        <thead>
          <tr><th style={{ ...thStyle, textAlign: 'center', background: '#e8e8e8' }} colSpan={4}>A) MAKLUMAT PEMOHON (Applicant Information)</th></tr>
        </thead>
        <tbody>
          <tr><td style={{ ...labelCellStyle, width: '25%' }}>Nama Pemohon</td><td style={inputCellStyle} colSpan={3}><TextField section="A" field="Name" placeholder="Full name" /></td></tr>
          <tr><td style={labelCellStyle}>No. KP / Pendaftaran Syarikat</td><td style={inputCellStyle} colSpan={3}><TextField section="A" field="IC_number" placeholder="e.g. 901234-12-5678" /></td></tr>
          <tr><td style={labelCellStyle}>Nama Wakil (Contact person)</td><td style={inputCellStyle} colSpan={3}><TextField section="A" field="Contact_person" /></td></tr>
          <tr><td style={labelCellStyle}>Alamat (line 1)</td><td style={inputCellStyle} colSpan={3}><TextField section="A" field="Address_line_1" /></td></tr>
          <tr><td style={labelCellStyle}>Alamat (line 2)</td><td style={inputCellStyle} colSpan={3}><TextField section="A" field="Address_line_2" /></td></tr>
          <tr><td style={labelCellStyle}>Poskod</td><td style={inputCellStyle}><TextField section="A" field="Postcode" /></td><td style={labelCellStyle}>Bandar</td><td style={inputCellStyle}><TextField section="A" field="City" /></td></tr>
          <tr><td style={labelCellStyle}>Negeri</td><td style={inputCellStyle} colSpan={3}><TextField section="A" field="State" /></td></tr>
          <tr><td style={labelCellStyle}>No. Telefon</td><td style={inputCellStyle}><TextField section="A" field="Phone" /></td><td style={labelCellStyle}>No. Faks</td><td style={inputCellStyle}><TextField section="A" field="Fax" /></td></tr>
          <tr><td style={labelCellStyle}>Alamat e-mail</td><td style={inputCellStyle} colSpan={3}><TextField section="A" field="Email" /></td></tr>
        </tbody>
      </table>

      {/* B) Farm Information */}
      <table style={{ ...tableStyle, marginTop: '12px' }}>
        <thead>
          <tr><th style={{ ...thStyle, textAlign: 'center', background: '#e8e8e8' }} colSpan={4}>B) MAKLUMAT LADANG (Farm Information)</th></tr>
        </thead>
        <tbody>
          <tr><td style={{ ...labelCellStyle, width: '25%' }}>Nama Ladang</td><td style={inputCellStyle} colSpan={3}><TextField section="B" field="Farm_name" /></td></tr>
          <tr><td style={labelCellStyle}>Nombor Lot</td><td style={inputCellStyle}><TextField section="B" field="Lot_number" /></td><td style={labelCellStyle}>Blok</td><td style={inputCellStyle}><TextField section="B" field="Block" /></td></tr>
          <tr><td style={labelCellStyle}>Kampung</td><td style={inputCellStyle}><TextField section="B" field="Village" /></td><td style={labelCellStyle}>Mukim</td><td style={inputCellStyle}><TextField section="B" field="Sub_district" /></td></tr>
          <tr><td style={labelCellStyle}>Daerah / Jajahan</td><td style={inputCellStyle}><TextField section="B" field="District" /></td><td style={labelCellStyle}>Negeri</td><td style={inputCellStyle}><TextField section="B" field="State" /></td></tr>
          <tr><td style={labelCellStyle}>Keluasan Fizikal (hektar)</td><td style={inputCellStyle} colSpan={3}><TextField section="B" field="Area" /></td></tr>
          <tr><td style={labelCellStyle}>Tarikh Mula Beroperasi</td><td style={inputCellStyle} colSpan={3}><DateField section="B" field="Date_of_farms_come_into_operation" /></td></tr>
          <tr><td style={labelCellStyle}>Jenis Ladang</td><td style={inputCellStyle} colSpan={3}>
            <SelectField section="B" field="Type_of_farm" options={[
              { value: 'Individu', label: 'Individu (Individual / Smallholder)' },
              { value: 'Syarikat', label: 'Syarikat (Company)' },
              { value: 'Projek', label: 'Projek / Program di bawah Jabatan Pertanian' },
              { value: 'Agensi', label: 'Projek di bawah Agensi Kementerian Pertanian' },
              { value: 'Lain-lain', label: 'Lain-lain' },
            ]} />
            {/* Sub-fields for options 3, 4, 5 — only shown for the relevant type so
                the specify text lands in its own field (not Q11's Contract_farming_text). */}
            {form11B.Type_of_farm === 'Projek' && (
              <div style={{ marginTop: 6, paddingLeft: 12, borderLeft: '2px solid #e5e7eb' }}>
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>Projek / Program di bawah Jabatan Pertanian — pilih:</div>
                <SelectField section="B" field="Type_of_farm_projek_sub" options={[
                  { value: 'TKPM', label: 'Taman Kekal Pengeluaran Makanan (TKPM)' },
                  { value: 'Stesyen', label: 'Stesyen / Pusat Pertanian' },
                  { value: 'Lain-lain', label: 'Lain-lain, nyatakan' },
                ]} />
                {form11B.Type_of_farm_projek_sub === 'Lain-lain' && (
                  <TextField section="B" field="Type_of_farm_projek_text" placeholder="Nyatakan projek lain di bawah Jabatan Pertanian" />
                )}
              </div>
            )}
            {form11B.Type_of_farm === 'Agensi' && (
              <div style={{ marginTop: 6, paddingLeft: 12, borderLeft: '2px solid #e5e7eb' }}>
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>Sila nyatakan projek / agensi:</div>
                <TextField section="B" field="Type_of_farm_agensi_text" placeholder="e.g. Projek di bawah Agensi ..." />
              </div>
            )}
            {form11B.Type_of_farm === 'Lain-lain' && (
              <div style={{ marginTop: 6, paddingLeft: 12, borderLeft: '2px solid #e5e7eb' }}>
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>Lain-lain, sila nyatakan:</div>
                <TextField section="B" field="Type_of_farm_other_text" placeholder="Nyatakan jenis ladang" />
              </div>
            )}
          </td></tr>
          <tr><td style={labelCellStyle}>Ladang Kontrak?</td><td style={inputCellStyle}>
            <SelectField section="B" field="Contract_farming" options={[
              { value: 'Tidak', label: 'Tidak (No)' },
              { value: 'Ya', label: 'Ya (Yes)' },
            ]} />
          </td><td style={labelCellStyle}>Jika Ya, nyatakan</td><td style={inputCellStyle}><TextField section="B" field="Contract_farming_text" /></td></tr>
          <tr><td style={labelCellStyle}>Sistem Ladang</td><td style={inputCellStyle} colSpan={3}>
            <SelectField section="B" field="Farmingsystem" options={[
              { value: 'Terbuka', label: 'Terbuka (Open)' },
              { value: 'Separa Tertutup', label: 'Separa Tertutup (Partially Closed)' },
              { value: 'Bertutup Penuh', label: 'Bertutup Penuh (Fully Closed)' },
            ]} />
          </td></tr>
        </tbody>
      </table>

      {/* C) Types of Crop */}
      <table style={{ ...tableStyle, marginTop: '12px' }}>
        <thead>
          <tr><th style={{ ...thStyle, textAlign: 'center', background: '#e8e8e8' }} colSpan={4}>C) JENIS TANAMAN (Types of Crop)</th></tr>
        </thead>
        <tbody>
          <tr><td style={{ ...labelCellStyle, width: '25%' }}>Komoditi</td><td style={inputCellStyle} colSpan={3}>
            <SelectField section="C" field="Commodity" options={[
              { value: 'Buah-buahan', label: 'Buah-buahan (Fruits)' },
              { value: 'Sayur-sayuran', label: 'Sayur-sayuran (Vegetables)' },
              { value: 'Padi', label: 'Padi (Paddy)' },
              { value: 'Lain-lain', label: 'Lain-lain (Others)' },
            ]} />
          </td></tr>
          <tr><td style={labelCellStyle}>Jenis Tanaman (Crop)</td><td style={inputCellStyle} colSpan={3}><TextField section="C" field="Crop" placeholder={selectedCycle?.produceName || 'e.g. Key Lime'} /></td></tr>
          <tr><td style={labelCellStyle}>Keluasan Bertanam (ha)</td><td style={inputCellStyle} colSpan={3}><TextField section="C" field="Planted_area" placeholder="e.g. 1" /></td></tr>
        </tbody>
      </table>

      {/* D) Marketing */}
      <table style={{ ...tableStyle, marginTop: '12px' }}>
        <thead>
          <tr><th style={{ ...thStyle, textAlign: 'center', background: '#e8e8e8' }} colSpan={4}>D) PEMASARAN (Marketing) — pilih satu atau lebih</th></tr>
        </thead>
        <tbody>
          <tr><td style={{ ...inputCellStyle, width: '33%' }}><CheckField section="D" field="Direct_sales" label="Jualan Terus (Direct sales)" /></td>
              <td style={inputCellStyle} colSpan={3}><CheckField section="D" field="Export" label="Eksport (Export)" /></td></tr>
          <tr><td style={labelCellStyle}>Jika eksport, negara</td><td style={inputCellStyle} colSpan={3}><TextField section="D" field="Export_country" placeholder="e.g. Singapore" /></td></tr>
          <tr><td style={inputCellStyle}><CheckField section="D" field="Others" label="Lain-lain (Others)" /></td>
              <td style={inputCellStyle} colSpan={3}><TextField section="D" field="Others_text" placeholder="Specify other marketing channel" /></td></tr>
        </tbody>
      </table>

      {/* E) Declaration */}
      <table style={{ ...tableStyle, marginTop: '12px' }}>
        <thead>
          <tr><th style={{ ...thStyle, textAlign: 'center', background: '#e8e8e8' }} colSpan={4}>E) PENGESAHAN (Declaration)</th></tr>
        </thead>
        <tbody>
          <tr>
            <td colSpan={4} style={{ ...inputCellStyle, fontSize: 12, color: '#374151', fontStyle: 'italic', background: '#f9fafb' }}>
              Saya mengesahkan bahawa segala butiran yang dinyatakan di atas adalah benar dan akan menurut segala syarat dalam keperluan pematuhan Pensijilan myGAP.
            </td>
          </tr>
          <tr>
            <td style={{ ...labelCellStyle, width: '25%' }}>Tandatangan Pemohon</td>
            <td style={inputCellStyle} colSpan={2}>
              {form11E.Signature_of_applicant
                ? <img src={form11E.Signature_of_applicant} alt="Signature" style={{ maxHeight: 60, maxWidth: 150, objectFit: 'contain' }} />
                : <span style={{ color: '#9ca3af', fontSize: 12 }}>No signature uploaded</span>}
            </td>
            <td style={inputCellStyle}>
              <input type="file" accept="image/*" onChange={handleSignatureUpload} style={{ fontSize: 11 }} />
              {form11E.Signature_of_applicant && (
                <button
                  onClick={() => setF11('E', 'Signature_of_applicant', '')}
                  style={{ marginLeft: 4, padding: '2px 6px', fontSize: 11, background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer' }}
                >Clear</button>
              )}
            </td>
          </tr>
          <tr>
            <td style={{ ...labelCellStyle, width: '25%' }}>Cop Syarikat (Company seal)</td>
            <td style={inputCellStyle} colSpan={2}>
              {form11E.Company_seal
                ? <img src={form11E.Company_seal} alt="Company seal" style={{ maxHeight: 70, maxWidth: 160, objectFit: 'contain' }} />
                : <span style={{ color: '#9ca3af', fontSize: 12 }}>No seal uploaded</span>}
            </td>
            <td style={inputCellStyle}>
              <input type="file" accept="image/*" onChange={handleSealUpload} style={{ fontSize: 11 }} />
              {form11E.Company_seal && (
                <button
                  onClick={() => setF11('E', 'Company_seal', '')}
                  style={{ marginLeft: 4, padding: '2px 6px', fontSize: 11, background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer' }}
                >Clear</button>
              )}
            </td>
          </tr>
          <tr><td style={labelCellStyle}>Tarikh</td><td style={inputCellStyle} colSpan={3}><DateField section="E" field="Date" /></td></tr>
        </tbody>
      </table>

      {/* Supplementary documents (Lampiran 1) — uploads + harvest checkbox */}
      <SupplementaryDocs farm={farm} att={form11ATT} setF11={setF11} onUploadingChange={onSuppUploadingChange} />

      <div style={{ marginTop: 10, padding: 10, background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 6, fontSize: 12, color: '#92400e' }}>
        <strong>Note:</strong> Sections F (officer) and G (certification secretariat) are intentionally left blank on the PDF — they're filled by hand by the Department of Agriculture officer after submission.
      </div>
    </div>
    </Form11FieldCtx.Provider>
  );
};

const MyGAPReport = ({ farm, cycles, selectedCycleId, onCycleChange }) => {
  const [loading, setLoading] = useState(true);
  const [sopActivities, setSopActivities] = useState([]);
  const [inventoryData, setInventoryData] = useState([]);
  const [yieldData, setYieldData] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [trainingData, setTrainingData] = useState([]);
  const [formDraftData, setFormDraftData] = useState([]);
  const [financialView, setFinancialView] = useState([]);   // financial_transaction_view (rich joined)
  const [farmDiary, setFarmDiary] = useState([]);           // farm_diary entries for context
  // material id → { lrmp, activeIngredient, typeId } from the `material` table
  // (the SOP view carries neither misc_number/LRMP nor content/active ingredient).
  const [materialInfo, setMaterialInfo] = useState({});
  // inventory_transaction id → sop_record_activity id, so SOP rows already shown
  // through an inventory usage record aren't listed twice in 4-3 / 4-5.
  const [invActivityLinks, setInvActivityLinks] = useState({});
  // Farm Record notes (see NOTES_* above). Starts from this user's localStorage
  // cache; the current farm's rows from mygap_farm_record_notes are merged over it.
  const { user: authUser } = useAuth();
  const notesUid = authUser?.id || 'anon';
  const notesUidRef = useRef(notesUid);
  const [notes, setNotesState] = useState(() => readLocalNotes(notesUid));
  const notesRef = useRef(notes);
  const notesUnsyncedRef = useRef(readPendingNotes(notesUid));   // key → { v, t, b } not yet confirmed saved
  const notesTimerRef = useRef(null);
  const notesTimerFarmRef = useRef(null);          // farm id the debounce timer will save
  const notesFlushingRef = useRef(new Set());      // farm ids with a save in flight
  const notesFlushAgainRef = useRef(new Set());    // farm ids edited while their save was in flight
  const notesInFlightRef = useRef(new Set());      // keys being sent right now
  const notesDbVerRef = useRef({});                // key → DB updated_at last seen (base of the next edit)
  const notesLoadTokenRef = useRef(0);
  const notesLastLoadRef = useRef(0);
  const notesEditSeqRef = useRef(0);               // bumps on every edit…
  const notesKeySeqRef = useRef({});               // …and records it per key, so a slow load can't undo a newer edit
  const notesFarmIdRef = useRef(null);
  const [notesSaveStatus, setNotesSaveStatus] = useState('idle');   // 'idle' | 'saving' | 'saved' | 'error'
  const [notesSource, setNotesSource] = useState('pending');        // 'pending' | 'db' | 'local' (DB unreadable / no access)
  // What the signed-in user may do on this farm's myGAP data (notes + residue files):
  // 'unknown' (checking, or the check failed: behave as before) | 'write' | 'read' | 'none'.
  const [farmAccess, setFarmAccess] = useState('unknown');
  const farmAccessRef = useRef('unknown');
  const [notesConflicts, setNotesConflicts] = useState(0);         // old edits replaced by a newer DB value
  const [notesRejected, setNotesRejected] = useState([]);          // [{ key, reason }] refused by the server
  // `${farmId}:${cycleId}` the Farm Record source data (yield rows etc.) was loaded for.
  const dataScopeRef = useRef('');
  const [exporting, setExporting] = useState(false);
  const [exportMode, setExportMode] = useState('farm-record');
  const reportRef = useRef(null);

  // Form 1-1 (Borang Permohonan) edits. Persisted to Supabase table
  // `mygap_form11_draft` (one row per farm) so the form is shared across
  // devices. localStorage is kept as a synchronous fallback so typing feels
  // instant even if the network is slow / offline. Sections A-E are filled
  // by the applicant; F and G are filled later by the officer / certification
  // office, so we deliberately leave those off the editable preview.
  const [form11Edits, setForm11Edits] = useState({ A: {}, B: {}, C: {}, D: {}, E: {}, ATT: {} });
  const [form11SaveStatus, setForm11SaveStatus] = useState('idle');  // 'idle' | 'saving' | 'saved' | 'error'
  // Refs used to coalesce rapid keystrokes into a single debounced upsert.
  const form11SaveTimerRef = useRef(null);
  const form11LatestRef = useRef({ A: {}, B: {}, C: {}, D: {}, E: {}, ATT: {} });
  // Monotonic token so a slower in-flight draft load can't clobber a newer one
  // (e.g. farm switched, or a focus-refresh races the mount load).
  const form11LoadTokenRef = useRef(0);
  // True while a supplementary-doc upload is in flight — pauses the focus refetch
  // so it can't replace local ATT state mid-upload.
  const suppUploadingRef = useRef(false);

  // Load the Form 1-1 local-edit draft (mygap_form11_draft, with a localStorage
  // fallback) for the current farm. Prefer Supabase (cross-device); fall back to
  // localStorage so any pre-DB drafts the user filled in still appear and then
  // get migrated up. Extracted so it can be re-run on demand (manual Refresh /
  // window focus), not only on mount.
  const loadForm11Edits = async () => {
    if (!farm?.id) return;
    const token = ++form11LoadTokenRef.current;
    let next = { A: {}, B: {}, C: {}, D: {}, E: {}, ATT: {} };
    try {
      const { data, error } = await supabase
        .from('mygap_form11_draft')
        .select('data')
        .eq('farm_id', farm.id)
        .maybeSingle();
      if (!error && data?.data && typeof data.data === 'object') {
        next = {
          A: data.data.A || {}, B: data.data.B || {}, C: data.data.C || {},
          D: data.data.D || {}, E: data.data.E || {}, ATT: data.data.ATT || {},
        };
      }
    } catch {}
    // If the DB row was empty, try migrating an existing localStorage draft.
    const anyDB = ['A','B','C','D','E','ATT'].some(k => next[k] && Object.keys(next[k]).length);
    if (!anyDB) {
      try {
        const saved = JSON.parse(localStorage.getItem('mygap_form11_' + farm.id) || '{}');
        if (saved && Object.keys(saved).length) {
          next = { A: saved.A || {}, B: saved.B || {}, C: saved.C || {}, D: saved.D || {}, E: saved.E || {}, ATT: saved.ATT || {} };
          // Push the migrated localStorage draft up to the DB so this device
          // is no longer authoritative.
          try {
            await supabase.from('mygap_form11_draft').upsert(
              { farm_id: farm.id, data: next },
              { onConflict: 'farm_id' }
            );
          } catch {}
        }
      } catch {}
    }
    if (token !== form11LoadTokenRef.current) return;  // a newer load superseded us
    form11LatestRef.current = next;
    setForm11Edits(next);
  };

  // Re-pull only the Kapitani mobile-app draft (form_draft) — cheap, and never
  // clobbers in-progress local edits (those live in form11Edits, not here).
  const loadFormDraftData = async () => {
    if (!farm?.id) return;
    try {
      const { data, error } = await supabase
        .from('form_draft')
        .select('*')
        .eq('farm', farm.id);
      if (!error) setFormDraftData(data || []);
    } catch {}
  };

  // Manual / focus-triggered refresh so a signature (or any field) saved on the
  // Kapitani mobile app — or on this form from another device — appears without a
  // full page reload. Skip the local-edit re-pull while a save is pending so we
  // never overwrite text the user is still typing.
  const refreshForm11Data = () => {
    loadFormDraftData();
    if (!form11SaveTimerRef.current && !suppUploadingRef.current) loadForm11Edits();
  };

  // Initial load when the farm changes.
  useEffect(() => {
    loadForm11Edits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [farm?.id]);

  // When the user returns to this tab (e.g. after signing on the mobile app),
  // re-pull the latest drafts so the signature/fields sync without a reload.
  useEffect(() => {
    if (!farm?.id) return;
    const onFocus = () => {
      if (document.visibilityState === 'hidden') return;
      refreshForm11Data();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [farm?.id]);

  // Helper: update one field. Writes to localStorage immediately (so the
  // value survives a reload even before the DB roundtrip), then schedules a
  // debounced upsert to Supabase.
  const setF11 = (section, field, value) => {
    setForm11Edits(prev => {
      const next = { ...prev, [section]: { ...prev[section], [field]: value } };
      form11LatestRef.current = next;
      try {
        if (farm?.id) localStorage.setItem('mygap_form11_' + farm.id, JSON.stringify(next));
      } catch {}
      setForm11SaveStatus('saving');
      if (form11SaveTimerRef.current) clearTimeout(form11SaveTimerRef.current);
      form11SaveTimerRef.current = setTimeout(async () => {
        form11SaveTimerRef.current = null;
        if (!farm?.id) return;
        try {
          const { error } = await supabase
            .from('mygap_form11_draft')
            .upsert({ farm_id: farm.id, data: form11LatestRef.current }, { onConflict: 'farm_id' });
          setForm11SaveStatus(error ? 'error' : 'saved');
        } catch {
          setForm11SaveStatus('error');
        }
      }, 600);
      return next;
    });
  };

  const selectedCycle = cycles?.find(c => c.id === selectedCycleId);
  // Residue uploads are scoped to the selected cycle (season_id NULL for "All
  // Cycles"). Loose id match: the Form 1-1 quick-fill passes the id as a string.
  const residueCycle = cycles?.find(c => String(c.id) === String(selectedCycleId));
  const residueCycleLabel = residueSeasonId(selectedCycleId)
    ? (residueCycle
      ? `${residueCycle.produceName || 'Kitaran'} (mula: ${formatDate(residueCycle.startDate)})`
      : `Kitaran #${selectedCycleId}`)
    : 'Tiada kitaran dipilih (semua kitaran)';
  const farmLocation = farm?.address || `${farm?.name || 'Farm'} (Lat: ${farm?.latitude || '-'}, Lng: ${farm?.longitude || '-'})`;

  // ── Farm Record notes: DB persistence (WI-22a) ─────────────────────────────
  // Same pattern as Form 1-1: state + localStorage immediately, then a debounced
  // batched upsert. Pending edits are kept (per signed-in user) until the DB
  // confirms them, so an edit made offline (or before a failed save) is re-sent
  // later instead of being lost, but only after checking that nobody saved a
  // newer value for that cell meanwhile.
  const persistPending = () => writeJsonLS(NOTES_PENDING_LS_PREFIX + notesUidRef.current, notesUnsyncedRef.current);
  const commitNotes = (next) => {
    notesRef.current = next;
    setNotesState(next);
    writeJsonLS(NOTES_CACHE_LS_PREFIX + notesUidRef.current, next);
  };
  const farmHasPending = (fid) => Object.keys(notesUnsyncedRef.current).some(k => noteKeyFarmId(k) === fid);
  const isStalePending = (e, now = Date.now()) => !e || now - (Number(e.t) || 0) > NOTES_STALE_MS;
  const canSaveNotes = () => farmAccessRef.current === 'write' || farmAccessRef.current === 'unknown';

  const scheduleNotesFlush = (fid) => {
    if (notesTimerRef.current) clearTimeout(notesTimerRef.current);
    notesTimerFarmRef.current = fid;
    notesTimerRef.current = setTimeout(() => {
      notesTimerRef.current = null;
      notesTimerFarmRef.current = null;
      flushNotes(fid);
    }, NOTES_SAVE_DEBOUNCE_MS);
  };
  // Send a waiting debounce now: tab hidden / page closing / leaving the farm
  // (a hidden tab on a tablet may not run the timer until it is reopened).
  const flushNotesNow = () => {
    if (!notesTimerRef.current) return;
    clearTimeout(notesTimerRef.current);
    const fid = notesTimerFarmRef.current;
    notesTimerRef.current = null;
    notesTimerFarmRef.current = null;
    if (fid) flushNotes(fid);
  };

  // Upsert the pending edits of one farm (500 rows per request). Rows the server
  // refuses for good (RLS, CHECK, length) are split out by halving the batch and
  // dropped + reported, so one bad row can't block every later save. Network
  // errors keep everything pending for a retry.
  const flushNotes = async (farmIdToSave, { verified = false } = {}) => {
    const fid = Number(farmIdToSave) || null;
    if (!fid) return;
    if (notesFlushingRef.current.has(fid)) { notesFlushAgainRef.current.add(fid); return; }
    const isCurrent = () => notesFarmIdRef.current === fid;
    if (isCurrent() && !canSaveNotes()) return;
    const pending = notesUnsyncedRef.current;
    let keys = Object.keys(pending).filter(k => noteKeyFarmId(k) === fid);
    if (!verified && keys.some(k => isStalePending(pending[k]))) {
      // Edits that waited are checked against the DB first: loadFarmNotes does
      // that and then calls back here with `verified`. For a farm that is no
      // longer open, send only the fresh ones; the rest wait for its next visit.
      if (isCurrent()) { loadFarmNotes(fid); return; }
      keys = keys.filter(k => !isStalePending(pending[k]));
    }
    if (!keys.length) {
      if (isCurrent()) setNotesSaveStatus(s => (s === 'saving' ? 'saved' : s));
      return;
    }
    notesFlushingRef.current.add(fid);
    if (isCurrent()) setNotesSaveStatus('saving');
    const rows = keys.map(k => ({ farm_id: fid, note_key: k, value: pending[k].v }));
    keys.forEach(k => notesInFlightRef.current.add(k));
    let transient = false;
    const rejected = [];
    const confirm = (batch, data) => {
      const ver = {};
      (data || []).forEach(r => { if (r && r.note_key) ver[r.note_key] = r.updated_at; });
      batch.forEach(r => {
        const u = ver[r.note_key] || null;
        if (u) notesDbVerRef.current[r.note_key] = u;
        const cur = notesUnsyncedRef.current[r.note_key];
        // Confirmed, unless the cell changed again meanwhile: that newer value
        // stays pending, now based on the version just saved.
        if (cur && cur.v === r.value) delete notesUnsyncedRef.current[r.note_key];
        else if (cur && u) cur.b = u;
      });
    };
    const send = async (batch) => {
      const { data, error } = await supabase.from(NOTES_TABLE)
        .upsert(batch, { onConflict: 'farm_id,note_key' })
        .select('note_key, updated_at');
      if (!error) { confirm(batch, data); return; }
      if (!isPermanentNoteError(error)) {
        transient = true;
        console.warn('myGAP notes: save failed (will retry)', error.message || error);
        return;
      }
      if (batch.length === 1) {
        rejected.push({ key: batch[0].note_key, value: batch[0].value, reason: error.message || String(error.code || 'ditolak') });
        return;
      }
      const mid = Math.ceil(batch.length / 2);
      await send(batch.slice(0, mid));
      if (!transient) await send(batch.slice(mid));
    };
    try {
      for (let i = 0; i < rows.length && !transient; i += 500) await send(rows.slice(i, i + 500));
    } catch (e) {
      transient = true;
      console.warn('myGAP notes: save failed (will retry)', e);
    }
    keys.forEach(k => notesInFlightRef.current.delete(k));
    rejected.forEach(({ key, value, reason }) => {
      console.warn('myGAP notes: the server refused this note, it was not saved:', key, reason);
      const cur = notesUnsyncedRef.current[key];
      if (cur && cur.v === value) delete notesUnsyncedRef.current[key];
    });
    persistPending();
    notesFlushingRef.current.delete(fid);
    if (rejected.length && isCurrent()) {
      setNotesRejected(prev => [...prev.filter(p => !rejected.some(r => r.key === p.key)), ...rejected.map(({ key, reason }) => ({ key, reason }))]);
      // Put back what the DB really holds for the refused cells (flushes again at its end).
      loadFarmNotes(fid);
    }
    if (notesFlushAgainRef.current.delete(fid) && !transient) { flushNotes(fid, { verified }); return; }
    if (!isCurrent()) return;
    if (transient) { setNotesSaveStatus('error'); return; }
    if (farmHasPending(fid)) {
      // Something typed during this save is still waiting for its debounce.
      setNotesSaveStatus('saving');
      if (!notesTimerRef.current) scheduleNotesFlush(fid);
      return;
    }
    setNotesSaveStatus('saved');
  };

  // Store one or more notes: { fullKey: value }. '' is a real value (cleared cell).
  const setNoteValues = (updates) => {
    const entries = Object.entries(updates || {});
    if (!entries.length) return;
    const fid = noteKeyFarmId(entries[0][0]);
    const current = fid === notesFarmIdRef.current;
    if (current && farmAccessRef.current === 'read') return;   // view only
    const clean = {};
    entries.forEach(([k, v]) => { clean[k] = v == null ? '' : String(v).slice(0, NOTE_MAX_LEN); });
    commitNotes({ ...notesRef.current, ...clean });
    // No server access to this farm's notes: keep them in this browser only, as
    // before WI-22a (not queued, so no endless "Gagal simpan").
    if (current && farmAccessRef.current === 'none') return;
    const seq = ++notesEditSeqRef.current;
    const now = Date.now();
    Object.entries(clean).forEach(([k, v]) => {
      const prev = notesUnsyncedRef.current[k];
      // Base = the DB version this edit replaces (kept from an earlier unsaved edit of the same cell).
      notesUnsyncedRef.current[k] = { v, t: now, b: prev ? prev.b : (notesDbVerRef.current[k] || null) };
      notesKeySeqRef.current[k] = seq;
    });
    persistPending();
    if (current) setNotesSaveStatus('saving');
    scheduleNotesFlush(fid);
  };
  const setNoteValue = (key, value) => setNoteValues({ [key]: value });

  // Load every note of one farm from the DB and merge it over the local cache.
  // DB wins, except for edits not yet saved and for cells saved / edited while
  // this load ran (its SELECT may predate them). A pending edit that waited is
  // dropped when another device saved that cell later than the edit was made.
  // If the table can't be read, keep the localStorage notes, as before WI-22a.
  const loadFarmNotes = async (fid) => {
    if (!fid || farmAccessRef.current === 'none') return;
    const token = ++notesLoadTokenRef.current;
    const startSeq = notesEditSeqRef.current;
    const startedAt = Date.now();
    notesLastLoadRef.current = startedAt;
    const prefix = `${fid}_`;
    // Keys pending or being saved now: their save may commit after the SELECT
    // reads the row, so the value read can be older than what is on screen.
    const inFlightAtStart = new Set([...notesInFlightRef.current].filter(k => k.startsWith(prefix)));
    const busyAtStart = new Set([...inFlightAtStart, ...Object.keys(notesUnsyncedRef.current).filter(k => k.startsWith(prefix))]);
    const dbRows = {};
    let ok = true;
    try {
      const PAGE = 1000;
      for (let from = 0; ; from += PAGE) {
        const { data, error } = await supabase
          .from(NOTES_TABLE)
          .select('note_key, value, updated_at')
          .eq('farm_id', fid)
          .order('id', { ascending: true })
          .range(from, from + PAGE - 1);
        if (error) { ok = false; console.warn('myGAP notes: saved notes unavailable, using this browser only:', error.message || error); break; }
        (data || []).forEach(r => { if (r.value != null) dbRows[r.note_key] = { v: String(r.value), u: r.updated_at || '' }; });
        if (!data || data.length < PAGE) break;
      }
    } catch (e) {
      ok = false;
      console.warn('myGAP notes: saved notes unavailable, using this browser only:', e);
    }
    if (token !== notesLoadTokenRef.current || notesFarmIdRef.current !== fid) return;   // superseded
    if (farmAccessRef.current === 'none') return;   // the access check finished meanwhile
    if (!ok) {
      setNotesSource('local');
      if (farmHasPending(fid)) setNotesSaveStatus('error');
      return;
    }

    const unsynced = notesUnsyncedRef.current;
    const canWrite = canSaveNotes();
    // 1. Pending edits that waited (offline, failed save, earlier visit): if the
    //    cell was saved elsewhere after the version this edit was based on, and
    //    later than the edit was made, the DB value wins and the edit is dropped.
    let conflicts = 0;
    const replaced = new Set();
    Object.keys(unsynced).forEach(k => {
      if (!k.startsWith(prefix)) return;
      if (!canWrite) {                       // view only: can never be saved
        console.warn('myGAP notes: dropping an unsaved edit, this account can no longer edit the farm:', k);
        delete unsynced[k];
        replaced.add(k);
        return;
      }
      const e = unsynced[k];
      if (!isStalePending(e, startedAt) || inFlightAtStart.has(k) || notesInFlightRef.current.has(k)) return;
      const row = dbRows[k];
      if (!row) return;                                    // no row (or reset): this edit recreates it
      if (e.b != null && row.u === e.b) return;            // nobody saved it since this edit's base
      const dbTime = Date.parse(row.u);
      if (Number.isFinite(dbTime) && dbTime > e.t) {
        delete unsynced[k];
        replaced.add(k);
        conflicts++;
      } else {
        e.b = row.u;                                       // this edit is the newer one
      }
    });

    // 2. One-time upload (per farm, per browser) of notes this browser held for
    //    the farm that the DB doesn't have yet (pre-WI-22a localStorage).
    const local = notesRef.current;
    const migrated = isFarmNotesMigrated(fid);
    const toMigrate = (!canWrite || migrated) ? [] : Object.keys(local)
      .filter(k => k.startsWith(prefix) && hasNote(local, k) && !dbRows[k] && !Object.prototype.hasOwnProperty.call(unsynced, k))
      .map(k => ({ farm_id: fid, note_key: k, value: String(local[k]).slice(0, NOTE_MAX_LEN) }));

    // 3. Merge. Cells saved / edited while this load ran keep what is on screen.
    const keepLocal = (k) => !replaced.has(k) && (busyAtStart.has(k) || (notesKeySeqRef.current[k] || 0) > startSeq);
    const next = { ...local };
    // Only when the access check confirmed this user can read the farm: under
    // RLS a refused SELECT looks exactly like an empty one.
    const readConfirmed = farmAccessRef.current === 'write' || farmAccessRef.current === 'read';
    if (migrated && readConfirmed) {
      // After the one-time upload the DB is the whole truth for this farm: a key
      // it no longer has was deleted / reset elsewhere and must not linger here.
      Object.keys(next).forEach(k => {
        if (k.startsWith(prefix) && !dbRows[k] && !Object.prototype.hasOwnProperty.call(unsynced, k) && !keepLocal(k)) {
          delete next[k];
          delete notesDbVerRef.current[k];
        }
      });
    }
    Object.entries(dbRows).forEach(([k, row]) => {
      if (keepLocal(k)) return;
      next[k] = row.v;
      notesDbVerRef.current[k] = row.u;
    });
    Object.entries(unsynced).forEach(([k, e]) => { if (k.startsWith(prefix)) next[k] = e.v; });
    commitNotes(next);
    persistPending();
    setNotesSource('db');
    if (conflicts) {
      console.warn(`myGAP notes: ${conflicts} older unsaved edit(s) in this browser were replaced by newer values saved elsewhere`);
      setNotesConflicts(c => c + conflicts);
    }
    if (!canWrite) { setNotesSaveStatus('idle'); return; }

    if (!migrated) {
      let migOk = true;
      for (let i = 0; i < toMigrate.length; i += 500) {
        try {
          const { error } = await supabase
            .from(NOTES_TABLE)
            .upsert(toMigrate.slice(i, i + 500), { onConflict: 'farm_id,note_key', ignoreDuplicates: true });
          if (error) { migOk = false; console.warn('myGAP notes: one-time upload of this browser\'s notes failed (will retry):', error.message || error); break; }
        } catch (e) {
          migOk = false;
          console.warn('myGAP notes: one-time upload of this browser\'s notes failed (will retry):', e);
          break;
        }
      }
      if (migOk) { try { localStorage.setItem(NOTES_MIGRATED_LS_PREFIX + fid, new Date().toISOString()); } catch { /* ignore */ } }
    }
    // Send anything typed offline or left over from a failed save (now checked).
    flushNotes(fid, { verified: true });
  };

  // What may this user do with the farm's myGAP data? Same helpers as the RLS
  // policies. An empty result is not an error under RLS, so without this check
  // a user the policies refuse would see "Disimpan" over an empty form.
  const checkFarmAccess = async (fid) => {
    try {
      const [r, w] = await Promise.all([
        supabase.rpc('mygap_can_read_farm', { p_farm: fid }),
        supabase.rpc('dashboard_can_write_farm', { p_farm: fid }),
      ]);
      if (r.error) { console.warn('myGAP: access check failed:', r.error.message || r.error); return 'unknown'; }
      if (r.data !== true) return 'none';
      if (w.error) { console.warn('myGAP: access check failed:', w.error.message || w.error); return 'unknown'; }
      return w.data === true ? 'write' : 'read';
    } catch (e) {
      console.warn('myGAP: access check failed:', e);
      return 'unknown';
    }
  };

  // Check access and load when the farm (or signed-in user) changes; re-pull on
  // focus (another device may have edited); retry pending saves on focus /
  // reconnect; save at once when the tab is hidden, the page closes or the farm changes.
  useEffect(() => {
    if (notesUidRef.current !== notesUid) {
      // Another account in this tab: its own cache and pending edits only.
      notesUidRef.current = notesUid;
      notesUnsyncedRef.current = readPendingNotes(notesUid);
      notesDbVerRef.current = {};
      notesKeySeqRef.current = {};
      const fresh = readLocalNotes(notesUid);
      notesRef.current = fresh;
      setNotesState(fresh);
    }
    const fid = Number(farm?.id) || null;
    notesFarmIdRef.current = fid;
    farmAccessRef.current = 'unknown';
    setFarmAccess('unknown');
    setNotesSaveStatus('idle');
    setNotesSource('pending');
    setNotesConflicts(0);
    setNotesRejected([]);
    if (!fid) return undefined;
    let alive = true;
    (async () => {
      const access = await checkFarmAccess(fid);
      if (!alive || notesFarmIdRef.current !== fid) return;
      farmAccessRef.current = access;
      setFarmAccess(access);
      if (access === 'none') { setNotesSource('local'); return; }
      loadFarmNotes(fid);
    })();
    const onFocus = () => {
      if (document.visibilityState === 'hidden') { flushNotesNow(); return; }
      if (farmAccessRef.current === 'none') return;
      if (farmHasPending(fid)) { if (!notesTimerRef.current) flushNotes(fid); return; }
      if (Date.now() - notesLastLoadRef.current < 3000) return;
      loadFarmNotes(fid);
    };
    const onOnline = () => { if (farmHasPending(fid)) flushNotes(fid); };
    const onPageHide = () => flushNotesNow();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('online', onOnline);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      alive = false;
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('pagehide', onPageHide);
      flushNotesNow();   // don't wait for the debounce when leaving this farm / tab
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [farm?.id, notesUid]);

  // Load myGAP data when farm/cycle changes
  useEffect(() => {
    if (!farm?.id) return;
    loadMyGAPData();
  }, [farm?.id, selectedCycleId]);

  const loadMyGAPData = async () => {
    const scope = `${farm.id}:${selectedCycleId}`;
    dataScopeRef.current = '';
    setLoading(true);
    try {
      // 1. Load SOP activities
      let actQuery = supabase
        .from('sop_record_activity_view_include_season')
        .select('*')
        .eq('farm_id', farm.id)
        .order('day', { ascending: true });

      if (selectedCycleId) {
        actQuery = actQuery.eq('season_id', selectedCycleId);
      }

      const { data: actData, error: actErr } = await actQuery;
      if (!actErr) setSopActivities(actData || []);

      // 2. Load inventory transactions
      const { data: invData, error: invErr } = await supabase
        .rpc('get_inventory_transactions_for_farm', { farm_id_input: farm.id });
      if (!invErr) setInventoryData(invData || []);

      // 3. Load yield transactions
      const { data: yData, error: yErr } = await supabase
        .rpc('get_yield_transactions_for_farm', { farm_id_input: farm.id });
      if (!yErr) setYieldData(yData || []);

      // 4. Load employees
      const { data: empData, error: empErr } = await supabase
        .rpc('get_farm_employees', { farm_id_input: farm.id });
      if (!empErr) setEmployees(empData || []);

      // 5. Load training records
      const { data: trainData, error: trainErr } = await supabase
        .rpc('get_farm_training_records', { farm_id_input: farm.id });
      if (!trainErr) setTrainingData(trainData || []);

      // 6. Load Form 1-1 draft data (form_draft table)
      const { data: fdData, error: fdErr } = await supabase
        .from('form_draft')
        .select('*')
        .eq('farm', farm.id);
      if (!fdErr) setFormDraftData(fdData || []);

      // 7. Load financial_transaction_view (joins inventory, vendor, yield,
      // produce). Powers Form 4 purchase records + buyer names.
      const { data: ftvData, error: ftvErr } = await supabase
        .from('financial_transaction_view')
        .select('*')
        .eq('farm', farm.id)
        .order('date_transaction', { ascending: false })
        .limit(2000);
      if (!ftvErr) setFinancialView(ftvData || []);

      // 8. Load farm_diary entries (cycle-scoped if a cycle is selected).
      let diaryQuery = supabase
        .from('farm_diary')
        .select('*')
        .eq('farm_id', farm.id)
        .order('record_date', { ascending: false });
      if (selectedCycleId) diaryQuery = diaryQuery.eq('cycle_id', selectedCycleId);
      const { data: diaryData, error: diaryErr } = await diaryQuery;
      if (!diaryErr) setFarmDiary(diaryData || []);

      // 9. Material details the SOP view doesn't carry: LRMP registration no.
      // (material.misc_number), active ingredient (material.content) and the
      // material's own type. Covers the SOP activities' materials plus the
      // materials behind inventory transactions (for 4-5 inventory rows).
      // Fail soft — every section still renders without it.
      try {
        const matIds = new Set();
        (actErr ? [] : (actData || [])).forEach(a => { if (a.material_id != null) matIds.add(a.material_id); });
        (ftvErr ? [] : (ftvData || [])).forEach(f => { if (f.material_id != null && f.inventory_transaction_id != null) matIds.add(f.material_id); });
        const ids = [...matIds];
        const info = {};
        for (let i = 0; i < ids.length; i += 150) {
          const { data: mData, error: mErr } = await supabase
            .from('material')
            .select('id, misc_number, content, material_type')
            .in('id', ids.slice(i, i + 150));
          if (mErr) { console.warn('myGAP: material details unavailable:', mErr.message || mErr); continue; }
          (mData || []).forEach(m => {
            info[m.id] = {
              lrmp: String(m.misc_number ?? '').trim(),
              activeIngredient: String(m.content ?? '').trim(),
              typeId: m.material_type ?? null,
            };
          });
        }
        setMaterialInfo(info);
      } catch (mEx) {
        console.warn('myGAP: material details unavailable:', mEx);
        setMaterialInfo({});
      }

      // 10. Which inventory transactions were created from an SOP activity
      // (inventory_transaction.sop_record_activity). The inventory RPC doesn't
      // return this link. RLS may hide the table for non-members — then this
      // is simply empty and 4-3/4-5 fall back to material-name + date matching.
      try {
        const { data: linkData, error: linkErr } = await supabase
          .from('inventory_transaction')
          .select('id, sop_record_activity')
          .eq('farm', farm.id)
          .not('sop_record_activity', 'is', null);
        const links = {};
        if (!linkErr) (linkData || []).forEach(r => { links[r.id] = r.sop_record_activity; });
        setInvActivityLinks(links);
      } catch {
        setInvActivityLinks({});
      }

    } catch (err) {
      console.error('Error loading myGAP data:', err);
    } finally {
      dataScopeRef.current = scope;
      setLoading(false);
    }
  };

  // Filter yield data by selected cycle/season
  const cycleYields = selectedCycleId
    ? yieldData.filter(y => y.yield_season_id === selectedCycleId || y.yield_season_id == selectedCycleId)
    : yieldData;

  // Inventory transactions don't have season_id - show all for the farm
  const cycleInventory = inventoryData;

  // Categorize inventory by type - match on material_type_name OR material_name
  const fertilizerItems = cycleInventory.filter(i => {
    const typeName = (i.material_type_name || '').toLowerCase();
    const matName = (i.material_name || i.inventory_name || '').toLowerCase();
    return typeName.match(/baja|fertiliz|fertilis|nutrient|compost|npk/) ||
           matName.match(/baja|fertiliz|fertilis|nutrient|compost|npk|nitrophoska|urea|aviafic/);
  });

  const pesticideItems = cycleInventory.filter(i => {
    const typeName = (i.material_type_name || '').toLowerCase();
    const matName = (i.material_name || i.inventory_name || '').toLowerCase();
    return typeName.match(/racun|pesticide|insecticide|fungicide|herbicide|chemical/) ||
           matName.match(/racun|pesticide|insecticide|fungicide|herbicide|prevathon|invinsi|forum|previcur|antracol/);
  });

  const seedItems = cycleInventory.filter(i => {
    const typeName = (i.material_type_name || '').toLowerCase();
    const matName = (i.material_name || i.inventory_name || '').toLowerCase();
    return typeName.match(/benih|seed|plant|tanaman|bahan tanaman|sapling/) ||
           matName.match(/benih|seed|sapling|anak pokok/);
  });

  // ── SOP activity classification ────────────────────────────────────────────
  // process ids (table `process`): 1 Fertilizer, 2 Pesticide, 3 Media Treatment,
  // 7 Seeding, 10 Foliar, 14 Substrate Treatment, 19 Transplanting.
  // material types: 1 Pesticide, 2 Fertilizer, 4 Foliar, 6 Media Treatment, 7 Seed.
  const procId = (a) => Number(a?.process_id ?? a?.process);
  const isCompletedAct = (a) => Number(a?.status_id ?? a?.status) === 4;   // 4 = Completed
  const hasMaterial = (a) => a?.material_id != null;
  const hasDosage = (a) => Number(a?.dosage_amount) > 0;
  // The view's material_type_id is the BRAND's type; material_type2_id is the
  // material's own type. Prefer the material's own, then the brand's.
  const matTypeId = (a) => {
    const own = a?.material_type2_id ?? materialInfo[a?.material_id]?.typeId;
    if (own != null) return Number(own);
    return a?.material_type_id != null ? Number(a.material_type_id) : null;
  };

  // Categorize SOP activities by process. The regex is the original matcher
  // (kept as-is so existing 4-1 rows and their typed-note keys don't move);
  // Media Treatment (3) and Substrate Treatment (14) are now matched by id —
  // the regex looked for "medium", which never matches "Media Treatment".
  const legacySoilMatch = (a) => {
    const proc = (a.process_name || '').toLowerCase();
    const act = (a.activity_name || '').toLowerCase();
    return proc.match(/tanah|soil|substrat|medium|lahan|land/) ||
           act.match(/tanah|soil|substrat|medium|lahan|land|loosening/);
  };
  const legacySoilActivities = sopActivities.filter(legacySoilMatch);
  // Rows matched only by id must be Completed, like 4-3: a planned (Pending)
  // Media/Substrate Treatment isn't a record of work done. Legacy matches are
  // left exactly as before.
  const soilActivities = sopActivities.filter(a => legacySoilMatch(a) ||
    ((procId(a) === 3 || procId(a) === 14) && isCompletedAct(a)));
  // 4-1 note key: rows the old matcher already listed keep their index-based key
  // (so notes typed before this change stay on the same row); newly-listed rows
  // get a stable key tied to the activity id.
  const soilNoteKey = (act, field) => {
    const i = legacySoilActivities.indexOf(act);
    return i >= 0 ? `${field}_${i}` : `sop_${act.id}_${field}`;
  };

  const pestActivities = sopActivities.filter(a => {
    const proc = (a.process_name || '').toLowerCase();
    const act = (a.activity_name || '').toLowerCase();
    return proc.match(/perosak|pest|serangga|kawalan|disease|penyakit|spray|sembur/) ||
           act.match(/perosak|pest|serangga|kawalan|disease|penyakit|spray|sembur/);
  });

  const postHarvestActivities = sopActivities.filter(a => {
    const proc = (a.process_name || '').toLowerCase();
    const act = (a.activity_name || '').toLowerCase();
    return proc.match(/pasca|post.*harvest|lepas.*tuai|pembungkusan|packaging|grading/) ||
           act.match(/pasca|post.*harvest|lepas.*tuai|pembungkusan|packaging|grading/);
  });

  // Harvests (yield_in = true) and Sales (yield_in = false)
  const harvests = cycleYields.filter(y => y.yield_in === true);
  const sales = cycleYields.filter(y => y.yield_in === false);

  // Combine harvest + sale for 4-6
  const productionSales = cycleYields;

  // Operators (employees names joined)
  const operatorNames = employees.map(e => e.full_name || `${e.first_name || ''} ${e.last_name || ''}`.trim()).filter(Boolean).join(', ');

  // ── Form 4 auto-fill helpers ────────────────────────────────────────────────
  // The dashboard collects most of what Form 4 asks for already (SOP activity
  // descriptions, inventory + financial transactions, tool selection, dosage
  // amounts, vendor names, buyer names). The helpers below let each Form 4 row
  // pull a sensible default for what previously needed manual entry.

  // SOP activity → "Kuantiti / Kadar" string (e.g. "5 L" or "2.5 kg")
  const sopDosageText = (act) => {
    if (!act) return '';
    if (act.dosage_amount && act.dosage_unit_symbol) return `${act.dosage_amount} ${act.dosage_unit_symbol}`;
    if (act.dosage_amount) return String(act.dosage_amount);
    if (act.mixture_amount && act.mixture_unit_symbol) return `${act.mixture_amount} ${act.mixture_unit_symbol}`;
    return '';
  };

  // SOP activity → "Bahan Aktif dan Kadar" string for Form 4-7
  const sopBahanKadar = (act) => {
    const mat = act?.name_material || '';
    const dose = sopDosageText(act);
    return [mat, dose].filter(Boolean).join(' — ');
  };

  // Financial_transaction_view row that backs a given inventory transaction.
  // Returns the joined fin row so we can pull vendor / receipt / description.
  const findFinTxnForInv = (invItem) => {
    if (!invItem || !financialView.length) return null;
    // 1. Direct inventory_transaction_id match (most accurate)
    let hit = financialView.find(f => f.inventory_transaction_id && invItem.id && f.inventory_transaction_id === invItem.id);
    if (hit) return hit;
    // 2. Match by inventory_item_id + similar date
    if (invItem.inventory_item_id) {
      hit = financialView.find(f => f.inventory_item_id === invItem.inventory_item_id);
      if (hit) return hit;
    }
    // 3. Match by material name + date proximity (loose)
    const name = (invItem.material_name || invItem.inventory_name || '').toLowerCase();
    if (name) {
      hit = financialView.find(f => (f.name_material || '').toLowerCase() === name);
      if (hit) return hit;
    }
    return null;
  };

  // For SOP-rooted rows (4-1/4-4/4-7), prefer the user's typed description
  // (sop_record_activity.description). Falls back to the row's own notes.
  const sopDescription = (act) => (act?.description || '').trim();

  // For inventory-rooted purchase rows (4-3/4-5), pull supplier from the
  // financial txn view (vendor_organisation_name) when not present on the
  // inventory item directly.
  const supplierFor = (invItem) => {
    if (invItem?.supplier_name) return invItem.supplier_name;
    const fin = findFinTxnForInv(invItem);
    return fin?.vendor_organisation_name || '';
  };

  // For sales rows (4-6), pull buyer from yield → fin view fallback chain.
  const buyerFor = (y) => {
    if (y?.buyer_name) return y.buyer_name;
    if (!financialView.length) return '';
    const hit = financialView.find(f => f.yield_transaction_id && y.yield_transaction_id && f.yield_transaction_id === y.yield_transaction_id);
    return hit?.organisation_client_name || '';
  };

  // Pesticide tool — most spray activities pick a tool (Knapsack, Boom, etc.).
  const toolFor = (act) => act?.tool_name || '';

  // Process label preferring Malay translation.
  const processLabel = (act) => act?.process_name_malay || act?.process_name || act?.activity_name || '';

  // ── SOP-programme rows for 4-2 / 4-3 / 4-4 / 4-5 ───────────────────────────
  // Farms whose whole fertigation + spray programme lives in the SOP (e.g. the
  // HATC chilli plots) have no inventory transactions, so 4-3 / 4-5 used to be
  // empty. Completed SOP activities are what was actually done, so they are
  // appended as records after the inventory rows (which are unchanged).
  const normName = (s) => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();
  // Calendar-day key (YYYY-MM-DD) for a date column or a timestamp.
  const dayKey = (d) => {
    if (!d) return '';
    const s = String(d);
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    const dt = new Date(s);
    if (isNaN(dt.getTime())) return s.slice(0, 10);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
  };
  // Oldest first; ties by SOP day, then id — same order on screen and in the PDF.
  const bySopDate = (a, b) => {
    const da = dayKey(a.date_activity), db = dayKey(b.date_activity);
    if (da !== db) {
      if (!da) return 1;
      if (!db) return -1;
      return da < db ? -1 : 1;
    }
    const dd = (Number(a.day) || 0) - (Number(b.day) || 0);
    if (dd) return dd;
    return (Number(a.id) || 0) - (Number(b.id) || 0);
  };

  // 4-5: a completed spray of a material (Pesticide process, or a material typed Pesticide).
  const isSopSpray = (a) => isCompletedAct(a) && hasMaterial(a) && (procId(a) === 2 || matTypeId(a) === 1);
  // 4-3: completed Fertilizer / Foliar / Media / Substrate treatment with a material + dose.
  // A pesticide-typed material is left to 4-5 so it is never listed in both.
  const isSopFertUse = (a) => isCompletedAct(a) && hasMaterial(a) && hasDosage(a) &&
    [1, 10, 3, 14].includes(procId(a)) && !isSopSpray(a);

  // Inventory usage rows already shown in a section: their (material, day)
  // pairs and the SOP activities they were created from.
  const invUsageIndex = (items) => {
    const keys = new Set();
    const actIds = new Set();
    items.forEach(it => {
      const linked = invActivityLinks[it.id];
      if (linked != null) actIds.add(Number(linked));
      if (it.is_in) return;
      const dk = dayKey(it.date_transact || it.created_at);
      [it.material_name, it.inventory_name].forEach(n => { if (n) keys.add(`${normName(n)}|${dk}`); });
    });
    return { keys, actIds };
  };
  const coveredByInventory = (act, idx) =>
    idx.actIds.has(Number(act.id)) || idx.keys.has(`${normName(act.name_material)}|${dayKey(act.date_activity)}`);

  const sopFertUse = sopActivities.filter(isSopFertUse);
  const sopSprays = sopActivities.filter(isSopSpray);
  const fertInvIndex = invUsageIndex(fertilizerItems);
  const pestInvIndex = invUsageIndex(pesticideItems);
  const sopFertRows43 = sopFertUse.filter(a => !coveredByInventory(a, fertInvIndex)).sort(bySopDate);
  const sopSprayRows45 = sopSprays.filter(a => !coveredByInventory(a, pestInvIndex)).sort(bySopDate);

  // Material type label for 4-3 "Formulasi" (Malay, as on the myGAP form).
  const MAT_TYPE_LABEL = { 1: 'Racun Perosak', 2: 'Baja', 4: 'Foliar', 6: 'Rawatan Media', 7: 'Benih' };
  const PROC_TYPE_LABEL = { 1: 'Baja', 10: 'Foliar', 3: 'Rawatan Media', 14: 'Rawatan Substrat' };
  const sopMaterialTypeLabel = (a) => {
    const t = matTypeId(a);
    if (t != null && MAT_TYPE_LABEL[t]) return MAT_TYPE_LABEL[t];
    return PROC_TYPE_LABEL[procId(a)] || a?.material_type2_name || a?.material_type_name || '';
  };
  // NPK / urea grade from the product name wins (same rule as the inventory rows).
  const sopFormulaDefault = (a) => {
    const name = a?.name_material || '';
    const npk = name.match(/(\d{1,3}[-:\s]\d{1,3}[-:\s]\d{1,3})/);
    const urea = name.match(/urea\s*(\d{1,3})\s*%?/i);
    return npk ? `NPK ${npk[1]}` : urea ? `Urea ${urea[1]}%` : sopMaterialTypeLabel(a);
  };
  // 4-3 "Cara Penggunaan": this activity's own tool + description.
  const sopCaraDefault = (a) => [toolFor(a), sopDescription(a)].filter(Boolean).join(' — ');

  // Target of a spray, parsed from the SOP description, e.g.
  //   "Imidacloprid 18.3%. Insecticides/Serangga - Dewasa White Fly. 25ml/100L"
  //   "Mancozeb 80%. Fungicide/Kulat. 130g/100L. 260g/200L"
  // An explicit "Perosak: X" (the original 4-4 rule) still wins.
  const sprayTarget = (desc) => {
    const d = String(desc || '');
    const clean = (s) => String(s || '').split(/,\s*\d/)[0].replace(/[\s,:;–-]+$/, '').trim();
    const perosak = d.match(/perosak\s*[:\-–][\s]*([^.,;\n]+)/i);
    if (perosak && clean(perosak[1])) {
      const x = clean(perosak[1]);
      return { pestName: x, justification: `Kawalan perosak: ${x}` };
    }
    const serangga = d.match(/serangga\s*[-–:]\s*([^.;\n]+)/i);
    if (serangga && clean(serangga[1])) {
      const x = clean(serangga[1]);
      return { pestName: x, justification: `Kawalan serangga: ${x}` };
    }
    if (/\b(fungicides?|fungisid\w*|kulat)\b/i.test(d)) return { pestName: 'Kulat (penyakit)', justification: 'Kawalan penyakit kulat' };
    if (/\b(herbicides?|herbisid\w*|rumpai)\b/i.test(d)) return { pestName: 'Rumpai', justification: 'Kawalan rumpai' };
    if (/\b(insecticides?|insektisid\w*|serangga)\b/i.test(d)) return { pestName: '', justification: 'Kawalan serangga' };
    return null;
  };
  // 4-4 "Nama Perosak" default.
  const pestNameDefault = (desc) => sprayTarget(desc)?.pestName || '';
  // 4-4 "% Kerosakan" default: ONLY an explicit "Kerosakan: N%" / "kerosakan N%".
  // Never a bare "N%" — that is usually the active ingredient's strength ("18.3%").
  const damageDefault = (desc) => {
    const m = String(desc || '').match(/kerosakan\s*[:\-–]?\s*~?\s*(\d+(?:[.,]\d+)?\s*%)/i);
    return m ? m[1].replace(/\s+/g, '') : '';
  };
  // 4-5 "Justifikasi Penggunaan" default for SOP sprays.
  const sprayJustificationDefault = (act) => sprayTarget(sopDescription(act))?.justification || sopDescription(act);
  // 4-5 "Nama Dagangan & Bahan Aktif".
  const tradeAndActive = (name, info) => [name, info?.activeIngredient].filter(Boolean).join(' / ');
  const REI_DEFAULT = '12 jam';   // agreed with the owner (23 Sep 2026); editable per row

  // SOP activity behind an inventory row (4-3 "Cara Penggunaan", 4-5 tool /
  // justification / LRMP): the activity the usage was created from, else the
  // same product on the same day, else (original rule) the first activity using
  // a product of that name.
  const relActForInv = (item, nameOverride) => {
    if (!item) return undefined;
    const linkedId = invActivityLinks[item.id];
    if (linkedId != null) {
      const hit = sopActivities.find(a => Number(a.id) === Number(linkedId));
      if (hit) return hit;
    }
    const nm = normName(item.material_name || item.inventory_name);
    const dk = dayKey(item.date_transact || item.created_at);
    if (nm && dk) {
      const sameDay = sopActivities.find(a => normName(a.name_material) === nm && dayKey(a.date_activity) === dk);
      if (sameDay) return sameDay;
    }
    const matName = nameOverride || item.material_name || item.inventory_name || '';
    return sopActivities.find(a => a.material_id === item.material_id || (a.name_material && matName && a.name_material.toLowerCase() === matName.toLowerCase()));
  };
  // Material details (LRMP / active ingredient) for an inventory row.
  const matInfoForInv = (item, relAct) => {
    const fin = findFinTxnForInv(item);
    return (fin?.material_id != null && materialInfo[fin.material_id]) ||
           (relAct?.material_id != null && materialInfo[relAct.material_id]) || null;
  };

  // 4-5 rows — one list for the screen, the PDF and "Salin ke semua" (WI-18):
  // inventory pesticide rows first (index keys, as before), then completed SOP
  // sprays (activity-id keys). `fields` = note field names, `defaults` = what
  // a cell shows when it has no note.
  const pest45Rows = [
    ...pesticideItems.map((item, idx) => {
      const fin = findFinTxnForInv(item);
      const relAct = relActForInv(item);
      const info = matInfoForInv(item, relAct);
      return {
        kind: 'inv', key: `inv-${idx}`, item, relAct, info,
        fields: { lrmp: `lrmp_${idx}`, justifikasi: `justifikasi_${idx}`, alat: `alat_${idx}`, phi: `phi_${idx}`, rei: `rei_${idx}` },
        defaults: { lrmp: info?.lrmp || '', justifikasi: fin?.description || sopDescription(relAct), alat: toolFor(relAct), phi: '', rei: REI_DEFAULT },
      };
    }),
    ...sopSprayRows45.map(act => {
      const info = materialInfo[act.material_id] || null;
      return {
        kind: 'sop', key: `sop45-${act.id}`, act, info,
        fields: { lrmp: `sop_${act.id}_lrmp`, justifikasi: `sop_${act.id}_justifikasi`, alat: `sop_${act.id}_alat`, phi: `sop_${act.id}_phi`, rei: `sop_${act.id}_rei` },
        defaults: { lrmp: info?.lrmp || '', justifikasi: sprayJustificationDefault(act), alat: toolFor(act), phi: '', rei: REI_DEFAULT },
      };
    }),
  ];

  // 4-2: the cycle's Transplanting (19) / Seeding (7) activities, if any.
  const cycleSopActs = selectedCycleId
    ? sopActivities.filter(a => a.season_id == null || Number(a.season_id) === Number(selectedCycleId))
    : [];
  // Only Completed activities count (myGAP records what was done); a Pending
  // one's planned date/quantity is never shown as done.
  const pickSopAct = (pid, needDose) =>
    cycleSopActs.filter(a => procId(a) === pid && isCompletedAct(a) && (!needDose || hasDosage(a)))
      .sort(bySopDate)[0] || null;
  const transplantAct = selectedCycle ? pickSopAct(19, false) : null;
  const seedingAct = selectedCycle ? pickSopAct(7, false) : null;
  // The usage quantity comes only from a dosed Transplanting; Seeding only
  // informs the date. If that transplant was drawn from seed inventory it is
  // already its own row below, so the cycle row keeps 0 (no double count).
  const dosedTransplantAct = selectedCycle ? pickSopAct(19, true) : null;
  const plantQtyAct = dosedTransplantAct && !coveredByInventory(dosedTransplantAct, invUsageIndex(seedItems))
    ? dosedTransplantAct : null;
  const plantDateAct = dosedTransplantAct || transplantAct || seedingAct;
  const plantUseDate = plantDateAct?.date_activity || selectedCycle?.startDate;
  const plantUseQty = plantQtyAct ? sopDosageText(plantQtyAct) : '0';
  const plantJustDefault = (() => {
    if (!selectedCycle?.produceName) return '';
    const base = `Penanaman ${selectedCycle.produceName} untuk kitaran ini`;
    const parts = [];
    if (seedingAct?.date_activity) parts.push(`semaian ${formatDateShort(seedingAct.date_activity)}`);
    if (transplantAct?.date_activity) parts.push(`pindah tanam ${formatDateShort(transplantAct.date_activity)}`);
    return parts.length ? `${base} (${parts.join(', ')})` : base;
  })();

  // Farm size label used by 4-3 "Keluasan".
  const farmSizeText = farm?.size ? `${String(farm.size).match(/[\d.]+/)?.[0] || ''} hektar` : '';

  // PDF pagination weight for rows carrying long free text (fertigation
  // descriptions run ~250 chars), so a page of them doesn't overflow. A normal
  // row (≤2 lines) renders ~36px = weight 1; each extra wrapped line (~85 chars
  // in the widest text column at the 1050px landscape layout) adds ~12px.
  const pdfRowWeight = (...texts) => {
    const len = Math.max(0, ...texts.map(t => String(t || '').length));
    const lines = Math.ceil(len / 85);
    return Math.max(1, (1 + lines) / 3);
  };

  // --- Form 1-1 data parsing ---
  const getFormDraftSection = (sectionName) => {
    const produceId = selectedCycle?.produceNameId;
    let draft = formDraftData.find(fd => fd.name === sectionName && fd.produce_id == produceId);
    if (!draft) draft = formDraftData.find(fd => fd.name === sectionName);
    if (!draft || !draft.data) return {};
    try {
      const parsed = Array.isArray(draft.data) ? draft.data : JSON.parse(draft.data);
      return Array.isArray(parsed) && parsed.length > 0 ? parsed[0] : (typeof parsed === 'object' ? parsed : {});
    } catch { return {}; }
  };
  // Two sources for Form 1-1 data, merged (local edits override form_draft):
  //   1. form_draft Supabase rows (populated by the Kapitani mobile app)
  //   2. form11Edits localStorage (filled in this React app's myGAP preview)
  const _form11A_draft = getFormDraftSection('A) Applicant Information');
  const _form11B_draft = getFormDraftSection('B) Farm Information');
  const _form11C_draft = getFormDraftSection('C) Types Of Crop');
  const _form11D_draft = getFormDraftSection('D) Marketing');
  const _form11E_draft = getFormDraftSection('E) Declaration');
  const form11A = { ..._form11A_draft, ...form11Edits.A };
  const form11B = { ..._form11B_draft, ...form11Edits.B };
  const form11C = { ..._form11C_draft, ...form11Edits.C };
  const form11D = { ..._form11D_draft, ...form11Edits.D };
  const form11E = { ..._form11E_draft, ...form11Edits.E };
  // Supplementary documents (Lampiran 1 attachments) live only in this web app's
  // draft (mygap_form11_draft.data.ATT) — the mobile app does not write them.
  const form11ATT = form11Edits.ATT || {};

  // Form 1-1 is now always available in the dropdown — users can fill it in
  // from this preview even if no draft exists in Supabase yet. Keeping the old
  // flag in case any downstream branch relies on it.
  const hasForm11 = true;

  // --- PDF Export: Build standalone HTML matching reference design ---
  // Helper: convert image URL to base64 data URL via fetch
  const imageToBase64 = (url) => fetch(url)
    .then(res => res.blob())
    .then(blob => new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve('');
      reader.readAsDataURL(blob);
    }))
    .catch(() => '');

  const buildPdfPage = (pageNum, totalPages, sectionNumber, sectionTitle, tableHtml, logos) => {
    const pageBreak = pageNum > 1 ? '<div class="pdf-page-break" style="page-break-before:always;"></div>' : '';
    const cropLogoHtml = logos.crop ? `<img src="${logos.crop}" style="width:55px;height:55px;object-fit:contain;" />` : '';
    const mygapLogoHtml = logos.mygap ? `<img src="${logos.mygap}" style="width:60px;height:55px;object-fit:contain;" />` : '';
    const footerLogoHtml = logos.footer ? `<img src="${logos.footer}" style="height:20px;object-fit:contain;" />` : '';
    return `${pageBreak}
      <div style="width:100%;min-height:680px;font-family:'Segoe UI',Arial,Helvetica,sans-serif;padding:15px 20px 10px 20px;box-sizing:border-box;">
        <!-- Page Header -->
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:14px;">
          <span style="font-size:9px;color:#888;">Generated by Kapitani taniLytics Software</span>
          <span style="font-size:9px;color:#888;">myGAP Farm Record</span>
        </div>
        <!-- Section Header -->
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:12px;">
          <div style="width:55px;height:55px;flex-shrink:0;">${cropLogoHtml}</div>
          <div style="text-align:center;flex:1;padding:0 10px;">
            <div style="font-size:20px;font-weight:bold;color:#222;margin-bottom:2px;">myGAP ${sectionNumber}</div>
            <div style="font-size:11px;font-weight:600;color:#333;text-transform:uppercase;letter-spacing:0.5px;">${sectionTitle}</div>
          </div>
          <div style="width:60px;height:55px;flex-shrink:0;">${mygapLogoHtml}</div>
        </div>
        <!-- Table Content -->
        ${tableHtml}
        <!-- Footer -->
        <div style="margin-top:20px;display:flex;justify-content:space-between;align-items:center;">
          <div style="display:flex;align-items:center;gap:6px;">${footerLogoHtml}</div>
          <span style="font-size:9px;color:#888;">${pageNum}</span>
        </div>
      </div>
    `;
  };

  const pdfThStyle = 'padding:8px 6px;text-align:center;font-weight:bold;color:#333;font-size:10px;border:1px solid #ccc;background:#e8e8e8;';
  const pdfThSubStyle = 'padding:6px 4px;text-align:center;font-weight:bold;color:#333;font-size:9px;border:1px solid #ccc;background:#e8e8e8;';
  const pdfTdStyle = 'padding:6px;text-align:center;color:#333;font-size:10px;border:1px solid #ddd;vertical-align:top;';
  const pdfTableStart = '<table style="width:100%;border-collapse:collapse;font-size:10px;">';

  // Same key as EditableCell. A stored note (even '') wins; the default is used
  // only when the key is absent — so screen and PDF always show the same value.
  const noteKeyFor = (section, field) => `${farm.id}_${selectedCycleId}_${section}_${field}`;
  const getNoteVal = (section, field, defaultValue = '') =>
    noteOrDefault(notes, noteKeyFor(section, field), defaultValue);

  // 4-6 (WI-20): notes keyed by the yield transaction id so they can't shift
  // rows; the list index only when a row has no id (= the pre-WI-20 key).
  const row46Key = (y, idx) => (y?.yield_transaction_id != null ? `yt${y.yield_transaction_id}` : String(idx));
  // A buyer typed before WI-20 was keyed by row index (`pembeli_${idx}`) and only
  // shown when no buyer was known. Rows are listed newest first, so every new
  // sale moved those names down one row. They are copied once onto the rows they
  // sit on now (effect below) and this marker stops the index keys being read.
  const LEGACY46_DONE_FIELD = 'pembeli_idx_copied';
  const legacy46Done = hasNote(notes, noteKeyFor('4-6', LEGACY46_DONE_FIELD));
  const row46Defaults = (y, idx) => {
    const legacyBuyer = legacy46Done ? null : notes[noteKeyFor('4-6', `pembeli_${idx}`)];
    return {
      invois: y?.order_id != null ? String(y.order_id) : '',
      pembeli: buyerFor(y) || (legacyBuyer != null ? String(legacyBuyer) : ''),
    };
  };
  useEffect(() => {
    if (!farm?.id || loading || notesSource !== 'db' || !canSaveNotes()) return;
    if (dataScopeRef.current !== `${farm.id}:${selectedCycleId}`) return;   // rows not (yet) this farm/cycle's
    if (legacy46Done) return;
    const legacyRe = new RegExp(`^${farm.id}_${selectedCycleId}_4-6_pembeli_\\d+$`);
    if (!Object.keys(notes).some(k => legacyRe.test(k))) return;   // nothing typed before WI-20
    const updates = {};
    productionSales.forEach((y, idx) => {
      if (y?.yield_transaction_id == null) return;          // no stable id: its cell still uses the index key
      const legacy = notes[noteKeyFor('4-6', `pembeli_${idx}`)];
      if (legacy == null || String(legacy) === '' || buyerFor(y)) return;
      const newKey = noteKeyFor('4-6', `pembeli_yt${y.yield_transaction_id}`);
      if (!hasNote(notes, newKey)) updates[newKey] = String(legacy);
    });
    updates[noteKeyFor('4-6', LEGACY46_DONE_FIELD)] = new Date().toISOString();
    setNoteValues(updates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [farm?.id, selectedCycleId, loading, notesSource, farmAccess, legacy46Done, notes, yieldData, financialView]);

  // 4-5 (WI-18) "Salin ke semua": copy row 1's current value of a column to
  // every 4-5 row (inventory + SOP) for this farm and cycle, as notes.
  const COPY45_LABELS = { alat: 'ALAT YANG DIGUNAKAN', justifikasi: 'JUSTIFIKASI PENGGUNAAN' };
  const first45Value = (col) => (pest45Rows.length
    ? getNoteVal('4-5', pest45Rows[0].fields[col], pest45Rows[0].defaults[col]) : '');
  const copy45ToAll = (col) => {
    if (pest45Rows.length < 2) return;
    const value = first45Value(col);
    if (!value.trim()) return;
    const differing = pest45Rows.filter(r => getNoteVal('4-5', r.fields[col], r.defaults[col]) !== value).length;
    const msg = `Salin "${value}" ke semua ${pest45Rows.length} baris ${COPY45_LABELS[col] || col} dalam 4-5?`
      + (differing ? `\n\n${differing} baris yang berbeza akan diganti.` : '\n\nSemua baris sudah sama; nilai ini akan disimpan untuk setiap baris.');
    if (!window.confirm(msg)) return;
    const updates = {};
    pest45Rows.forEach(r => { updates[noteKeyFor('4-5', r.fields[col])] = value; });
    setNoteValues(updates);
  };

  const buildPdfHtml = (logos) => {
    let pages = [];
    let pageNum = 1;
    const ROWS_PER_PAGE = 12;

    // Helper: split row array into page-sized chunks, each with full header/footer.
    // Optional rowWeights (one per row, default 1) let rows with long text take
    // more of a page's ROWS_PER_PAGE budget; with no weights this is the plain
    // 12-rows-per-page split.
    const addSection = (sectionNumber, sectionTitle, theadHtml, rowsArr, emptyColspan, rowWeights) => {
      if (rowsArr.length === 0) {
        rowsArr = [`<tr><td style="${pdfTdStyle}" colspan="${emptyColspan}">&nbsp;</td></tr>`];
        rowWeights = null;
      }
      const chunks = [];
      if (rowWeights && rowWeights.length === rowsArr.length) {
        let cur = [];
        let used = 0;
        rowsArr.forEach((r, i) => {
          const w = Math.max(1, Number(rowWeights[i]) || 1);
          if (cur.length && used + w > ROWS_PER_PAGE) { chunks.push(cur); cur = []; used = 0; }
          cur.push(r);
          used += w;
        });
        if (cur.length) chunks.push(cur);
      } else {
        for (let i = 0; i < rowsArr.length; i += ROWS_PER_PAGE) {
          chunks.push(rowsArr.slice(i, i + ROWS_PER_PAGE));
        }
      }
      chunks.forEach(chunk => {
        pages.push(buildPdfPage(pageNum++, 0, sectionNumber, sectionTitle,
          `${pdfTableStart}<thead>${theadHtml}</thead><tbody>${chunk.join('')}</tbody></table>`, logos
        ));
      });
    };

    // --- Form 1-1: Application Form (Borang Permohonan) ---
    if (hasForm11 && (exportMode === 'form11' || exportMode === 'all')) {
      const cb = (checked) => checked ? '☑' : '☐';
      // Match BOTH Malay and English values — the Kapitani mobile app stores
      // English (e.g. "Individual", "Projects") while the web editor stores the
      // Malay enum. Keep the three "other"-type options mutually exclusive: the
      // Agensi label literally contains "Projek", and "Projects" must still tick
      // the Projek box.
      const _tof = (form11B.Type_of_farm || '').toLowerCase();
      const isIndividu = _tof.includes('individu') || _tof.includes('individual');
      const isSyarikat = _tof.includes('syarikat') || _tof.includes('company');
      const isAgensi = _tof.includes('agensi') || _tof.includes('agency');
      const isProjek = !isAgensi && (_tof.includes('projek') || _tof.includes('project') || _tof.includes('program'));
      const isLainLain = !isAgensi && !isProjek && (_tof.includes('lain') || _tof.includes('other'));
      // Section 10 sub-options / specify text (filled from the editor).
      const projekSub = (form11B.Type_of_farm_projek_sub || '').toLowerCase();
      const isProjekTKPM = isProjek && projekSub.includes('tkpm');
      const isProjekStesyen = isProjek && (projekSub.includes('stesyen') || projekSub.includes('stesen') || projekSub.includes('pusat'));
      const isProjekOther = isProjek && projekSub.includes('lain');
      const projekText = form11B.Type_of_farm_projek_text || '';
      const agensiText = form11B.Type_of_farm_agensi_text || '';
      const otherFarmText = form11B.Type_of_farm_other_text || '';
      // Lampiran 1 supplementary-document checklist ticks (Section ATT).
      const _att = form11ATT || {};
      const tickIcSsm   = (_att.ic_ssm || []).length ? '✓' : '&nbsp;';
      const tickLand    = (_att.land_status || []).length ? '✓' : '&nbsp;';
      const tickHarvest = _att.harvest_checked ? '✓' : '&nbsp;';
      const tickLayout  = (_att.farm_layout || []).length ? '✓' : '&nbsp;';
      const tickEia     = (_att.eia || []).length ? '✓' : '&nbsp;';
      const isContractNo = (form11B.Contract_farming || '').toLowerCase() === 'no' || (form11B.Contract_farming || '').toLowerCase() === 'tidak';
      const isContractYes = (form11B.Contract_farming || '').toLowerCase() === 'yes' || (form11B.Contract_farming || '').toLowerCase() === 'ya';
      const isTerbuka = (form11B.Farmingsystem || '').toLowerCase().includes('open') || (form11B.Farmingsystem || '').toLowerCase().includes('terbuka');
      const isSepara = (form11B.Farmingsystem || '').toLowerCase().includes('separa') || (form11B.Farmingsystem || '').toLowerCase().includes('partial');
      const isBertutup = (form11B.Farmingsystem || '').toLowerCase().includes('bertutup') || (form11B.Farmingsystem || '').toLowerCase().includes('closed') || (form11B.Farmingsystem || '').toLowerCase().includes('fully');
      const isDirectSales = form11D.Direct_sales === 'true' || form11D.Direct_sales === true;
      const isExport = form11D.Export === 'true' || form11D.Export === true;
      const isOthersD = form11D.Others === 'true' || form11D.Others === true;
      const cropLogoHtml = logos.crop ? `<img src="${logos.crop}" style="width:70px;height:70px;object-fit:contain;" />` : '';
      const mygapLogoHtml = logos.mygap ? `<img src="${logos.mygap}" style="width:80px;height:70px;object-fit:contain;" />` : '';
      const commodity = form11C.Commodity || '';
      const cropName = form11C.Crop || selectedCycle?.produceName || '';
      const plantedArea = form11C.Planted_area || '';
      const isFruit = commodity.toLowerCase().includes('buah') || commodity.toLowerCase().includes('fruit');
      const isVeg = commodity.toLowerCase().includes('sayur') || commodity.toLowerCase().includes('vegetable');
      const isPadi = commodity.toLowerCase().includes('padi') || commodity.toLowerCase().includes('paddy');
      const sigHtml = form11E.Signature_of_applicant ? `<img src="${form11E.Signature_of_applicant}" style="max-height:50px;max-width:150px;object-fit:contain;" />` : '';
      const sealHtml = form11E.Company_seal ? `<img src="${form11E.Company_seal}" style="max-height:60px;max-width:150px;object-fit:contain;" />` : '';
      const bd = 'border:1px solid #000;';
      const secHdr = (letter, titleM, titleE) => `<tr><td style="${bd}padding:0 8px 10px;background:#d9d9d9;font-weight:bold;font-size:16px;width:35px;text-align:center;vertical-align:middle;">${letter}</td><td colspan="5" style="${bd}padding:0 8px 10px;background:#d9d9d9;font-weight:bold;font-size:16px;text-align:center;"><div>${titleM}</div><div style="font-style:italic;font-size:13px;">(${titleE})</div></td></tr>`;
      const fld = (num, lm, le, val) => `<tr><td colspan="6" style="${bd}padding:0 10px 10px;font-size:15px;line-height:1.0;"><div style="line-height:1.35;"><b>[${num}] ${lm} :</b> &nbsp; ${val || ''}</div><div style="line-height:1.3;"><i style="font-size:12px;color:#555;">${le}</i></div></td></tr>`;
      const fld3 = (n1,l1,e1,v1,n2,l2,e2,v2,n3,l3,e3,v3) => `<tr><td colspan="2" style="${bd}padding:0 10px 10px;font-size:15px;line-height:1.0;"><div style="line-height:1.35;"><b>[${n1}] ${l1} :</b> &nbsp; ${v1||''}</div><div style="line-height:1.3;"><i style="font-size:12px;color:#555;">${e1}</i></div></td><td colspan="2" style="${bd}padding:0 10px 10px;font-size:15px;line-height:1.0;"><div style="line-height:1.35;"><b>[${n2}] ${l2} :</b> &nbsp; ${v2||''}</div><div style="line-height:1.3;"><i style="font-size:12px;color:#555;">${e2}</i></div></td><td colspan="2" style="${bd}padding:0 10px 10px;font-size:15px;line-height:1.0;"><div style="line-height:1.35;"><b>[${n3}] ${l3} :</b> &nbsp; ${v3||''}</div><div style="line-height:1.3;"><i style="font-size:12px;color:#555;">${e3}</i></div></td></tr>`;

      // Single continuous HTML block — no forced page breaks, flows naturally like the reference PDF
      const form11Html = `<div style="width:100%;font-family:'Segoe UI',Arial,sans-serif;padding:30px 14px 20px 30px;box-sizing:border-box;font-size:15px;line-height:1.4;">
      <table style="width:100%;border-collapse:collapse;${bd}">
        <tr><td colspan="6" style="${bd}padding:8px;">
          <div style="display:flex;align-items:center;justify-content:space-between;">
            <div style="flex-shrink:0;">${cropLogoHtml}</div>
            <div style="text-align:center;flex:1;padding:0 8px;">
              <div style="font-size:16px;font-weight:bold;">myGAP</div>
              <div style="font-size:11px;font-style:italic;">Malaysian Good Agricultural Practice</div>
              <div style="font-size:15px;font-weight:bold;">JABATAN PERTANIAN</div>
              <div style="font-size:11px;font-style:italic;">Department Of Agriculture</div>
              <div style="font-size:15px;font-weight:bold;margin-top:6px;">BORANG PERMOHONAN</div>
              <div style="font-size:12px;font-style:italic;">APPLICATION FORM</div>
            </div>
            <div style="flex-shrink:0;">${mygapLogoHtml}</div>
          </div>
          <div style="text-align:center;margin-top:8px;font-size:11px;font-style:italic;color:#333;">
            Bahagian A hingga E untuk diisi oleh Pemohon<br/>
            Bahagian F untuk diisi oleh Pegawai Pembimbing<br/>
            Bahagian G untuk diisi oleh Urus Setia Pensijilan
          </div>
          <div style="text-align:center;margin-top:6px;font-size:11px;font-style:italic;">(SILA RUJUK PANDUAN MENGISI BORANG)</div>
        </td></tr>
        ${secHdr('A', 'MAKLUMAT PEMOHON', 'INFORMATION OF APPLICANT')}
        ${fld('1', 'Nama Pemohon', 'Name of Applicant', form11A.Name)}
        ${fld('2', 'No. KP/No. Pendaftaran Syarikat', 'IC No. or Company Registration No.', form11A.IC_number)}
        ${fld('3', 'Nama Wakil Yang Boleh Dihubungi', 'Name of the contact person', form11A.Contact_person)}
        ${fld('4', 'Alamat Surat Menyurat', 'Correspondence Address', (form11A.Address_line_1 || '') + (form11A.Address_line_2 ? ', ' + form11A.Address_line_2 : ''))}
        ${fld3('5','Poskod','Postcode',form11A.Postcode,'6','Bandar','City',form11A.City,'7','Negeri','State',form11A.State)}
        <tr><td colspan="2" style="${bd}padding:0 10px 10px;font-size:15px;line-height:1.0;"><div style="line-height:1.35;"><b>[8] No. Telefon :</b> &nbsp; ${form11A.Phone || ''}</div><div style="line-height:1.3;"><i style="font-size:12px;color:#555;">Phone No.</i></div></td><td colspan="2" style="${bd}padding:0 10px 10px;font-size:15px;line-height:1.0;"><div style="line-height:1.35;"><b>[9] No. Faks :</b> &nbsp; ${form11A.Fax || ''}</div><div style="line-height:1.3;"><i style="font-size:12px;color:#555;">Fax No.</i></div></td><td colspan="2" style="${bd}padding:0 10px 10px;font-size:15px;line-height:1.0;"><div style="line-height:1.35;"><b>[10] Alamat e-mail :</b> &nbsp; ${form11A.Email || ''}</div><div style="line-height:1.3;"><i style="font-size:12px;color:#555;">E-mail address</i></div></td></tr>
        ${secHdr('B', 'MAKLUMAT LADANG', 'DESCRIPTION OF FARM')}
        ${fld('1', 'Nama Ladang', 'Name of Farm', form11B.Farm_name || farm?.name)}
        <tr><td colspan="2" style="${bd}padding:0 10px 10px;font-size:15px;line-height:1.0;"><div style="line-height:1.35;"><b>[2] Nombor Lot :</b> &nbsp; ${form11B.Lot_number || ''}</div><div style="line-height:1.3;"><i style="font-size:12px;color:#555;">Lot Number</i></div></td><td colspan="2" style="${bd}padding:0 10px 10px;font-size:15px;line-height:1.0;"><div style="line-height:1.35;"><b>[3] Blok :</b> &nbsp; ${form11B.Block || ''}</div><div style="line-height:1.3;"><i style="font-size:12px;color:#555;">Block</i></div></td><td colspan="2" style="${bd}padding:0 10px 10px;font-size:15px;line-height:1.0;"><div style="line-height:1.35;"><b>[4] Kampung :</b> &nbsp; ${form11B.Village || ''}</div><div style="line-height:1.3;"><i style="font-size:12px;color:#555;">Village</i></div></td></tr>
        ${fld3('5','Mukim','Sub District',form11B.Sub_district,'6','Daerah / Jajahan','District',form11B.District,'7','Negeri','State',form11B.State)}
        ${fld('8', 'Keluasan Fizikal Ladang (hektar)', 'Area (hectares)', form11B.Area)}
        ${fld('9', 'Tarikh Ladang Mula Beroperasi', "Date of farm's come into operation", form11B.Date_of_farms_come_into_operation)}
      </table>
      <div style="page-break-before:always;"></div>
      <table style="width:100%;border-collapse:collapse;${bd}">
        <tr><td colspan="6" style="${bd}padding:0 10px 10px;font-size:15px;line-height:1.0;">
          <div style="line-height:1.35;"><b>[10] Jenis Ladang :</b></div>
          <div style="margin-left:14px;font-size:13px;font-style:italic;">Sila tanda [✓] di ruang yang disediakan.<br/><span style="color:#555;">Type of Farm, Please tick [✓] in the appropriate space given</span></div>
          <div style="margin:8px 0 3px 28px;font-size:15px;">${cb(isIndividu)} &nbsp; <b>Individu</b><br/><span style="margin-left:20px;font-size:12px;font-style:italic;color:#555;">Individual/ Smallholder Farm</span></div>
          <div style="margin:6px 0 3px 28px;font-size:15px;">${cb(isSyarikat)} &nbsp; <b>Syarikat</b><br/><span style="margin-left:20px;font-size:12px;font-style:italic;color:#555;">Company</span></div>
          <div style="margin:6px 0 3px 28px;font-size:15px;">${cb(isProjek)} &nbsp; <b>Projek / Program dibawah Jabatan Pertanian</b><br/><span style="margin-left:20px;font-size:12px;font-style:italic;color:#555;">Projects/Programme under Department Of Agriculture</span></div>
          <div style="margin:14px 0 3px 50px;font-size:13px;display:flex;gap:20px;flex-wrap:wrap;">
            <span>${cb(isProjekTKPM)} &nbsp; Taman Kekal Pengeluaran Makanan (TKPM)<br/><i style="font-size:11px;color:#555;">&nbsp;&nbsp;&nbsp;&nbsp;Permanent Food Production Park</i></span>
            <span>${cb(isProjekStesyen)} &nbsp; Stesyen / Pusat Pertanian<br/><i style="font-size:11px;color:#555;">&nbsp;&nbsp;&nbsp;&nbsp;Agriculture Station / Center</i></span>
            <span>${cb(isProjekOther)} &nbsp; Lain-lain, nyatakan<br/><i style="font-size:11px;color:#555;">&nbsp;&nbsp;&nbsp;&nbsp;Others Please specify:</i>&nbsp;<span style="border-bottom:1px dotted #000;display:inline-block;min-width:80px;padding:0 4px 3px;">${isProjekOther ? (projekText || '&nbsp;') : '&nbsp;'}</span></span>
          </div>
          <div style="margin:6px 0 3px 28px;font-size:15px;">${cb(isAgensi)} &nbsp; <b>Projek di bawah Agensi Kementerian Pertanian dan Industri Asas Tani</b><br/><span style="margin-left:20px;font-size:12px;font-style:italic;color:#555;">Projects under agencies of Ministry of Agriculture and Agro-based Industry</span><br/><span style="margin-left:20px;font-size:13px;">Sila nyatakan : <i style="font-size:12px;color:#555;">Please specify</i> <span style="border-bottom:1px dotted #000;display:inline-block;min-width:180px;padding:0 4px 3px;">${isAgensi ? (agensiText || '&nbsp;') : '&nbsp;'}</span></span></div>
          <div style="margin:16px 0 3px 28px;font-size:15px;">${cb(isLainLain)} &nbsp; <b>Lain-lain, Sila nyatakan :</b><br/><span style="margin-left:20px;font-size:12px;font-style:italic;color:#555;">Others, Please specify</span> <span style="border-bottom:1px dotted #000;display:inline-block;min-width:180px;padding:0 4px 13px;font-size:12px;">${isLainLain ? (otherFarmText || '&nbsp;') : '&nbsp;'}</span></div>
        </td></tr>
        <tr><td colspan="6" style="${bd}padding:0 10px 10px;font-size:15px;line-height:1.0;">
          <div style="line-height:1.35;"><b>[11] Adakah ladang ini menyertai Program Ladang Kontrak?</b></div><div style="line-height:1.3;"><i style="font-size:12px;color:#555;">(Does the farm join Contract Farming?)</i></div>
          <div style="margin:8px 0 0 28px;font-size:15px;display:flex;gap:30px;">
            <span>${cb(isContractNo)} &nbsp; <b>Tidak</b> <i style="font-size:12px;color:#555;">No</i></span>
            <span>${cb(isContractYes)} &nbsp; <b>Ya</b> <i style="font-size:12px;color:#555;">Yes</i></span>
            <span style="font-size:13px;">Jika ya, sila nyatakan <span style="border-bottom:1px dotted #000;display:inline-block;min-width:150px;padding:0 4px 13px;">${form11B.Contract_farming_text || '&nbsp;'}</span></span>
          </div>
        </td></tr>
        <tr><td colspan="6" style="${bd}padding:0 10px 10px;font-size:15px;line-height:1.0;">
          <div style="line-height:1.35;"><b>[12] Sistem Ladang :</b> <i style="font-size:12px;color:#555;">(Farming system)</i></div>
          <div style="margin-left:14px;font-size:13px;">Sila tanda [✓] di ruang yang disediakan. <i style="font-size:12px;color:#555;">(Please tick [✓] in the appropriate space given)</i></div>
          <div style="margin:6px 0 3px 28px;font-size:15px;">${cb(isTerbuka)} &nbsp; <b>Terbuka</b> <i style="font-size:12px;color:#555;">Open</i></div>
          <div style="margin:5px 0 3px 28px;font-size:15px;">${cb(isSepara)} &nbsp; <b>Separa Tertutup</b> <i style="font-size:12px;color:#555;">Partially Closed</i></div>
          <div style="margin:5px 0 3px 28px;font-size:15px;">${cb(isBertutup)} &nbsp; <b>Bertutup Penuh</b> <i style="font-size:12px;color:#555;">Fully Closed</i></div>
        </td></tr>
        ${secHdr('C', 'JENIS TANAMAN YANG DIUSAHAKAN', 'TYPES OF CROP')}
        <tr>
          <td colspan="2" style="${bd}padding:4px 4px 10px;text-align:center;font-size:15px;font-weight:bold;">Komoditi<br/><i style="font-weight:normal;font-size:12px;">Commodity</i></td>
          <td colspan="2" style="${bd}padding:4px 4px 10px;text-align:center;font-size:15px;font-weight:bold;">Jenis tanaman<br/><i style="font-weight:normal;font-size:12px;">Crop</i></td>
          <td colspan="2" style="${bd}padding:4px 4px 10px;text-align:center;font-size:15px;font-weight:bold;">Keluasan bertanam (ha)<br/><i style="font-weight:normal;font-size:12px;">Planted area (ha)</i></td>
        </tr>
        <tr><td colspan="2" style="${bd}padding:4px 4px 10px;text-align:center;font-size:15px;">Buah-buahan<br/><i style="font-size:12px;color:#555;">Fruits</i></td><td colspan="2" style="${bd}padding:4px 4px 10px;text-align:center;font-size:15px;">${isFruit ? cropName : ''}</td><td colspan="2" style="${bd}padding:4px 4px 10px;text-align:center;font-size:15px;">${isFruit ? plantedArea : ''}</td></tr>
        <tr><td colspan="2" style="${bd}padding:4px 4px 10px;text-align:center;font-size:15px;">Sayur-sayuran<br/><i style="font-size:12px;color:#555;">Vegetables</i></td><td colspan="2" style="${bd}padding:4px 4px 10px;text-align:center;font-size:15px;">${isVeg ? cropName : ''}</td><td colspan="2" style="${bd}padding:4px 4px 10px;text-align:center;font-size:15px;">${isVeg ? plantedArea : ''}</td></tr>
        <tr><td colspan="2" style="${bd}padding:4px 4px 10px;text-align:center;font-size:15px;">Padi<br/><i style="font-size:12px;color:#555;">Paddy</i></td><td colspan="2" style="${bd}padding:4px 4px 10px;text-align:center;font-size:15px;">${isPadi ? cropName : ''}</td><td colspan="2" style="${bd}padding:4px 4px 10px;text-align:center;font-size:15px;">${isPadi ? plantedArea : ''}</td></tr>
        <tr><td colspan="2" style="${bd}padding:4px 4px 10px;text-align:center;font-size:15px;">Lain-lain<br/><i style="font-size:12px;color:#555;">Others</i></td><td colspan="2" style="${bd}padding:4px 4px 10px;text-align:center;font-size:15px;">${(!isFruit && !isVeg && !isPadi) ? cropName : ''}</td><td colspan="2" style="${bd}padding:4px 4px 10px;text-align:center;font-size:15px;">${(!isFruit && !isVeg && !isPadi) ? plantedArea : ''}</td></tr>
        <tr><td colspan="4" style="${bd}padding:4px 8px 10px;text-align:right;font-size:15px;font-weight:bold;">Jumlah Keluasan Ladang Bertanam (ha) :<br/><i style="font-weight:normal;font-size:12px;color:#555;">Total planted area (ha)</i></td><td colspan="2" style="${bd}padding:4px 4px 10px;text-align:center;font-size:15px;color:#c00;">= ${plantedArea || ''}</td></tr>
      </table>
      <div style="page-break-before:always;"></div>
      <table style="width:100%;border-collapse:collapse;${bd}">
        ${secHdr('D', 'PEMASARAN', 'MARKETING')}
        <tr><td colspan="6" style="${bd}padding:0 10px 10px;font-size:15px;line-height:1.0;">
          <div style="font-size:13px;">Sila tanda [✓]. <i style="color:#555;">(Please tick [✓])</i></div>
          <div style="margin:8px 0 3px 28px;">${cb(isDirectSales)} &nbsp; <b>Jualan Terus</b><br/><span style="margin-left:20px;font-size:12px;font-style:italic;color:#555;">Direct sales</span></div>
          <div style="margin:6px 0 3px 28px;">${cb(isExport)} &nbsp; <b>Eksport</b><br/><span style="margin-left:20px;font-size:12px;font-style:italic;color:#555;">Export</span><br/><span style="margin-left:20px;font-size:13px;">Sila nyatakan : <span style="border-bottom:1px dotted #000;display:inline-block;width:180px;">${form11D.Export_country || '&nbsp;'}</span><br/><i style="font-size:12px;color:#555;">Please specify the country</i></span></div>
          <div style="margin:6px 0 3px 28px;">${cb(isOthersD)} &nbsp; <b>Lain-lain</b>${isOthersD && form11D.Others_text ? ' : ' + form11D.Others_text : ''}<br/><span style="margin-left:20px;font-size:12px;font-style:italic;color:#555;">Others, Please specify</span></div>
          <div style="margin-top:6px;font-size:12px;font-style:italic;">*Boleh memilih lebih dari satu / <i>May choose more than one</i></div>
        </td></tr>
        ${secHdr('E', 'PENGESAHAN', 'DECLARATION')}
        <tr><td colspan="6" style="${bd}padding:0 10px 10px;font-size:15px;line-height:1.35;">
          Saya mengesahkan bahawa segala butiran yang dinyatakan di atas adalah benar dan akan menurut segala syarat dalam keperluan pematuhan Pensijilan myGAP. Saya juga membenarkan maklumat saya diguna pakai oleh pihak Jabatan bagi tujuan pensijilan dan dipaparkan kepada umum bagi tujuan promosi dan pengesahan maklumat serta sebarang keperluan pada masa akan datang.
        </td></tr>
        <tr><td colspan="6" style="${bd}padding:0 10px 10px;font-size:13px;font-style:italic;line-height:1.5;color:#333;">
          I hereby certify that the information given above are correct and will abide with all rules in the requirement myGAP Certification. I also allow my information to be used by the Department for the purpose of certification and display to the public for the purpose of promotion and verification of information as well as any need in the future.
        </td></tr>
        <tr><td colspan="3" style="${bd}padding:0 10px 10px;font-size:15px;"><b>Tandatangan Pemohon :</b><br/><i style="font-size:12px;color:#555;">Signature of applicant</i><div style="min-height:60px;padding:8px;">${sigHtml}</div></td><td colspan="3" style="${bd}padding:0 10px 10px;font-size:15px;"><b>Cop Syarikat :</b><br/><i style="font-size:12px;color:#555;">Company seal</i><div style="min-height:60px;padding:8px;">${sealHtml || '&nbsp;'}</div></td></tr>
        <tr><td colspan="3" style="${bd}padding:0 10px 10px;font-size:15px;"><b>Tarikh :</b> <i style="font-size:12px;color:#555;">Date</i></td><td colspan="3" style="${bd}padding:0 10px 10px;font-size:15px;">${form11E.Date || ''}</td></tr>
        <tr><td colspan="6" style="${bd}padding:0 10px 10px;font-size:15px;text-align:center;line-height:1.35;">
          <div>Sila hantarkan borang permohonan yang lengkap beserta dokumen berkaitan (<b>Lampiran 1</b>) kepada</div>
          <div style="font-size:12px;font-style:italic;color:#555;">Please send the complete application form together with the relevant documents (Appendix 1)</div>
          <div style="margin-top:10px;font-weight:bold;font-size:16px;">PEJABAT PERTANIAN DAERAH</div>
          <div style="font-size:13px;">(mengikut lokasi ladang)</div>
          <div style="font-size:15px;font-weight:bold;font-style:italic;">AGRICULTURE DISTRICT OFFICE</div>
          <div style="font-size:12px;font-style:italic;color:#555;">(based on farm location)</div>
        </td></tr>
      </table>
      <div class="pdf-page-break" style="page-break-before:always;"></div>
      <table style="width:100%;border-collapse:collapse;${bd}">
        <tr><td style="${bd}padding:0 8px 10px;background:#d9d9d9;font-weight:bold;font-size:16px;width:35px;text-align:center;vertical-align:middle;">F</td><td colspan="5" style="${bd}padding:0 8px 10px;background:#d9d9d9;font-weight:bold;font-size:15px;text-align:center;">UNTUK DIISI OLEH PEGAWAI PEMBIMBING DARIPADA JABATAN PERTANIAN</td></tr>
        <tr><td colspan="6" style="${bd}padding:0 10px 10px;font-size:15px;line-height:1.35;"><b>[1] Nama:</b> <i style="font-size:12px;color:#555;">(Name)</i></td></tr>
        <tr><td colspan="6" style="${bd}padding:0 10px 10px;font-size:15px;line-height:1.35;"><b>[2] Jawatan :</b> <i style="font-size:12px;color:#555;">(Position)</i></td></tr>
        <tr><td colspan="6" style="${bd}padding:0 10px 10px;font-size:15px;line-height:1.35;"><b>[3] Kawasan Perkhidmatan:</b> <i style="font-size:12px;color:#555;">(Service area)</i></td></tr>
        <tr><td colspan="6" style="${bd}padding:0 10px 10px;font-size:15px;line-height:1.35;"><b>[4] Alamat Pejabat :</b> <i style="font-size:12px;color:#555;">(Office Address)</i></td></tr>
        <tr><td colspan="2" style="${bd}padding:0 10px 10px;font-size:15px;line-height:1.0;"><div style="line-height:1.35;"><b>[5] Poskod :</b></div><div style="line-height:1.3;"><i style="font-size:12px;color:#555;">(Postcode)</i></div></td><td colspan="2" style="${bd}padding:0 10px 10px;font-size:15px;line-height:1.0;"><div style="line-height:1.35;"><b>[6] Daerah / Jajahan:</b></div><div style="line-height:1.3;"><i style="font-size:12px;color:#555;">(District)</i></div></td><td colspan="2" style="${bd}padding:0 10px 10px;font-size:15px;line-height:1.0;"><div style="line-height:1.35;"><b>[7] Negeri :</b></div><div style="line-height:1.3;"><i style="font-size:12px;color:#555;">(State)</i></div></td></tr>
        <tr><td colspan="2" style="${bd}padding:0 10px 10px;font-size:15px;line-height:1.0;"><div style="line-height:1.35;"><b>[8] No. Telefon Pejabat :</b></div><div style="line-height:1.3;"><i style="font-size:12px;color:#555;">(Office Tel No.)</i></div></td><td colspan="2" style="${bd}padding:0 10px 10px;font-size:15px;line-height:1.0;"><div style="line-height:1.35;"><b>[9] No. Telefon Bimbit :</b></div><div style="line-height:1.3;"><i style="font-size:12px;color:#555;">(Mobile No.)</i></div></td><td colspan="2" style="${bd}padding:0 10px 10px;font-size:15px;line-height:1.0;"><div style="line-height:1.35;"><b>[10] No. Faks :</b></div><div style="line-height:1.3;"><i style="font-size:12px;color:#555;">(Fax No.)</i></div></td></tr>
        <tr><td colspan="6" style="${bd}padding:0 10px 10px;font-size:15px;line-height:1.35;"><b>[11] Alamat e-mail :</b> <i style="font-size:12px;color:#555;">(E-mail address)</i></td></tr>
        <tr><td colspan="6" style="${bd}padding:0 10px 10px;font-size:15px;text-align:center;line-height:1.35;">
          <div style="font-weight:bold;">Sila sertakan dokumen berikut bersama permohonan ini.</div>
          <div style="font-size:12px;font-style:italic;color:#555;">Please attach the following documents together with this application</div>
          <div style="text-align:left;margin-top:10px;padding-left:16px;">
            <div style="margin-bottom:6px;"><b>1.</b> &nbsp; <b>Borang myGAP 1-2</b> <span style="font-size:12px;font-style:italic;color:#555;">(myGAP 1-2 form)</span></div>
            <div><b>2.</b> &nbsp; <b>Dokumen yang berkaitan seperti yang dinyatakan dalam Senarai Semak Permohonan myGAP 1-3.</b><br/><span style="margin-left:14px;font-size:12px;font-style:italic;color:#555;">(Relevant documents as specify in the checklist of myGAP 1-3 Application)</span></div>
          </div>
        </td></tr>
      </table>

      <table style="width:100%;border-collapse:collapse;${bd}margin-top:10px;">
        <tr><td style="${bd}padding:0 8px 10px;background:#d9d9d9;font-weight:bold;font-size:16px;width:35px;text-align:center;vertical-align:middle;">G</td><td colspan="5" style="${bd}padding:0 8px 10px;background:#d9d9d9;font-weight:bold;font-size:15px;text-align:center;">UNTUK KEGUNAAN PEJABAT URUS SETIA PENSIJILAN<br/><i style="font-weight:normal;font-size:12px;">FOR OFFICIAL USE</i></td></tr>
        <tr><td colspan="3" style="${bd}padding:0 10px 10px;font-size:15px;line-height:1.0;"><div style="line-height:1.35;"><b>[1] Tarikh Daftar :</b></div><div style="line-height:1.3;"><i style="font-size:12px;color:#555;">(Date registered)</i></div></td><td colspan="3" style="${bd}padding:0 10px 10px;font-size:15px;">&nbsp;</td></tr>
        <tr><td colspan="3" style="${bd}padding:0 10px 10px;font-size:15px;line-height:1.0;"><div style="line-height:1.35;"><b>[2] No. Pendaftaran :</b></div><div style="line-height:1.3;"><i style="font-size:12px;color:#555;">(Registration No.)</i></div></td><td colspan="3" style="${bd}padding:0 10px 10px;font-size:15px;">myGAP.</td></tr>
        <tr><td colspan="3" style="${bd}padding:0 10px 10px;font-size:15px;line-height:1.0;"><div style="line-height:1.35;"><b>[3] Tandatangan dan Cop :</b></div><div style="line-height:1.3;"><i style="font-size:12px;color:#555;">(Signature and stamp)</i></div></td><td colspan="3" style="${bd}padding:0 10px 10px;font-size:15px;min-height:70px;">&nbsp;</td></tr>
      </table>
      <div style="margin-top:28px;text-align:center;font-size:15px;line-height:1.8;">
        <div style="font-weight:bold;">Sebarang pertanyaan, sila hubungi :</div>
        <div style="font-size:12px;font-style:italic;color:#555;">For further information, please contact</div>
        <div style="margin-top:10px;font-weight:bold;">Urus setia</div>
        <div>Seksyen Pensijilan Ladang</div>
        <div>Bahagian Kawalan Kualiti Tanaman, Jabatan Pertanian</div>
        <div>Aras 7, Lot 4G2, Wisma Tani, No. 30 Persiaran Perdana, Presint 4</div>
        <div>62624 Putrajaya</div>
        <div><b>No. Tel: 03-8870 3000 /03-8870 3448</b></div>
        <div><b>No. Faks: 03-8888 7639</b></div>
        <div><b>E-mail: mygapmyorganic@gmail.com</b></div>
      </div>

      <div class="pdf-page-break" style="page-break-before:always;"></div>
      <div style="text-align:right;font-size:15px;font-weight:bold;text-decoration:underline;margin-top:28px;">Lampiran 1</div>
      <div style="text-align:center;font-size:15px;font-weight:bold;margin:12px 0;">Sila sertakan salinan dokumen berikut;</div>
      <table style="width:100%;border-collapse:collapse;${bd}font-size:15px;">
        <tr><td style="${bd}padding:4px 4px 10px;text-align:center;font-weight:bold;width:35px;">Bil</td><td style="${bd}padding:4px 4px 10px;text-align:center;font-weight:bold;">Perkara</td><td style="${bd}padding:4px 4px 10px;text-align:center;font-weight:bold;width:70px;">Tindakan<br/>Pemohon<br/>(/)</td></tr>
        <tr><td style="${bd}padding:4px 4px 10px;text-align:center;font-weight:bold;">1.</td><td style="${bd}padding:4px 4px 10px;"><b>Kad Pengenalan/Sijil Daftar Syarikat (SSM)</b></td><td style="${bd}padding:4px 4px 10px;text-align:center;font-weight:bold;font-size:18px;">${tickIcSsm}</td></tr>
        <tr><td style="${bd}padding:4px 4px 10px;text-align:center;font-weight:bold;">2.</td><td style="${bd}padding:4px 4px 10px;">
          <b>Bukti status tanah yang diusahakan;</b><br/>
          <div style="padding-left:14px;line-height:1.6;margin-top:4px;">
            i. &nbsp; Tanah Hak Milik Sendiri - <b>Bukti</b> Salinan Geran Hak Milik Sendiri.<br/><br/>
            ii. &nbsp; Tanah Sewa/Pajak - <b>Bukti</b> Salinan Perjanjian Sewa/Pajak Tanah Beserta Salinan Geran Pemilik <b>atau</b> Surat Kebenaran yang masih sah laku berserta Salinan Geran Pemilik.<br/><br/>
            iii. &nbsp; Tanah LPS (TOL) - <b>Bukti</b> Salinan Lesen Pendudukan Sementara yang sah laku/Surat Permohonan dalam Proses Pembaharuan LPS daripada Pejabat Tanah.<br/><br/>
            iv. &nbsp; TKPM - <b>Bukti</b> Salinan Surat Pengesahan Peserta TKPM/ Surat Perjanjian Peserta dengan Jabatan berserta Pelan Lot TKPM Keseluruhan.<br/><br/>
            v. &nbsp; Tanah di Pusat Pertanian atau Stesen &ndash; <b>Bukti</b> Surat Persetujuan Daripada Pengarah Bahagian/Negeri berserta Pelan Lot Stesen/Pusat Keseluruhan.<br/><br/>
            vi. &nbsp; Tanah Kerajaan atau Badan Berkanun &ndash; <b>Bukti</b> Surat Kebenaran daripada Ketua/Pengurus Agensi Berkenaan berserta Pelan Lot tanah yang diusahakan.<br/><br/>
            <div style="margin-top:4px;">Permohonan yang melebihi daripada satu lot perlu berada dalam lingkungan 1 km radius. Jika jarak lot tersebut melebihi dari 1 km radius, perlu mengemukakan permohonan yang berasingan.</div>
          </div>
        </td><td style="${bd}padding:4px 4px 10px;text-align:center;font-weight:bold;font-size:18px;">${tickLand}</td></tr>
        <tr><td style="${bd}padding:4px 4px 10px;text-align:center;font-weight:bold;">3.</td><td style="${bd}padding:4px 4px 10px;"><b>Ladang telah/akan mengeluarkan hasil dalam masa 6 bulan.</b></td><td style="${bd}padding:4px 4px 10px;text-align:center;font-weight:bold;font-size:18px;">${tickHarvest}</td></tr>
        <tr><td style="${bd}padding:4px 4px 10px;text-align:center;font-weight:bold;">4.</td><td style="${bd}padding:4px 4px 10px;"><b>Pelan Blok Tanaman hendaklah dilampirkan.</b></td><td style="${bd}padding:4px 4px 10px;text-align:center;font-weight:bold;font-size:18px;">${tickLayout}</td></tr>
        <tr><td style="${bd}padding:4px 4px 10px;text-align:center;font-weight:bold;">5.</td><td style="${bd}padding:4px 4px 10px;">
          <b>Bagi kawasan yang dibangunkan pada atau selepas 2015, sertakan Surat Kelulusan EIA Jadual 1 daripada Jabatan Alam Sekitar sekiranya:</b><br/>
          <div style="padding-left:14px;line-height:1.6;margin-top:4px;">
            a. &nbsp; Pembangunan tanah hutan yang meliputi kawasan seluas &ge;20 hektar tetapi kurang daripada 500 hektar untuk dijadikan sebagai kawasan pengeluaran pertanian*. Sekiranya &ge;500 hektar perlu menyediakan Laporan EIA mengikut Jadual 2*.<br/><br/>
            b. &nbsp; Pembangunan estet pertanian (kelapa sawit, getah dan tanaman perladangan) yang meliputi kawasan seluas &ge;500 hektar yang melibatkan perubahan dalam jenis kegunaan pertanian.<br/><br/>
            c. &nbsp; Pengubahan hutan pada ketinggian &ge;300 meter atas paras laut kepada kegunaan lain tanah yang meliputi kawasan pertanian seluas &ge;20 hektar tetapi kurang daripada 100 hektar. Sekiranya &ge;100 hektar perlu menyediakan Laporan EIA mengikut Jadual 2*<br/><br/>
            d. &nbsp; Pengubahan kawasan;<br/>
            <div style="padding-left:14px;">
              (i) &nbsp; hutan paya bakau;<br/>
              (ii) &nbsp; hutan paya gambut; atau<br/>
              (iii) &nbsp; hutan paya air tawar,<br/>
            </div>
            <div style="margin-left:16px;margin-top:4px;">bagi kegunaan perindustrian, perumahan atau <b>pertanian</b> yang meliputi kawasan seluas &ge;20 hektar tetapi kurang daripada 50 hektar. Sekiranya &ge;50 hektar perlu menyediakan Laporan EIA mengikut Jadual 2*</div><br/>
            e. &nbsp; Pembangunan atau pembersihan tanah yang meliputi kawasan yang kurang daripada 50% kawasan cerun yang berkecerunan &ge;25&deg; tetapi kurang daripada 35&deg;. Sekiranya &ge;50% perlu menyediakan Laporan EIA mengikut Jadual 2*
          </div>
          <div style="margin-top:10px;font-size:8px;line-height:1.5;">
            *Mengikut Perintah Kualiti Alam Sekeliling (Aktiviti Yang Ditetapkan) Penilaian Kesan kepada Alam Sekeliling 2015. <i>(jika berkenaan)</i>
          </div>
        </td><td style="${bd}padding:4px 4px 10px;text-align:center;font-weight:bold;font-size:18px;">${tickEia}</td></tr>
      </table>
      </div>`;
      pages.push(form11Html);
    }

    // --- 4-1 to 4-8: Farm Record sections ---
    if (exportMode === 'farm-record' || exportMode === 'all') {
    const rows41 = [];
    const weights41 = [];
    soilActivities.forEach((act) => {
      const problem = getNoteVal('4-1', soilNoteKey(act, 'problem'), sopDescription(act));
      rows41.push(`<tr>
        <td style="${pdfTdStyle}">${formatDateShort(act.date_activity || act.actual_date || act.planned_date)}</td>
        <td style="${pdfTdStyle}">${escHtml(problem)}</td>
        <td style="${pdfTdStyle}">${escHtml(processLabel(act))}</td>
        <td style="${pdfTdStyle}">${escHtml(act.name_material || act.material_name || '')}</td>
        <td style="${pdfTdStyle}">${escHtml(sopDosageText(act) || act.quantity || '')}</td>
        <td style="${pdfTdStyle}">${escHtml(getNoteVal('4-1', soilNoteKey(act, 'period')))}</td>
        <td style="${pdfTdStyle}">${escHtml(farmLocation)}</td>
        <td style="${pdfTdStyle}">${escHtml(operatorNames)}</td>
      </tr>`);
      // Rows the old matcher listed paginate as before; the newly-added media /
      // substrate rows carry long drip instructions, so weight them by length.
      weights41.push(legacySoilActivities.includes(act) ? 1 : pdfRowWeight(problem));
    });
    addSection('4-1', 'PEMULIHARAAN DAN RAWATAN TANAH / SUBSTRAT',
      `<tr><th style="${pdfThStyle}">Tarikh</th><th style="${pdfThStyle}">Masalah Tanah / Substrat</th><th style="${pdfThStyle}">Kaedah Pemuliharaan / Rawatan</th><th style="${pdfThStyle}">Nama Bahan/Peralatan</th><th style="${pdfThStyle}">Kuantiti Bahan / Peralatan</th><th style="${pdfThStyle}">Tempoh Dilarang Menanam</th><th style="${pdfThStyle}">Blok/Lokasi</th><th style="${pdfThStyle}">Nama Operator</th></tr>`,
      rows41, 8, weights41
    );

    // --- 4-2: Plant Material ---
    const rows42 = [];
    const allPlantItems = [];
    if (selectedCycle) {
      // Usage date / quantity come from the cycle's Completed Transplanting
      // (date may fall back to Seeding) SOP activity; otherwise cycle start and 0.
      allPlantItems.push({ date: plantUseDate, name: selectedCycle.produceName, isCycle: true });
    }
    seedItems.forEach(item => allPlantItems.push({ ...item, isCycle: false }));
    allPlantItems.forEach((item, idx) => {
      if (item.isCycle) {
        rows42.push(`<tr>
          <td style="${pdfTdStyle}">${formatDateShort(item.date)}</td>
          <td style="${pdfTdStyle}">${escHtml(item.name || '')}</td>
          <td style="${pdfTdStyle}"></td><td style="${pdfTdStyle}"></td>
          <td style="${pdfTdStyle}">${escHtml(plantUseQty)}</td>
          <td style="${pdfTdStyle}">${escHtml(farmLocation)}</td>
          <td style="${pdfTdStyle}">${escHtml(getNoteVal('4-2', 'justifikasi_0', plantJustDefault))}</td><td style="${pdfTdStyle}"></td><td style="${pdfTdStyle}"></td>
          <td style="${pdfTdStyle}">${escHtml(operatorNames)}</td>
        </tr>`);
      } else {
        const fin = findFinTxnForInv(item);
        const justDef = fin?.description || (item.material_name ? `Pembelian ${item.material_name}` : '');
        rows42.push(`<tr>
          <td style="${pdfTdStyle}">${formatDateShort(item.date_transact || item.created_at)}</td>
          <td style="${pdfTdStyle}">${escHtml(item.material_name || item.inventory_name || '')}</td>
          <td style="${pdfTdStyle}">${item.is_in ? escHtml(item.amount || '') : ''}</td>
          <td style="${pdfTdStyle}">${item.is_in && item.price ? 'RM ' + parseFloat(item.price).toFixed(2) : ''}</td>
          <td style="${pdfTdStyle}">${!item.is_in ? escHtml(item.amount || '0') : '0'}</td>
          <td style="${pdfTdStyle}">${escHtml(farmLocation)}</td>
          <td style="${pdfTdStyle}">${escHtml(getNoteVal('4-2', `justifikasi_${selectedCycle ? idx : idx + 1}`, justDef))}</td><td style="${pdfTdStyle}"></td><td style="${pdfTdStyle}"></td>
          <td style="${pdfTdStyle}">${escHtml(supplierFor(item) || operatorNames)}</td>
        </tr>`);
      }
    });
    addSection('4-2', 'PEMBELIAN, PENGGUNAAN DAN RAWATAN BAHAN TANAMAN',
      `<tr><th style="${pdfThStyle}" rowspan="2">Tarikh</th><th style="${pdfThStyle}" rowspan="2">Jenis Varieti / Pokok Induk / Bahan Tanaman</th><th style="${pdfThStyle}" colspan="2">Pembelian</th><th style="${pdfThStyle}" colspan="2">Penggunaan</th><th style="${pdfThStyle}" colspan="3">Rawatan Bahan Tanaman</th><th style="${pdfThStyle}" rowspan="2">Nama Pembekal Benih / Operator</th></tr>
      <tr><th style="${pdfThSubStyle}">Kuantiti</th><th style="${pdfThSubStyle}">Harga</th><th style="${pdfThSubStyle}">Kuantiti</th><th style="${pdfThSubStyle}">Blok/Luas</th><th style="${pdfThSubStyle}">Justifikasi</th><th style="${pdfThSubStyle}">Bahan</th><th style="${pdfThSubStyle}">Kadar</th></tr>`,
      rows42, 10
    );

    // --- 4-3: Fertilizer ---
    const rows43 = [];
    if (fertilizerItems.length > 0) {
      const fertGroups = {};
      fertilizerItems.forEach(item => {
        const rawName = item.material_name || item.inventory_name || 'Unknown';
        const key = rawName.toLowerCase().trim().replace(/\s*\(.*?\)\s*$/, '').trim();
        if (!fertGroups[key]) fertGroups[key] = { name: rawName, dates: [], totalQty: 0, totalPrice: 0, unit: item.unit_symbol || item.unit_name || 'kg', supplier: item.supplier_name || '', items: [] };
        // Keep the longest (most detailed) name for display
        if (rawName.length > fertGroups[key].name.length) fertGroups[key].name = rawName;
        fertGroups[key].dates.push(formatDateShort(item.date_transact || item.created_at));
        fertGroups[key].totalQty += parseFloat(item.amount || 0);
        if (item.price) fertGroups[key].totalPrice += parseFloat(item.price || 0);
        fertGroups[key].items.push(item);
      });
      Object.values(fertGroups).forEach((grp, gi) => {
        const dateStr = [...new Set(grp.dates.filter(d => d !== '-'))].join(', ');
        const firstItem = grp.items[0];
        const supplier = grp.items.find(i => i.supplier_name)?.supplier_name || supplierFor(firstItem) || '';
        const npkMatch = grp.name.match(/(\d{1,3}[-:\s]\d{1,3}[-:\s]\d{1,3})/);
        const ureaMatch = grp.name.match(/urea\s*(\d{1,3})\s*%?/i);
        const formulaDef = npkMatch ? `NPK ${npkMatch[1]}` : ureaMatch ? `Urea ${ureaMatch[1]}%` : '';
        const relAct = relActForInv(firstItem, grp.name);
        const caraDef = relAct ? [toolFor(relAct), sopDescription(relAct)].filter(Boolean).join(' — ') : '';
        rows43.push(`<tr>
          <td style="${pdfTdStyle}">${dateStr || '-'}</td>
          <td style="${pdfTdStyle}">${escHtml(grp.name)}</td>
          <td style="${pdfTdStyle}">${escHtml(getNoteVal('4-3', `formulasi_${gi}`, formulaDef))}</td>
          <td style="${pdfTdStyle}">${grp.items.filter(i => i.is_in).reduce((s, i) => s + parseFloat(i.amount || 0), 0) || ''} ${escHtml(grp.unit)}</td>
          <td style="${pdfTdStyle}">${grp.totalPrice > 0 ? 'RM ' + grp.totalPrice.toLocaleString('en-MY', { minimumFractionDigits: 2 }) : ''}</td>
          <td style="${pdfTdStyle}">${escHtml(supplier)}</td>
          <td style="${pdfTdStyle}">${grp.totalQty} ${escHtml(grp.unit)}</td>
          <td style="${pdfTdStyle}">${escHtml(getNoteVal('4-3', `cara_${gi}`, caraDef))}</td>
          <td style="${pdfTdStyle}">${escHtml(farmLocation)}</td>
          <td style="${pdfTdStyle}">${escHtml(farmSizeText)}</td>
          <td style="${pdfTdStyle}">${escHtml(operatorNames)}</td>
        </tr>`);
      });
    }
    const weights43 = rows43.map(() => 1);
    // Completed SOP fertiliser / foliar / media / substrate applications,
    // oldest first — same rows, keys and defaults as the on-screen table.
    sopFertRows43.forEach(act => {
      const cara = getNoteVal('4-3', `sop_${act.id}_cara`, sopCaraDefault(act));
      rows43.push(`<tr>
          <td style="${pdfTdStyle}">${formatDateShort(act.date_activity)}</td>
          <td style="${pdfTdStyle}">${escHtml(act.name_material || '')}</td>
          <td style="${pdfTdStyle}">${escHtml(getNoteVal('4-3', `sop_${act.id}_formulasi`, sopFormulaDefault(act)))}</td>
          <td style="${pdfTdStyle}">-</td>
          <td style="${pdfTdStyle}">-</td>
          <td style="${pdfTdStyle}">-</td>
          <td style="${pdfTdStyle}">${escHtml(sopDosageText(act))}</td>
          <td style="${pdfTdStyle}">${escHtml(cara)}</td>
          <td style="${pdfTdStyle}">${escHtml(farmLocation)}</td>
          <td style="${pdfTdStyle}">${escHtml(farmSizeText)}</td>
          <td style="${pdfTdStyle}">${escHtml(operatorNames)}</td>
        </tr>`);
      weights43.push(pdfRowWeight(cara));
    });
    addSection('4-3', 'PEMBELIAN DAN PENGGUNAAN BAJA, SUBSTRAT* DAN BAHAN LAIN**',
      `<tr><th style="${pdfThStyle}" rowspan="2">Tarikh Beli / Guna</th><th style="${pdfThStyle}" rowspan="2">Jenis Bahan</th><th style="${pdfThStyle}" rowspan="2">Formulasi</th><th style="${pdfThStyle}" colspan="3">Pembelian</th><th style="${pdfThStyle}" colspan="5">Penggunaan</th></tr>
      <tr><th style="${pdfThSubStyle}">Kuantiti</th><th style="${pdfThSubStyle}">Harga</th><th style="${pdfThSubStyle}">Pembekal</th><th style="${pdfThSubStyle}">Kuantiti</th><th style="${pdfThSubStyle}">Cara Penggunaan</th><th style="${pdfThSubStyle}">Lokasi/ Blok</th><th style="${pdfThSubStyle}">Keluasan</th><th style="${pdfThSubStyle}">Operator</th></tr>`,
      rows43, 11, weights43
    );

    // --- 4-4: Pest Surveillance ---
    const rows44 = [];
    const pestGroups44 = {};
    pesticideItems.forEach(item => {
      const rawName = item.material_name || item.inventory_name || 'Unknown';
      const key = rawName.toLowerCase().trim().replace(/\s*\(.*?\)\s*$/, '').trim();
      if (!pestGroups44[key]) pestGroups44[key] = { name: rawName, dates: [], items: [] };
      if (rawName.length > pestGroups44[key].name.length) pestGroups44[key].name = rawName;
      pestGroups44[key].dates.push(formatDateShort(item.date_transact || item.created_at));
      pestGroups44[key].items.push(item);
    });
    // Include both pestActivities (SOP) and grouped pesticide purchases.
    pestActivities.forEach((act, idx) => {
      const desc = sopDescription(act);
      // Pest name: explicit "Perosak: X", else the SOP target ("Serangga - X" → X,
      // "Fungicide/Kulat" → "Kulat (penyakit)"). Damage: only an explicit
      // "Kerosakan: N%" — never the active ingredient's strength ("18.3%").
      rows44.push(`<tr>
        <td style="${pdfTdStyle}">${formatDateShort(act.date_activity || act.actual_date || act.planned_date)}</td>
        <td style="${pdfTdStyle}">${escHtml(getNoteVal('4-4', `cara_${idx}`, desc))}</td>
        <td style="${pdfTdStyle}">${escHtml(farmLocation)}</td>
        <td style="${pdfTdStyle}">${escHtml(getNoteVal('4-4', `perosak_${idx}`, pestNameDefault(desc)))}</td>
        <td style="${pdfTdStyle}">${escHtml(getNoteVal('4-4', `kerosakan_${idx}`, damageDefault(desc)))}</td>
        <td style="${pdfTdStyle}">${escHtml([processLabel(act), act.name_material].filter(Boolean).join(' — '))}</td>
        <td style="${pdfTdStyle}">${escHtml(operatorNames)}</td>
      </tr>`);
    });
    Object.values(pestGroups44).forEach((grp, gi) => {
      const dateStr = [...new Set(grp.dates.filter(d => d !== '-'))].join(', ');
      const fin = findFinTxnForInv(grp.items[0]);
      const caraDef = fin?.description || (grp.name ? `Sembur ${grp.name}` : '');
      rows44.push(`<tr>
        <td style="${pdfTdStyle}">${dateStr}</td>
        <td style="${pdfTdStyle}">${escHtml(getNoteVal('4-4', `cara_inv_${gi}`, caraDef))}</td>
        <td style="${pdfTdStyle}">${escHtml(farmLocation)}</td>
        <td style="${pdfTdStyle}">${escHtml(getNoteVal('4-4', `perosak_inv_${gi}`))}</td>
        <td style="${pdfTdStyle}">${escHtml(getNoteVal('4-4', `kerosakan_inv_${gi}`))}</td>
        <td style="${pdfTdStyle}">${escHtml(grp.name)}</td>
        <td style="${pdfTdStyle}">${escHtml(operatorNames)}</td>
      </tr>`);
    });
    addSection('4-4', 'PENGAWASAN DAN KAWALAN PEROSAK',
      `<tr><th style="${pdfThStyle}">Tarikh</th><th style="${pdfThStyle}">Cara Pengawasan</th><th style="${pdfThStyle}">Lokasi / Blok</th><th style="${pdfThStyle}">Nama Perosak</th><th style="${pdfThStyle}">Bil. Perosak / % Kerosakan</th><th style="${pdfThStyle}">Kaedah Kawalan</th><th style="${pdfThStyle}">Nama Operator</th></tr>`,
      rows44, 7
    );

    // --- 4-5: Pesticide Purchase & Usage ---
    // Inventory rows, then completed SOP sprays (oldest first) — the same
    // pest45Rows list, keys and defaults as on screen.
    const rows45 = [];
    pest45Rows.forEach(r => {
      const note = (col) => escHtml(getNoteVal('4-5', r.fields[col], r.defaults[col]));
      if (r.kind === 'inv') {
        const { item, relAct, info } = r;
        const matName = item.material_name || item.inventory_name || '';
        rows45.push(`<tr>
        <td style="${pdfTdStyle}">${formatDateShort(item.date_transact || item.created_at)}</td>
        <td style="${pdfTdStyle}">${escHtml(selectedCycle?.produceName || '')}</td>
        <td style="${pdfTdStyle}">${escHtml(farmLocation)}</td>
        <td style="${pdfTdStyle}">${escHtml(tradeAndActive(matName, info))}</td>
        <td style="${pdfTdStyle}">${note('lrmp')}</td>
        <td style="${pdfTdStyle}">${item.price ? 'RM ' + parseFloat(item.price).toFixed(2) : ''}</td>
        <td style="${pdfTdStyle}">${escHtml(item.amount ? item.amount + ' ' + (item.unit_name || '') : sopDosageText(relAct))}</td>
        <td style="${pdfTdStyle}">${note('justifikasi')}</td>
        <td style="${pdfTdStyle}">${note('alat')}</td>
        <td style="${pdfTdStyle}">${escHtml(operatorNames)}</td>
        <td style="${pdfTdStyle}">${note('phi')}</td>
        <td style="${pdfTdStyle}">${note('rei')}</td>
      </tr>`);
      } else {
        const { act, info } = r;
        rows45.push(`<tr>
        <td style="${pdfTdStyle}">${formatDateShort(act.date_activity)}</td>
        <td style="${pdfTdStyle}">${escHtml(selectedCycle?.produceName || act.produce_name || '')}</td>
        <td style="${pdfTdStyle}">${escHtml(farmLocation)}</td>
        <td style="${pdfTdStyle}">${escHtml(tradeAndActive(act.name_material, info))}</td>
        <td style="${pdfTdStyle}">${note('lrmp')}</td>
        <td style="${pdfTdStyle}">-</td>
        <td style="${pdfTdStyle}">${escHtml(sopDosageText(act))}</td>
        <td style="${pdfTdStyle}">${note('justifikasi')}</td>
        <td style="${pdfTdStyle}">${note('alat')}</td>
        <td style="${pdfTdStyle}">${escHtml(operatorNames)}</td>
        <td style="${pdfTdStyle}">${note('phi')}</td>
        <td style="${pdfTdStyle}">${note('rei')}</td>
      </tr>`);
      }
    });
    addSection('4-5', 'PEMBELIAN & PENGGUNAAN RACUN PEROSAK / BAHAN KIMIA',
      `<tr><th style="${pdfThStyle}">Tarikh</th><th style="${pdfThStyle}">Tanaman</th><th style="${pdfThStyle}">Lokasi & Keluasan</th><th style="${pdfThStyle}">Nama Dagangan & Bahan Aktif</th><th style="${pdfThStyle}">No. LRMP</th><th style="${pdfThStyle}">Harga</th><th style="${pdfThStyle}">Kuantiti Pembelian / Kadar Penggunaan</th><th style="${pdfThStyle}">Justifikasi Penggunaan</th><th style="${pdfThStyle}">Alat Yang Digunakan</th><th style="${pdfThStyle}">Nama Operator</th><th style="${pdfThStyle}">PHI</th><th style="${pdfThStyle}">REI</th></tr>`,
      rows45, 12
    );

    // --- 4-6: Production & Sales ---
    const rows46 = [];
    productionSales.forEach((y, idx) => {
      const amount = parseFloat(y.yield_transaction_amount || 0);
      const price = parseFloat(y.price_per_unit || 0);
      const total = parseFloat(y.order_amount_total || 0) || (amount * price);
      const produceName = y.produce_name_malay || y.produce_name || selectedCycle?.produceName || '';
      // NO. INVOIS / NAMA PEMBELI (WI-20): the typed note wins over order_id /
      // the resolved buyer — same keys and defaults as the screen.
      const k46 = row46Key(y, idx);
      const d46 = row46Defaults(y, idx);
      rows46.push(`<tr>
        <td style="${pdfTdStyle}">${formatDateShort(y.yield_transaction_date)}</td>
        <td style="${pdfTdStyle}">${escHtml(produceName)}${y.grade_name ? ' Gred ' + escHtml(y.grade_name) : ''}</td>
        <td style="${pdfTdStyle}">${escHtml(farmLocation)}</td>
        <td style="${pdfTdStyle}">${y.yield_in ? amount.toLocaleString() : ''}</td>
        <td style="${pdfTdStyle}">${!y.yield_in ? amount.toLocaleString() : ''}</td>
        <td style="${pdfTdStyle}">${price > 0 ? 'RM ' + price.toFixed(2) : ''}</td>
        <td style="${pdfTdStyle}">${total > 0 ? 'RM ' + total.toLocaleString('en-MY', { minimumFractionDigits: 2 }) : ''}</td>
        <td style="${pdfTdStyle}">${escHtml(getNoteVal('4-6', `invois_${k46}`, d46.invois))}</td>
        <td style="${pdfTdStyle}">${escHtml(getNoteVal('4-6', `pembeli_${k46}`, d46.pembeli))}</td>
      </tr>`);
    });
    addSection('4-6', 'PENGELUARAN DAN PENJUALAN HASIL',
      `<tr><th style="${pdfThStyle}" rowspan="2">Tarikh</th><th style="${pdfThStyle}" rowspan="2">Jenis Tanaman</th><th style="${pdfThStyle}" rowspan="2">Blok / Lokasi</th><th style="${pdfThStyle}" colspan="2">Jumlah Hasil (kg @ unit)</th><th style="${pdfThStyle}" rowspan="2">Harga Jualan/kg @ unit (RM)</th><th style="${pdfThStyle}" rowspan="2">Jumlah Harga (RM)</th><th style="${pdfThStyle}" rowspan="2">No. Invois</th><th style="${pdfThStyle}" rowspan="2">Nama Pembeli</th></tr>
      <tr><th style="${pdfThSubStyle}">Tuai</th><th style="${pdfThSubStyle}">Jual</th></tr>`,
      rows46, 9
    );

    // --- 4-7: Post-Harvest ---
    const rows47 = [];
    postHarvestActivities.forEach((act, idx) => {
      rows47.push(`<tr>
        <td style="${pdfTdStyle}">${formatDateShort(act.date_activity || act.actual_date || act.planned_date)}</td>
        <td style="${pdfTdStyle}">${escHtml(selectedCycle?.produceName || '')}</td>
        <td style="${pdfTdStyle}">${escHtml(farmLocation)}</td>
        <td style="${pdfTdStyle}">${escHtml(getNoteVal('4-7', `justifikasi_${idx}`, sopDescription(act)))}</td>
        <td style="${pdfTdStyle}">${escHtml(processLabel(act))}</td>
        <td style="${pdfTdStyle}">${escHtml(getNoteVal('4-7', `bahan_${idx}`, sopBahanKadar(act)))}</td>
        <td style="${pdfTdStyle}">${escHtml(getNoteVal('4-7', `tempoh_${idx}`))}</td>
        <td style="${pdfTdStyle}">${escHtml(operatorNames)}</td>
      </tr>`);
    });
    addSection('4-7', 'RAWATAN PASCA TUAI',
      `<tr><th style="${pdfThStyle}">Tarikh</th><th style="${pdfThStyle}">Jenis Tanaman</th><th style="${pdfThStyle}">Blok / Lokasi</th><th style="${pdfThStyle}">Justifikasi Rawatan</th><th style="${pdfThStyle}">Jenis Rawatan</th><th style="${pdfThStyle}">Bahan Aktif dan Kadar</th><th style="${pdfThStyle}">Tempoh Rawatan</th><th style="${pdfThStyle}">Nama Operator</th></tr>`,
      rows47, 8
    );

    // --- 4-8: Workers ---
    const rows48 = [];
    employees.forEach((emp, idx) => {
      const name = emp.full_name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || '';
      const empTraining = trainingData.filter(t => t.user_id === emp.user_id);
      const trainingDate = empTraining.length > 0 ? empTraining.map(t => formatDateShort(t.date_training)).join(', ') : getNoteVal('4-8', 'latihan_tarikh_' + idx);
      const trainingName = empTraining.length > 0 ? empTraining.map(t => t.description).join(', ') : getNoteVal('4-8', 'latihan_nama_' + idx);
      const trainingOrganiser = empTraining.length > 0 ? empTraining.map(t => t.organiser).filter(Boolean).join(', ') : getNoteVal('4-8', 'latihan_penganjur_' + idx);
      // Jantina: same rule as the screen cell (note, else emp.gender).
      rows48.push(`<tr>
        <td style="${pdfTdStyle}">${escHtml(name)}</td>
        <td style="${pdfTdStyle}">${emp.birthdate ? formatDateShort(emp.birthdate) : ''}</td>
        <td style="${pdfTdStyle}">${escHtml(emp.identification_no || emp.ic_number || '')}</td>
        <td style="${pdfTdStyle}">${escHtml(getNoteVal('4-8', 'jantina_' + idx, emp.gender || ''))}</td>
        <td style="${pdfTdStyle}">${escHtml(emp.nationality || emp.country || 'Malaysia')}</td>
        <td style="${pdfTdStyle}">${escHtml(trainingDate)}</td>
        <td style="${pdfTdStyle}">${escHtml(trainingName)}</td>
        <td style="${pdfTdStyle}">${escHtml(trainingOrganiser)}</td>
        <td style="${pdfTdStyle}">${escHtml(getNoteVal('4-8', 'kesihatan_tarikh_' + idx))}</td>
        <td style="${pdfTdStyle}">${escHtml(getNoteVal('4-8', 'kesihatan_klinik_' + idx))}</td>
        <td style="${pdfTdStyle}">${escHtml(getNoteVal('4-8', 'kesihatan_nota_' + idx))}</td>
      </tr>`);
    });
    addSection('4-8', 'PENGURUSAN PEKERJA',
      `<tr><th style="${pdfThStyle}" rowspan="2">Nama Pekerja</th><th style="${pdfThStyle}" rowspan="2">Tarikh Lahir</th><th style="${pdfThStyle}" rowspan="2">Kad Pengenalan / Passport</th><th style="${pdfThStyle}" rowspan="2">Jantina</th><th style="${pdfThStyle}" rowspan="2">Kewarganegaraan</th><th style="${pdfThStyle}" colspan="3">Rekod Latihan</th><th style="${pdfThStyle}" colspan="3">Rekod Kesihatan</th></tr>
      <tr><th style="${pdfThSubStyle}">Tarikh</th><th style="${pdfThSubStyle}">Latihan</th><th style="${pdfThSubStyle}">Penganjur</th><th style="${pdfThSubStyle}">Tarikh</th><th style="${pdfThSubStyle}">Klinik / Hospital</th><th style="${pdfThSubStyle}">Nota</th></tr>`,
      rows48, 11
    );
    } // end farm-record sections

    return pages.join('');
  };

  // Export PDF handler
  const handleExportPDF = async () => {
    setExporting(true);
    try {
      // Preload logos as base64 data URLs
      const [cropLogo, mygapLogo, footerLogo] = await Promise.all([
        imageToBase64('/kapitani-crop-logo.png'),
        imageToBase64('/mygap-logo.png'),
        imageToBase64('/kapitani-footer.png')
      ]);
      const logos = { crop: cropLogo, mygap: mygapLogo, footer: footerLogo };
      const today = new Date();
      const dateStr = `${today.getDate()} ${today.toLocaleString('en', { month: 'short' })} ${today.getFullYear()}`;

      // Attachments that could not be merged (or may render badly) — shown to
      // the user after the download instead of being dropped silently.
      const problems = [];

      const generatePdf = async (html, orientation, containerWidth, fname, addHeaderFooter, avoidRows, attachments = []) => {
        const wrapper = document.createElement('div');
        wrapper.className = 'mygap-pdf-wrapper';
        wrapper.style.cssText = 'position:fixed;left:0;top:0;width:0;height:0;overflow:hidden;z-index:-1;';
        const container = document.createElement('div');
        container.style.cssText = `width:${containerWidth}px;background:white;`;
        container.innerHTML = html;
        wrapper.appendChild(container);
        document.body.appendChild(wrapper);
        try {
          await new Promise(r => setTimeout(r, 100));
          const opt = {
            margin: orientation === 'portrait' ? [14, 10, 14, 10] : [5, 8, 5, 8],
            filename: fname,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true, letterRendering: true, scrollX: 0, scrollY: 0, windowWidth: containerWidth },
            jsPDF: { unit: 'mm', format: 'a4', orientation },
            // avoidRows: keep table rows/cells whole so html2pdf never slices
            // through the middle of a row when auto-paginating (Form 1-1).
            pagebreak: avoidRows
              ? { mode: ['css', 'legacy'], before: '.pdf-page-break', avoid: 'tr' }
              : { mode: ['css', 'legacy'], before: '.pdf-page-break' }
          };
          // We need the jsPDF instance whenever we add a header/footer OR have
          // attachments to merge. get('pdf') resolves to the instance directly
          // (chaining .then(cb=>cb) on html2pdf's worker returns the worker, not
          // your value).
          if (addHeaderFooter || (attachments && attachments.length)) {
            const worker = html2pdf().set(opt).from(container).toPdf();
            const pdf = await worker.get('pdf');
            if (addHeaderFooter) {
              const totalPages = pdf.internal.getNumberOfPages();
              const pageWidth = pdf.internal.pageSize.getWidth();
              const pageHeight = pdf.internal.pageSize.getHeight();
              for (let i = 1; i <= totalPages; i++) {
                pdf.setPage(i);
                // Header: "myGAP 1-1" top right
                pdf.setFontSize(10);
                pdf.setFont('helvetica', 'bold');
                pdf.text('myGAP 1-1', pageWidth - 12, 9, { align: 'right' });
                // Footer: versioning (left) and page number (right)
                pdf.setFontSize(7);
                pdf.setFont('helvetica', 'italic');
                pdf.text('myGAP 1-1 (Pindaan 1/2022)', 12, pageHeight - 6);
                pdf.text(`${i} / ${totalPages}`, pageWidth - 12, pageHeight - 6, { align: 'right' });
              }
            }
            // Append supplementary documents (Lampiran 1) into the same PDF, if any.
            if (attachments && attachments.length) {
              try {
                const mergedBytes = await mergeAttachmentsIntoPdf(pdf.output('arraybuffer'), attachments, problems);
                downloadPdfBytes(mergedBytes, fname);
              } catch (mErr) {
                console.warn('Attachment merge failed; saving form only:', mErr);
                problems.push({ kind: 'skip', label: 'Semua lampiran', name: '', reason: `penggabungan gagal, hanya borang disimpan (${mErr?.message || mErr})` });
                pdf.save(fname);
              }
            } else {
              pdf.save(fname);
            }
          } else {
            await html2pdf().set(opt).from(container).save();
          }
        } finally {
          // Always remove the offscreen wrapper, even if rendering threw, so it
          // never leaks into the live DOM.
          if (wrapper.parentNode) wrapper.parentNode.removeChild(wrapper);
        }
      };

      const pdfHtml = buildPdfHtml(logos);

      // Order per spec: IC/SSM → land status → farm layout → EIA (after the form).
      const att = form11ATT || {};
      const orderedAttachments = [
        ...(att.ic_ssm || []).map(a => ({ ...a, label: 'Lampiran 1 — Kad Pengenalan / Sijil SSM' })),
        ...(att.land_status || []).map(a => ({ ...a, label: 'Lampiran 1 — Bukti Status Tanah' })),
        ...(att.farm_layout || []).map(a => ({ ...a, label: 'Lampiran 1 — Pelan Blok Tanaman / Susun Atur Ladang' })),
        ...(att.eia || []).map(a => ({ ...a, label: 'Lampiran 1 — Surat Kelulusan EIA' })),
      ];
      // Residue section (WI-02): a heading page, then the uploaded files of the
      // selected cycle in slot order 1→6 (only slots that have files). Each
      // private file is fetched through a signed URL created just before use.
      // The files are merged FIRST and the heading's contents list is built from
      // the ones that made it in, so the PDF never lists a file it doesn't hold
      // (every skipped file is reported in `problems`).
      // Returns { total: files on record, included: files merged, bytes: PDF or null }.
      const buildResiduePdf = async () => {
        const docs = await listResidueDocs(farm.id, residueSeasonId(selectedCycleId));
        if (!docs.length) return { total: 0, included: 0, bytes: null };
        const fileName = (d) => d.file_name || String(d.path || '').split('/').pop();
        const fileAtts = docs.map(d => {
          const def = residueSlotDef(d.slot);
          return {
            getUrl: () => residueSignedUrl(d.path),
            name: fileName(d),
            mime: d.mime || '',
            label: `${d.slot}. ${def ? def.pdfLabel : `Slot ${d.slot}`}`,
            slot: d.slot,
          };
        });
        const included = [];
        // No default page: pdf-lib adds a blank A4 to a page-less document on save.
        const emptyPdf = await (await PDFDocument.create()).save({ addDefaultPage: false });
        const filesBytes = await mergeAttachmentsIntoPdf(emptyPdf, fileAtts, problems, included);
        if (!included.length) return { total: docs.length, included: 0, bytes: null };
        const headingBytes = await buildResidueHeadingPdf({
          farmName: farm?.name || '',
          cycleLabel: residueCycleLabel,
          dateStr,
          entries: included.map(a => ({ slot: a.slot, fileName: a.name })),
          logos,
        });
        const merged = await PDFDocument.load(headingBytes);
        const src = await PDFDocument.load(filesBytes);
        (await merged.copyPages(src, src.getPageIndices())).forEach(p => merged.addPage(p));
        return { total: docs.length, included: included.length, bytes: await merged.save() };
      };
      const noResidueAccess = farmAccessRef.current === 'none';

      if (exportMode === 'form11') {
        await generatePdf(pdfHtml, 'portrait', 680, `myGAP Form 1-1 - ${farm?.name || 'Farm'} - ${dateStr}.pdf`, true, true, orderedAttachments);
      } else if (exportMode === 'farm-record') {
        await generatePdf(pdfHtml, 'landscape', 1050, `myGAP Farm Record - ${farm?.name || 'Farm'} - ${dateStr}.pdf`);
      } else if (exportMode === 'residue') {
        if (noResidueAccess) {
          alert('Akaun anda tiada akses kepada laporan residu ladang ini.');
          return;
        }
        let res;
        try {
          res = await buildResiduePdf();
        } catch (rErr) {
          console.error('Residue list error:', rErr);
          alert(`Senarai laporan residu tidak dapat dibaca: ${rErr?.message || rErr}`);
          return;
        }
        if (!res.total) {
          alert(`Tiada laporan residu dimuat naik untuk kitaran ini (${residueCycleLabel}).`);
          return;
        }
        if (!res.bytes) {
          alert(`Tiada fail residu dapat disertakan, PDF tidak dijana:\n\n${formatMergeProblems(problems)}`);
          return;
        }
        downloadPdfBytes(res.bytes, `myGAP Laporan Analisis Residu - ${farm?.name || 'Farm'} - ${dateStr}.pdf`);
      } else {
        // 'all' includes the Form 1-1 section + its Lampiran 1 checklist, so the
        // attachments must be appended here too (no header/footer so the 4-x pages
        // aren't mislabelled "myGAP 1-1"). The residue section (heading page +
        // files, already merged into one PDF) follows the Lampiran 1 files; it is
        // left out when nothing was uploaded.
        const residueAtts = [];
        if (noResidueAccess) {
          problems.push({ kind: 'skip', label: RESIDUE_HEADING, name: '', reason: 'akaun anda tiada akses kepada laporan residu ladang ini' });
        } else {
          try {
            const res = await buildResiduePdf();
            if (res.bytes) residueAtts.push({ bytes: res.bytes, name: '', mime: 'application/pdf', label: RESIDUE_HEADING });
          } catch (rErr) {
            console.warn('Residue list error:', rErr);
            problems.push({ kind: 'skip', label: RESIDUE_HEADING, name: '', reason: `senarai fail tidak dapat dibaca (${rErr?.message || rErr})` });
          }
        }
        await generatePdf(pdfHtml, 'portrait', 1200, `myGAP Complete Report - ${farm?.name || 'Farm'} - ${dateStr}.pdf`, false, false, [...orderedAttachments, ...residueAtts]);
      }
      if (problems.length) {
        alert(`PDF telah dijana, tetapi perhatikan:\n\n${formatMergeProblems(problems)}`);
      }
    } catch (err) {
      console.error('PDF export error:', err);
      alert('Error exporting PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // Save status of the Farm Record notes ("Disimpan" / "Menyimpan…" / "Gagal simpan"),
  // plus notices for edits the server refused or that a newer value replaced.
  const notesStatusPill = (() => {
    const pill = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600, boxShadow: '0 1px 3px rgba(0,0,0,0.12)', whiteSpace: 'nowrap' };
    const smallBtn = (color, border) => ({ padding: '0 6px', fontSize: 10, background: '#fff', color, border: `1px solid ${border}`, borderRadius: 4, cursor: 'pointer' });
    let main = null;
    if (notesSaveStatus === 'saving') {
      main = <span style={{ ...pill, background: '#f3f4f6', color: '#4b5563', border: '1px solid #e5e7eb' }}>Menyimpan…</span>;
    } else if (notesSaveStatus === 'error') {
      main = (
        <span style={{ ...pill, background: '#fee2e2', color: '#b91c1c', border: '1px solid #fecaca' }}
          title="Perubahan masih ada dalam pelayar ini dan akan dihantar semula secara automatik.">
          ⚠ Gagal simpan
          <button type="button" onClick={() => flushNotes(notesFarmIdRef.current)} style={smallBtn('#b91c1c', '#fecaca')}>
            Cuba lagi
          </button>
        </span>
      );
    } else if (farmAccess === 'none') {
      main = (
        <span style={{ ...pill, background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' }}
          title="Akaun anda tiada akses ke rekod ladang ini di pelayan. Catatan yang ditaip disimpan dalam pelayar ini sahaja dan tidak dikongsi.">
          ⚠ Dalam pelayar ini sahaja (tiada akses)
        </span>
      );
    } else if (notesSource === 'local') {
      main = (
        <span style={{ ...pill, background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' }}
          title="Catatan tidak dapat dibaca dari pelayan; yang dipaparkan ialah salinan dalam pelayar ini.">
          ⚠ Dalam pelayar ini sahaja
        </span>
      );
    } else if (farmAccess === 'read' && notesSource === 'db') {
      main = <span style={{ ...pill, background: '#f3f4f6', color: '#4b5563', border: '1px solid #e5e7eb' }}
        title="Akaun anda boleh melihat tetapi tidak boleh mengubah rekod ladang ini.">Baca sahaja</span>;
    } else if (notesSaveStatus === 'saved' || notesSource === 'db') {
      main = <span style={{ ...pill, background: '#dcfce7', color: '#15803d', border: '1px solid #bbf7d0' }}>✓ Disimpan</span>;
    }
    const notice = { ...pill, whiteSpace: 'normal', maxWidth: 320, borderRadius: 8, fontWeight: 500 };
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
        {main}
        {notesRejected.length > 0 && (
          <span style={{ ...notice, background: '#fee2e2', color: '#b91c1c', border: '1px solid #fecaca' }}
            title={notesRejected.map(r => `${r.key}: ${r.reason}`).join('\n')}>
            ⚠ {notesRejected.length} perubahan ditolak oleh pelayan dan tidak disimpan
            <button type="button" onClick={() => setNotesRejected([])} style={smallBtn('#b91c1c', '#fecaca')}>Tutup</button>
          </span>
        )}
        {notesConflicts > 0 && (
          <span style={{ ...notice, background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' }}>
            {notesConflicts} perubahan lama dalam pelayar ini tidak dihantar: nilai yang lebih baharu telah disimpan dari peranti lain.
            <button type="button" onClick={() => setNotesConflicts(0)} style={smallBtn('#92400e', '#fde68a')}>Tutup</button>
          </span>
        )}
      </div>
    );
  })();

  // The Farm Record PDF prints the saved notes: don't export defaults before
  // they have arrived (a fresh device right after opening the page).
  const notesLoadingForExport = (exportMode === 'farm-record' || exportMode === 'all') && notesSource === 'pending';

  // "Salin ke semua ↓" under row 1 of 4-5 ALAT / JUSTIFIKASI (WI-18).
  const copy45Button = (col) => {
    const disabled = farmAccess === 'read' || !first45Value(col).trim();
    return (
      <button type="button" onClick={() => copy45ToAll(col)} disabled={disabled}
        title={farmAccess === 'read' ? 'Baca sahaja' : (disabled ? 'Isi baris pertama dahulu' : 'Salin nilai baris pertama ke semua baris 4-5')}
        style={{ marginTop: 4, padding: '1px 6px', fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap',
          background: disabled ? '#f3f4f6' : '#ecfdf5', color: disabled ? '#9ca3af' : '#047857',
          border: `1px solid ${disabled ? '#e5e7eb' : '#a7f3d0'}`, borderRadius: 4, cursor: disabled ? 'not-allowed' : 'pointer' }}>
        Salin ke semua ↓
      </button>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mb-2"></div>
          <p className="text-sm text-gray-500">Loading myGAP report data...</p>
        </div>
      </div>
    );
  }

  return (
    <NotesReadOnlyCtx.Provider value={farmAccess === 'read'}>
    <div>
      {/* Header with cycle selector and export */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <MyGAPLogo size={48} />
          <div>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#15803d' }}>myGAP Report</h3>
            <p style={{ margin: 0, fontSize: '12px', color: '#6b7280' }}>{farm?.name || 'Select a farm'}</p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <select
            value={selectedCycleId || ''}
            onChange={(e) => onCycleChange && onCycleChange(e.target.value ? parseInt(e.target.value) : null)}
            style={{
              padding: '6px 10px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              fontSize: '12px',
              color: '#374151',
              background: 'white',
              cursor: 'pointer',
              minWidth: '200px'
            }}
          >
            <option value="">All Cycles</option>
            {cycles?.map(c => (
              <option key={c.id} value={c.id}>
                {c.produceName} (start: {formatDate(c.startDate)})
              </option>
            ))}
          </select>
          <select
            value={exportMode}
            onChange={(e) => setExportMode(e.target.value)}
            style={{
              padding: '6px 10px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              fontSize: '12px',
              color: '#374151',
              background: 'white',
              cursor: 'pointer',
              minWidth: '160px'
            }}
          >
            <option value="farm-record">📋 Farm Record (4-1 to 4-8)</option>
            <option value="residue">🧪 {RESIDUE_HEADING}</option>
            {hasForm11 && <option value="form11">📝 Form 1-1 (Application)</option>}
            {hasForm11 && <option value="all">📑 All Forms</option>}
          </select>
          <button
            onClick={handleExportPDF}
            disabled={exporting || notesLoadingForExport}
            title={notesLoadingForExport ? 'Menunggu catatan yang disimpan dimuatkan…' : undefined}
            style={{
              padding: '8px 16px',
              backgroundColor: (exporting || notesLoadingForExport) ? '#9ca3af' : '#16a34a',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: 600,
              cursor: exporting || notesLoadingForExport ? 'wait' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              whiteSpace: 'nowrap',
            }}
          >
            {exporting ? (
              <>
                <span className="inline-block animate-spin" style={{ width: '14px', height: '14px', border: '2px solid white', borderTopColor: 'transparent', borderRadius: '50%' }}></span>
                Exporting...
              </>
            ) : notesLoadingForExport ? (
              <>Memuatkan catatan…</>
            ) : (
              <>📄 Export PDF</>
            )}
          </button>
        </div>
      </div>

      {/* Report content */}
      <div ref={reportRef} style={{ background: 'white' }}>

        {/* myGAP 1-1: Application Form (Borang Permohonan) — now editable */}
        {(exportMode === 'form11' || exportMode === 'all') && (
        <div style={sectionCardStyle}>
          <SectionHeader number="1-1" titleMalay="BORANG PERMOHONAN / APPLICATION FORM" />
          <Form11Editor
            farm={farm}
            selectedCycle={selectedCycle}
            cycles={cycles}
            employees={employees}
            onCycleChange={onCycleChange}
            form11A={form11A}
            form11B={form11B}
            form11C={form11C}
            form11D={form11D}
            form11E={form11E}
            form11ATT={form11ATT}
            setF11={setF11}
            saveStatus={form11SaveStatus}
            onRefresh={refreshForm11Data}
            onSuppUploadingChange={(b) => { suppUploadingRef.current = b; }}
            tableStyle={tableStyle}
            thStyle={thStyle}
            tdStyle={tdStyle}
          />
        </div>
        )}

        {/* Laporan Analisis Residu Racun Perosak Hasil Pertanian (WI-01/02) —
            between Form 1-1 and the Farm Record, as agreed on 23 Sep 2026. */}
        {(exportMode === 'residue' || exportMode === 'all') && (
        <div style={sectionCardStyle}>
          <MyGAPResidueSection farm={farm} seasonId={selectedCycleId} cycleLabel={residueCycleLabel} access={farmAccess} />
        </div>
        )}

        {/* myGAP 4-1 to 4-8: Farm Record sections */}
        {(exportMode === 'farm-record' || exportMode === 'all') && (<>
        {/* Save status of the typed Farm Record cells (WI-22a); stays in view while scrolling. */}
        <div style={{ position: 'sticky', top: 8, zIndex: 30, height: 0 }}>
          <div style={{ position: 'absolute', right: 8, top: 0 }}>{notesStatusPill}</div>
        </div>
        {/* myGAP 4-1: Soil/Substrate Maintenance */}
        <div style={sectionCardStyle}>
          <SectionHeader number="4-1" titleMalay="PEMULIHARAAN DAN RAWATAN TANAH / SUBSTRAT" />
          <div style={{ padding: '12px', overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Tarikh</th>
                  <th style={thStyle}>Masalah Tanah / Substrat</th>
                  <th style={thStyle}>Kaedah Pemuliharaan / Rawatan</th>
                  <th style={thStyle}>Nama Bahan/Peralatan</th>
                  <th style={thStyle}>Kuantiti Bahan / Peralatan</th>
                  <th style={thStyle}>Tempoh Dilarang Menanam</th>
                  <th style={thStyle}>Blok/Lokasi</th>
                  <th style={thStyle}>Nama Operator</th>
                </tr>
              </thead>
              <tbody>
                {soilActivities.length > 0 ? soilActivities.map((act, idx) => (
                  <tr key={act.id ?? idx}>
                    <td style={tdStyle}>{formatDateShort(act.date_activity || act.actual_date || act.planned_date)}</td>
                    <td style={tdStyle}>
                      <EditableCell value={sopDescription(act)} placeholder="Masukkan masalah..." farmId={farm.id} seasonId={selectedCycleId} section="4-1" field={soilNoteKey(act, 'problem')} notes={notes} onNoteChange={setNoteValue} />
                    </td>
                    <td style={tdStyle}>{processLabel(act) || '-'}</td>
                    <td style={tdStyle}>{act.name_material || act.material_name || '-'}</td>
                    <td style={tdStyle}>{sopDosageText(act) || act.quantity || '-'}</td>
                    <td style={tdStyle}>
                      <EditableCell placeholder="Masukkan tempoh..." farmId={farm.id} seasonId={selectedCycleId} section="4-1" field={soilNoteKey(act, 'period')} notes={notes} onNoteChange={setNoteValue} />
                    </td>
                    <td style={tdStyle}>{farmLocation}</td>
                    <td style={tdStyle}>{operatorNames || '-'}</td>
                  </tr>
                )) : (
                  <tr>
                    <td style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af', padding: '20px' }} colSpan={8}>
                      Tiada rekod pemuliharaan tanah
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* myGAP 4-2: Plant Material Purchase & Usage */}
        <div style={sectionCardStyle}>
          <SectionHeader number="4-2" titleMalay="PEMBELIAN, PENGGUNAAN DAN RAWATAN BAHAN TANAMAN" />
          <div style={{ padding: '12px', overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Tarikh</th>
                  <th style={thStyle}>Jenis Varieti / Pokok Induk / Bahan Tanaman</th>
                  <th style={thStyle} colSpan={2}>Pembelian</th>
                  <th style={thStyle} colSpan={2}>Penggunaan</th>
                  <th style={thStyle} colSpan={3}>Rawatan Bahan Tanaman</th>
                  <th style={thStyle}>Nama Pembekal Benih / Operator</th>
                </tr>
                <tr>
                  <th style={{ ...thStyle, borderTop: 'none' }}></th>
                  <th style={{ ...thStyle, borderTop: 'none' }}></th>
                  <th style={{ ...thStyle, borderTop: 'none', fontSize: '9px' }}>Kuantiti</th>
                  <th style={{ ...thStyle, borderTop: 'none', fontSize: '9px' }}>Harga</th>
                  <th style={{ ...thStyle, borderTop: 'none', fontSize: '9px' }}>Kuantiti</th>
                  <th style={{ ...thStyle, borderTop: 'none', fontSize: '9px' }}>Blok/Luas</th>
                  <th style={{ ...thStyle, borderTop: 'none', fontSize: '9px' }}>Justifikasi</th>
                  <th style={{ ...thStyle, borderTop: 'none', fontSize: '9px' }}>Bahan</th>
                  <th style={{ ...thStyle, borderTop: 'none', fontSize: '9px' }}>Kadar</th>
                  <th style={{ ...thStyle, borderTop: 'none' }}></th>
                </tr>
              </thead>
              <tbody>
                {(seedItems.length > 0 || (selectedCycle && true)) ? (
                  <>
                    {selectedCycle && (
                      <tr>
                        {/* Usage date / quantity from the cycle's Completed
                            Transplanting (date may fall back to Seeding) SOP
                            activity; otherwise cycle start and 0 as before. */}
                        <td style={tdStyle}>{formatDateShort(plantUseDate)}</td>
                        <td style={tdStyle}>{selectedCycle.produceName || '-'}</td>
                        <td style={tdStyle}>-</td>
                        <td style={tdStyle}>-</td>
                        <td style={tdStyle}>{plantUseQty}</td>
                        <td style={tdStyle}>{farmLocation}</td>
                        <td style={tdStyle}>
                          <EditableCell value={plantJustDefault} placeholder="Masukkan justifikasi..." farmId={farm.id} seasonId={selectedCycleId} section="4-2" field="justifikasi_0" notes={notes} onNoteChange={setNoteValue} />
                        </td>
                        <td style={tdStyle}>-</td>
                        <td style={tdStyle}>-</td>
                        <td style={tdStyle}>{operatorNames || '-'}</td>
                      </tr>
                    )}
                    {seedItems.map((item, idx) => {
                      const fin = findFinTxnForInv(item);
                      const justDefault = fin?.description || (item.material_name ? `Pembelian ${item.material_name}` : '');
                      return (
                      <tr key={idx}>
                        <td style={tdStyle}>{formatDateShort(item.date_transact || item.created_at)}</td>
                        <td style={tdStyle}>{item.material_name || item.inventory_name || '-'}</td>
                        <td style={tdStyle}>{item.is_in ? `${item.amount || 0} ${item.unit_name || ''}` : '-'}</td>
                        <td style={tdStyle}>{item.is_in && item.price ? `RM ${parseFloat(item.price).toFixed(2)}` : '-'}</td>
                        <td style={tdStyle}>{!item.is_in ? `${item.amount || 0} ${item.unit_name || ''}` : '-'}</td>
                        <td style={tdStyle}>{farmLocation}</td>
                        <td style={tdStyle}>
                          <EditableCell value={justDefault} placeholder="Masukkan justifikasi..." farmId={farm.id} seasonId={selectedCycleId} section="4-2" field={`justifikasi_${idx + 1}`} notes={notes} onNoteChange={setNoteValue} />
                        </td>
                        <td style={tdStyle}>-</td>
                        <td style={tdStyle}>-</td>
                        <td style={tdStyle}>{supplierFor(item) || operatorNames || '-'}</td>
                      </tr>
                      );
                    })}
                  </>
                ) : (
                  <tr>
                    <td style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af', padding: '20px' }} colSpan={10}>
                      Tiada rekod bahan tanaman
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* myGAP 4-3: Fertilizer Purchase & Usage */}
        <div style={sectionCardStyle}>
          <SectionHeader number="4-3" titleMalay="PEMBELIAN DAN PENGGUNAAN BAJA, SUBSTRAT* DAN BAHAN LAIN**" />
          <div style={{ padding: '12px', overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Tarikh Beli / Guna</th>
                  <th style={thStyle}>Jenis Bahan</th>
                  <th style={thStyle}>Formulasi</th>
                  <th style={thStyle} colSpan={3}>Pembelian</th>
                  <th style={thStyle} colSpan={5}>Penggunaan</th>
                </tr>
                <tr>
                  <th style={{ ...thStyle, borderTop: 'none' }}></th>
                  <th style={{ ...thStyle, borderTop: 'none' }}></th>
                  <th style={{ ...thStyle, borderTop: 'none' }}></th>
                  <th style={{ ...thStyle, borderTop: 'none', fontSize: '9px' }}>Kuantiti</th>
                  <th style={{ ...thStyle, borderTop: 'none', fontSize: '9px' }}>Harga</th>
                  <th style={{ ...thStyle, borderTop: 'none', fontSize: '9px' }}>Pembekal</th>
                  <th style={{ ...thStyle, borderTop: 'none', fontSize: '9px' }}>Kuantiti</th>
                  <th style={{ ...thStyle, borderTop: 'none', fontSize: '9px' }}>Cara Penggunaan</th>
                  <th style={{ ...thStyle, borderTop: 'none', fontSize: '9px' }}>Lokasi/ Blok</th>
                  <th style={{ ...thStyle, borderTop: 'none', fontSize: '9px' }}>Keluasan</th>
                  <th style={{ ...thStyle, borderTop: 'none', fontSize: '9px' }}>Operator</th>
                </tr>
              </thead>
              <tbody>
                {(fertilizerItems.length > 0 || sopFertRows43.length > 0) ? (<>
                {fertilizerItems.map((item, idx) => {
                  const matName = item.material_name || item.inventory_name || '';
                  // Auto-detect NPK / urea / compost formulation from the material name.
                  // Matches things like "NPK 15-15-15", "Urea 46%", etc.
                  const npkMatch = matName.match(/(\d{1,3}[-:\s]\d{1,3}[-:\s]\d{1,3})/);
                  const ureaMatch = matName.match(/urea\s*(\d{1,3})\s*%?/i);
                  const formulaDefault = npkMatch ? `NPK ${npkMatch[1]}` : ureaMatch ? `Urea ${ureaMatch[1]}%` : '';
                  // "Cara penggunaan" — the SOP activity this usage came from (else
                  // one using this material); pull its tool + description.
                  const relAct = relActForInv(item);
                  const caraDefault = relAct ? [toolFor(relAct), sopDescription(relAct)].filter(Boolean).join(' — ') : '';
                  return (
                  <tr key={idx}>
                    <td style={tdStyle}>{formatDateShort(item.date_transact || item.created_at)}</td>
                    <td style={tdStyle}>{matName || '-'}</td>
                    <td style={tdStyle}>
                      <EditableCell value={formulaDefault} placeholder="Masukkan formulasi..." farmId={farm.id} seasonId={selectedCycleId} section="4-3" field={`formulasi_${idx}`} notes={notes} onNoteChange={setNoteValue} />
                    </td>
                    <td style={tdStyle}>{item.is_in ? `${item.amount || 0} ${item.unit_symbol || item.unit_name || 'kg'}` : '-'}</td>
                    <td style={tdStyle}>{item.is_in && item.price ? `RM ${parseFloat(item.price).toLocaleString('en-MY', { minimumFractionDigits: 2 })}` : '-'}</td>
                    <td style={tdStyle}>{supplierFor(item) || '-'}</td>
                    <td style={tdStyle}>{`${item.amount || 0} ${item.unit_symbol || item.unit_name || 'kg'}`}</td>
                    <td style={tdStyle}>
                      <EditableCell value={caraDefault} placeholder="Masukkan cara penggunaan..." farmId={farm.id} seasonId={selectedCycleId} section="4-3" field={`cara_${idx}`} notes={notes} onNoteChange={setNoteValue} />
                    </td>
                    <td style={tdStyle}>{farmLocation}</td>
                    <td style={tdStyle}>{farmSizeText || '-'}</td>
                    <td style={tdStyle}>{operatorNames || '-'}</td>
                  </tr>
                  );
                })}
                {/* Completed SOP fertiliser / foliar / media / substrate
                    applications (oldest first), minus any already shown above
                    through an inventory usage record. Keys use the activity id
                    so typed notes can't shift rows. */}
                {sopFertRows43.map(act => (
                  <tr key={`sop43-${act.id}`}>
                    <td style={tdStyle}>{formatDateShort(act.date_activity)}</td>
                    <td style={tdStyle}>{act.name_material || '-'}</td>
                    <td style={tdStyle}>
                      <EditableCell value={sopFormulaDefault(act)} placeholder="Masukkan formulasi..." farmId={farm.id} seasonId={selectedCycleId} section="4-3" field={`sop_${act.id}_formulasi`} notes={notes} onNoteChange={setNoteValue} />
                    </td>
                    <td style={tdStyle}>-</td>
                    <td style={tdStyle}>-</td>
                    <td style={tdStyle}>-</td>
                    <td style={tdStyle}>{sopDosageText(act) || '-'}</td>
                    <td style={tdStyle}>
                      <EditableCell value={sopCaraDefault(act)} placeholder="Masukkan cara penggunaan..." farmId={farm.id} seasonId={selectedCycleId} section="4-3" field={`sop_${act.id}_cara`} notes={notes} onNoteChange={setNoteValue} />
                    </td>
                    <td style={tdStyle}>{farmLocation}</td>
                    <td style={tdStyle}>{farmSizeText || '-'}</td>
                    <td style={tdStyle}>{operatorNames || '-'}</td>
                  </tr>
                ))}
                </>) : (
                  <tr>
                    <td style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af', padding: '20px' }} colSpan={11}>
                      Tiada rekod pembelian baja
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* myGAP 4-4: Pest Surveillance & Control */}
        <div style={sectionCardStyle}>
          <SectionHeader number="4-4" titleMalay="PENGAWASAN DAN KAWALAN PEROSAK" />
          <div style={{ padding: '12px', overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Tarikh</th>
                  <th style={thStyle}>Cara Pengawasan</th>
                  <th style={thStyle}>Lokasi / Blok</th>
                  <th style={thStyle}>Nama Perosak</th>
                  <th style={thStyle}>Bil. Perosak / % Kerosakan</th>
                  <th style={thStyle}>Kaedah Kawalan</th>
                  <th style={thStyle}>Nama Operator</th>
                </tr>
              </thead>
              <tbody>
                {(pestActivities.length > 0 || pesticideItems.length > 0) ? (
                  <>
                    {pestActivities.map((act, idx) => {
                      // The SOP description usually captures the inspection note
                      // ("Found leafhopper damage on Block A, ~10% leaves").
                      // Try to parse a pest name + damage% from it; otherwise just
                      // use the whole description as "cara pengawasan".
                      const desc = sopDescription(act);
                      // Pest name: explicit "Perosak: X", else the SOP target
                      // ("Serangga - X" → X, "Fungicide/Kulat" → "Kulat (penyakit)").
                      // Damage: only an explicit "Kerosakan: N%" — never the active
                      // ingredient's strength ("Imidacloprid 18.3%").
                      const pestDefault = pestNameDefault(desc);
                      const damageDef = damageDefault(desc);
                      return (
                      <tr key={`pa-${idx}`}>
                        <td style={tdStyle}>{formatDateShort(act.date_activity || act.actual_date || act.planned_date)}</td>
                        <td style={tdStyle}>
                          <EditableCell value={desc} placeholder="Masukkan cara pengawasan..." farmId={farm.id} seasonId={selectedCycleId} section="4-4" field={`cara_${idx}`} notes={notes} onNoteChange={setNoteValue} />
                        </td>
                        <td style={tdStyle}>{farmLocation}</td>
                        <td style={tdStyle}>
                          <EditableCell value={pestDefault} placeholder="Masukkan nama perosak..." farmId={farm.id} seasonId={selectedCycleId} section="4-4" field={`perosak_${idx}`} notes={notes} onNoteChange={setNoteValue} />
                        </td>
                        <td style={tdStyle}>
                          <EditableCell value={damageDef} placeholder="Masukkan bil atau % kerosakan..." farmId={farm.id} seasonId={selectedCycleId} section="4-4" field={`kerosakan_${idx}`} notes={notes} onNoteChange={setNoteValue} />
                        </td>
                        <td style={tdStyle}>{[processLabel(act), act.name_material].filter(Boolean).join(' — ') || '-'}</td>
                        <td style={tdStyle}>{operatorNames || '-'}</td>
                      </tr>
                      );
                    })}
                    {pesticideItems.map((item, idx) => {
                      const fin = findFinTxnForInv(item);
                      const caraDefault = fin?.description || (item.material_name ? `Sembur ${item.material_name}` : '');
                      return (
                      <tr key={`pi-${idx}`}>
                        <td style={tdStyle}>{formatDateShort(item.date_transact || item.created_at)}</td>
                        <td style={tdStyle}>
                          <EditableCell value={caraDefault} placeholder="Masukkan cara pengawasan..." farmId={farm.id} seasonId={selectedCycleId} section="4-4" field={`cara_inv_${idx}`} notes={notes} onNoteChange={setNoteValue} />
                        </td>
                        <td style={tdStyle}>{farmLocation}</td>
                        <td style={tdStyle}>
                          <EditableCell placeholder="Masukkan nama perosak..." farmId={farm.id} seasonId={selectedCycleId} section="4-4" field={`perosak_inv_${idx}`} notes={notes} onNoteChange={setNoteValue} />
                        </td>
                        <td style={tdStyle}>
                          <EditableCell placeholder="Masukkan bil atau % kerosakan..." farmId={farm.id} seasonId={selectedCycleId} section="4-4" field={`kerosakan_inv_${idx}`} notes={notes} onNoteChange={setNoteValue} />
                        </td>
                        <td style={tdStyle}>{item.material_name || '-'}</td>
                        <td style={tdStyle}>{operatorNames || '-'}</td>
                      </tr>
                      );
                    })}
                  </>
                ) : (
                  <tr>
                    <td style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af', padding: '20px' }} colSpan={7}>
                      Tiada rekod pengawasan perosak
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* myGAP 4-5: Pesticide Purchase & Usage */}
        <div style={sectionCardStyle}>
          <SectionHeader number="4-5" titleMalay="PEMBELIAN & PENGGUNAAN RACUN PEROSAK / BAHAN KIMIA" />
          <div style={{ padding: '12px', overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Tarikh</th>
                  <th style={thStyle}>Tanaman</th>
                  <th style={thStyle}>Lokasi & Keluasan</th>
                  <th style={thStyle}>Nama Dagangan & Bahan Aktif</th>
                  <th style={thStyle}>No. LRMP</th>
                  <th style={thStyle}>Harga</th>
                  <th style={thStyle}>Kuantiti / Kadar</th>
                  <th style={thStyle}>Justifikasi Penggunaan</th>
                  <th style={thStyle}>Alat Yang Digunakan</th>
                  <th style={thStyle}>Nama Operator</th>
                  <th style={thStyle}>PHI</th>
                  <th style={thStyle}>REI</th>
                </tr>
              </thead>
              <tbody>
                {pest45Rows.length > 0 ? pest45Rows.map((r, i) => {
                  // Inventory rows first, then completed SOP sprays (oldest first,
                  // minus any already shown through an inventory usage record).
                  // Defaults: LRMP / active ingredient from the material record,
                  // tool + justification from the SOP activity, REI "12 jam".
                  const cell = (col, placeholder) => (
                    <EditableCell value={r.defaults[col]} placeholder={placeholder} farmId={farm.id} seasonId={selectedCycleId} section="4-5" field={r.fields[col]} notes={notes} onNoteChange={setNoteValue} />
                  );
                  const showCopy = i === 0 && pest45Rows.length > 1;
                  const noteCells = (<>
                    <td style={tdStyle}>
                      {cell('justifikasi', 'Masukkan justifikasi penggunaan...')}
                      {showCopy && copy45Button('justifikasi')}
                    </td>
                    <td style={tdStyle}>
                      {cell('alat', 'Masukkan alat...')}
                      {showCopy && copy45Button('alat')}
                    </td>
                    <td style={tdStyle}>{operatorNames || '-'}</td>
                    <td style={tdStyle}>{cell('phi', 'PHI')}</td>
                    <td style={tdStyle}>{cell('rei', 'REI')}</td>
                  </>);
                  if (r.kind === 'inv') {
                    const { item, relAct, info } = r;
                    const matName = item.material_name || item.inventory_name || '';
                    return (
                    <tr key={r.key}>
                      <td style={tdStyle}>{formatDateShort(item.date_transact || item.created_at)}</td>
                      <td style={tdStyle}>{selectedCycle?.produceName || '-'}</td>
                      <td style={tdStyle}>{farmLocation}</td>
                      <td style={tdStyle}>{tradeAndActive(matName, info) || '-'}</td>
                      <td style={tdStyle}>{cell('lrmp', 'LRMP')}</td>
                      <td style={tdStyle}>{item.price ? `RM ${parseFloat(item.price).toLocaleString('en-MY', { minimumFractionDigits: 2 })}` : '-'}</td>
                      <td style={tdStyle}>{item.amount ? `${item.amount} ${item.unit_name || ''}` : (sopDosageText(relAct) || '-')}</td>
                      {noteCells}
                    </tr>
                    );
                  }
                  const { act, info } = r;
                  return (
                  <tr key={r.key}>
                    <td style={tdStyle}>{formatDateShort(act.date_activity)}</td>
                    <td style={tdStyle}>{selectedCycle?.produceName || act.produce_name || '-'}</td>
                    <td style={tdStyle}>{farmLocation}</td>
                    <td style={tdStyle}>{tradeAndActive(act.name_material, info) || '-'}</td>
                    <td style={tdStyle}>{cell('lrmp', 'LRMP')}</td>
                    <td style={tdStyle}>-</td>
                    <td style={tdStyle}>{sopDosageText(act) || '-'}</td>
                    {noteCells}
                  </tr>
                  );
                }) : (
                  <tr>
                    <td style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af', padding: '20px' }} colSpan={12}>
                      Tiada rekod racun perosak
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* myGAP 4-6: Production & Sales */}
        <div style={sectionCardStyle}>
          <SectionHeader number="4-6" titleMalay="PENGELUARAN DAN PENJUALAN HASIL" />
          <div style={{ padding: '12px', overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Tarikh</th>
                  <th style={thStyle}>Jenis Tanaman</th>
                  <th style={thStyle}>Blok / Lokasi</th>
                  <th style={thStyle}>Tuai (kg)</th>
                  <th style={thStyle}>Jual (kg)</th>
                  <th style={thStyle}>Harga Jualan/kg (RM)</th>
                  <th style={thStyle}>Jumlah Harga (RM)</th>
                  <th style={thStyle}>No. Invois</th>
                  <th style={thStyle}>Nama Pembeli</th>
                </tr>
              </thead>
              <tbody>
                {productionSales.length > 0 ? productionSales.map((y, idx) => {
                  const amount = parseFloat(y.yield_transaction_amount || 0);
                  const price = parseFloat(y.price_per_unit || 0);
                  const total = parseFloat(y.order_amount_total || 0) || (amount * price);
                  // NO. INVOIS / NAMA PEMBELI (WI-20): always editable; default =
                  // order_id / the resolved buyer; keyed by yield_transaction_id.
                  const k46 = row46Key(y, idx);
                  const d46 = row46Defaults(y, idx);
                  return (
                    <tr key={k46}>
                      <td style={tdStyle}>{formatDateShort(y.yield_transaction_date)}</td>
                      <td style={tdStyle}>{y.produce_name_malay || y.produce_name || selectedCycle?.produceName || '-'} {y.grade_name ? `Gred ${y.grade_name}` : ''}</td>
                      <td style={tdStyle}>{farmLocation}</td>
                      <td style={tdStyle}>{y.yield_in ? `${amount.toLocaleString()} ${y.unit_symbol || 'kg'}` : '-'}</td>
                      <td style={tdStyle}>{!y.yield_in ? `${amount.toLocaleString()} ${y.unit_symbol || 'kg'}` : '-'}</td>
                      <td style={tdStyle}>{price > 0 ? `RM ${price.toFixed(2)}` : '-'}</td>
                      <td style={tdStyle}>{total > 0 ? `RM ${total.toLocaleString('en-MY', { minimumFractionDigits: 2 })}` : '-'}</td>
                      <td style={tdStyle}>
                        <EditableCell value={d46.invois} placeholder="No. invois (jika ada)..." farmId={farm.id} seasonId={selectedCycleId} section="4-6" field={`invois_${k46}`} notes={notes} onNoteChange={setNoteValue} />
                      </td>
                      <td style={tdStyle}>
                        <EditableCell value={d46.pembeli} placeholder="Masukkan nama pembeli..." farmId={farm.id} seasonId={selectedCycleId} section="4-6" field={`pembeli_${k46}`} notes={notes} onNoteChange={setNoteValue} />
                      </td>
                    </tr>
                  );
                }) : (
                  <tr>
                    <td style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af', padding: '20px' }} colSpan={9}>
                      Tiada rekod pengeluaran dan penjualan
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* myGAP 4-7: Post-Harvest Treatment */}
        <div style={sectionCardStyle}>
          <SectionHeader number="4-7" titleMalay="RAWATAN PASCA TUAI" />
          <div style={{ padding: '12px', overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Tarikh</th>
                  <th style={thStyle}>Jenis Tanaman</th>
                  <th style={thStyle}>Blok / Lokasi</th>
                  <th style={thStyle}>Justifikasi Rawatan</th>
                  <th style={thStyle}>Jenis Rawatan</th>
                  <th style={thStyle}>Bahan Aktif dan Kadar</th>
                  <th style={thStyle}>Tempoh Rawatan</th>
                  <th style={thStyle}>Nama Operator</th>
                </tr>
              </thead>
              <tbody>
                {postHarvestActivities.length > 0 ? postHarvestActivities.map((act, idx) => (
                  <tr key={idx}>
                    <td style={tdStyle}>{formatDateShort(act.date_activity || act.actual_date || act.planned_date)}</td>
                    <td style={tdStyle}>{selectedCycle?.produceName || '-'}</td>
                    <td style={tdStyle}>{farmLocation}</td>
                    <td style={tdStyle}>
                      <EditableCell value={sopDescription(act)} placeholder="Masukkan justifikasi..." farmId={farm.id} seasonId={selectedCycleId} section="4-7" field={`justifikasi_${idx}`} notes={notes} onNoteChange={setNoteValue} />
                    </td>
                    <td style={tdStyle}>{processLabel(act) || '-'}</td>
                    <td style={tdStyle}>
                      <EditableCell value={sopBahanKadar(act)} placeholder="Masukkan bahan aktif..." farmId={farm.id} seasonId={selectedCycleId} section="4-7" field={`bahan_${idx}`} notes={notes} onNoteChange={setNoteValue} />
                    </td>
                    <td style={tdStyle}>
                      <EditableCell placeholder="Masukkan tempoh..." farmId={farm.id} seasonId={selectedCycleId} section="4-7" field={`tempoh_${idx}`} notes={notes} onNoteChange={setNoteValue} />
                    </td>
                    <td style={tdStyle}>{operatorNames || '-'}</td>
                  </tr>
                )) : (
                  <tr>
                    <td style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af', padding: '20px' }} colSpan={8}>
                      Tiada rekod rawatan pasca tuai
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* myGAP 4-8: Worker Management */}
        <div style={sectionCardStyle}>
          <SectionHeader number="4-8" titleMalay="PENGURUSAN PEKERJA" />
          <div style={{ padding: '12px', overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Nama Pekerja</th>
                  <th style={thStyle}>Tarikh Lahir</th>
                  <th style={thStyle}>Kad Pengenalan / Passport</th>
                  <th style={thStyle}>Jantina</th>
                  <th style={thStyle}>Kewarganegaraan</th>
                  <th style={thStyle} colSpan={3}>Rekod Latihan</th>
                  <th style={thStyle} colSpan={3}>Rekod Kesihatan</th>
                </tr>
                <tr>
                  <th style={{ ...thStyle, borderTop: 'none' }}></th>
                  <th style={{ ...thStyle, borderTop: 'none' }}></th>
                  <th style={{ ...thStyle, borderTop: 'none' }}></th>
                  <th style={{ ...thStyle, borderTop: 'none' }}></th>
                  <th style={{ ...thStyle, borderTop: 'none' }}></th>
                  <th style={{ ...thStyle, borderTop: 'none', fontSize: '9px' }}>Tarikh</th>
                  <th style={{ ...thStyle, borderTop: 'none', fontSize: '9px' }}>Latihan</th>
                  <th style={{ ...thStyle, borderTop: 'none', fontSize: '9px' }}>Penganjur</th>
                  <th style={{ ...thStyle, borderTop: 'none', fontSize: '9px' }}>Tarikh</th>
                  <th style={{ ...thStyle, borderTop: 'none', fontSize: '9px' }}>Klinik / Hospital</th>
                  <th style={{ ...thStyle, borderTop: 'none', fontSize: '9px' }}>Nota</th>
                </tr>
              </thead>
              <tbody>
                {employees.length > 0 ? employees.map((emp, idx) => {
                  const empTraining = trainingData.filter(t => t.user_id === emp.user_id);
                  return (
                  <tr key={idx}>
                    <td style={tdStyle}>{emp.full_name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || '-'}</td>
                    <td style={tdStyle}>{emp.birthdate ? formatDateShort(emp.birthdate) : '-'}</td>
                    <td style={tdStyle}>{emp.identification_no || emp.ic_number || '-'}</td>
                    <td style={tdStyle}>
                      <EditableCell value={emp.gender || ''} placeholder="Masukkan jantina..." farmId={farm.id} seasonId={selectedCycleId} section="4-8" field={`jantina_${idx}`} notes={notes} onNoteChange={setNoteValue} />
                    </td>
                    <td style={tdStyle}>{emp.nationality || emp.country || 'Malaysia'}</td>
                    {/* Training Record */}
                    <td style={tdStyle}>
                      {empTraining.length > 0
                        ? empTraining.map(t => formatDateShort(t.date_training)).join(', ')
                        : <EditableCell placeholder="Tarikh" farmId={farm.id} seasonId={selectedCycleId} section="4-8" field={`latihan_tarikh_${idx}`} notes={notes} onNoteChange={setNoteValue} />
                      }
                    </td>
                    <td style={tdStyle}>
                      {empTraining.length > 0
                        ? empTraining.map(t => t.description).join(', ')
                        : <EditableCell placeholder="Latihan" farmId={farm.id} seasonId={selectedCycleId} section="4-8" field={`latihan_nama_${idx}`} notes={notes} onNoteChange={setNoteValue} />
                      }
                    </td>
                    <td style={tdStyle}>
                      {empTraining.length > 0
                        ? empTraining.map(t => t.organiser).filter(Boolean).join(', ')
                        : <EditableCell placeholder="Penganjur" farmId={farm.id} seasonId={selectedCycleId} section="4-8" field={`latihan_penganjur_${idx}`} notes={notes} onNoteChange={setNoteValue} />
                      }
                    </td>
                    {/* Health Record */}
                    <td style={tdStyle}>
                      <EditableCell placeholder="Tarikh" farmId={farm.id} seasonId={selectedCycleId} section="4-8" field={`kesihatan_tarikh_${idx}`} notes={notes} onNoteChange={setNoteValue} />
                    </td>
                    <td style={tdStyle}>
                      <EditableCell placeholder="Klinik / Hospital" farmId={farm.id} seasonId={selectedCycleId} section="4-8" field={`kesihatan_klinik_${idx}`} notes={notes} onNoteChange={setNoteValue} />
                    </td>
                    <td style={tdStyle}>
                      <EditableCell placeholder="Nota" farmId={farm.id} seasonId={selectedCycleId} section="4-8" field={`kesihatan_nota_${idx}`} notes={notes} onNoteChange={setNoteValue} />
                    </td>
                  </tr>);
                }) : (
                  <tr>
                    <td style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af', padding: '20px' }} colSpan={11}>
                      Tiada rekod pekerja
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        </>)}

      </div>
    </div>
    </NotesReadOnlyCtx.Provider>
  );
};

export default MyGAPReport;
